import {useRegionMoney,useBusinessRegion} from '../../utils/businessRegionContext';
import React, { useEffect, useMemo, useState } from "react";
import ForecastOverview from "./ForecastOverview";
import { getFunctions, httpsCallable } from "firebase/functions";
import { doc, getDoc, setDoc } from "firebase/firestore";

const RANGE_QUERY_MODE = "scheduledAt_bounded_shadow";
const DETAIL_QUERY_MODE = "scheduledAt_bounded_detail_v1";
const UI_ROWS_VERSION = "finance_ui_rows_v1";
const PROFITABILITY_VERSION = "finance_profitability_v1";
const PROFITABILITY_SEMANTICS_VERSION = "reports-v3-a4.4-profitability-v1";
const PROFITABILITY_QUERY_MODE = "scheduledAt_bounded_profitability_v1";
const AGING_VERSION = "finance_aging_v1";
const AGING_SEMANTICS_VERSION = "reports-v3-a4.5-aging-v1";
const AGING_QUERY_MODE = "scheduledAt_bounded_aging_v1";
const AGING_HISTORY_DAYS = 730;
const FORECAST_VERSION = "finance_forecast_v1";
const FORECAST_SEMANTICS_VERSION = "reports-v3-a4.6-forecast-v1.1";
const FORECAST_QUERY_MODE = "scheduledAt_bounded_forecast_v1";
const FORECAST_CERT_TARGET = 1000;
const CLOSEOUT_VERSION = "finance_closeout_v1";
const CLOSEOUT_SEMANTICS_VERSION = "reports-v3-a4.7-closeout-v1";
const CLOSEOUT_QUERY_MODE = "scheduledAt_bounded_closeout_v1";

const RANGE_CACHE = new Map();
const RANGE_INFLIGHT = new Map();
const DETAIL_CACHE = new Map();
const DETAIL_INFLIGHT = new Map();
const PROFITABILITY_CACHE = new Map();
const PROFITABILITY_INFLIGHT = new Map();
const AGING_CACHE = new Map();
const AGING_INFLIGHT = new Map();
const FORECAST_CACHE = new Map();
const FORECAST_INFLIGHT = new Map();
const CLOSEOUT_CACHE = new Map();
const CLOSEOUT_INFLIGHT = new Map();

const SUMMARY_SCALARS = [
  "revenue",
  "ticketAvg",
  "collectedRevenue",
  "pendingRevenue",
  "paymentMismatchRevenue",
  "trackedRevenueBase",
  "trackedRevenueBookingsCount",
  "untrackedExpectedRevenue",
  "coveredRevenueTotal",
  "cashReceivedTotal",
  "refundedRevenue",
  "totalDiscount",
  "totalTips",
  "workerPayoutTotal",
  "workerPayoutPendingTotal",
  "workerPayoutPaidTotal",
  "materialCostTotal",
  "productSalesRevenueTotal",
  "productSalesCostTotal",
  "productSalesPayoutTotal",
  "generatedContribution",
  "operatingExpenses",
  "paidOperatingExpenses",
  "pendingOperatingExpenses",
  "operatingResult",
  "businessNetRevenue",
  "cashAfterExpenses",
  "trackedReconciliationGap",
  "financeReconciliationGap",
  "collectionRate",
];

function iso(value) {
  if (value instanceof Date && Number.isFinite(value.getTime())) return value.toISOString();
  if (value && typeof value.toDate === "function") {
    const d = value.toDate();
    return d instanceof Date && Number.isFinite(d.getTime()) ? d.toISOString() : "";
  }
  if (typeof value === "string" && value.trim()) {
    const d = new Date(value);
    return Number.isFinite(d.getTime()) ? d.toISOString() : "";
  }
  return "";
}

function numeric(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function moneyEqual(a, b) {
  return Math.abs(numeric(a) - numeric(b)) <= 0.005;
}

function trimCache(cache, max = 30) {
  while (cache.size > max) {
    const first = cache.keys().next().value;
    cache.delete(first);
  }
}

async function cachedCall(cache, inflight, key, execute) {
  if (cache.has(key)) return cache.get(key);
  if (inflight.has(key)) return inflight.get(key);

  const promise = Promise.resolve()
    .then(execute)
    .then((value) => {
      cache.set(key, value);
      trimCache(cache);
      return value;
    })
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, promise);
  return promise;
}

function resolveFunctions(db) {
  const app = db?.app || db?._app || null;
  return app ? getFunctions(app, "us-central1") : getFunctions();
}

function revenueMap(rows, nameKey) {
  return new Map(
    (Array.isArray(rows) ? rows : []).map((row) => [
      String(row?.[nameKey] || ""),
      numeric(row?.revenue),
    ])
  );
}

function countMap(rows, nameKey) {
  return new Map(
    (Array.isArray(rows) ? rows : []).map((row) => [
      String(row?.[nameKey] || ""),
      numeric(row?.count),
    ])
  );
}

function reviveFinanceDate(value) {
  if (value instanceof Date && Number.isFinite(value.getTime())) return value;
  if (!value) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
}

function uiRowsAsDates(rows) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    ...(row || {}),
    date: reviveFinanceDate(row?.date),
  }));
}

function placeholderRows(count, prefix) {
  return Array.from({ length: Math.max(0, numeric(count)) }, (_, index) => ({
    id: `${prefix}-${index + 1}`,
  }));
}

function sumMapFromRows(rows, keyName, valueName) {
  const map = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const key = String(row?.[keyName] || "").trim();
    if (!key) return;
    map.set(key, numeric(map.get(key)) + numeric(row?.[valueName]));
  });
  return map;
}

export function overlayFinanceSummary(local = {}, remote = null, remoteUiRows = null) {
  if (!remote || typeof remote !== "object") return local;

  const next = { ...(local || {}) };
  SUMMARY_SCALARS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(remote, key)) next[key] = remote[key];
  });

  if (Array.isArray(remote.services)) {
    next.revenueByService = revenueMap(remote.services, "service");
    next.countByService = countMap(remote.services, "service");
  }

  if (Array.isArray(remote.workers)) {
    next.revenueByWorker = revenueMap(remote.workers, "worker");
    next.countByWorker = countMap(remote.workers, "worker");
  }

  const counts = remote.counts || {};
  const detailCounts = remote.detailCounts || {};
  next.revenueBookings = placeholderRows(counts.revenueBookings, "remote-revenue");
  next.unpaidBookings = placeholderRows(detailCounts.receivables, "remote-receivable");
  next.paymentMismatchBookings = placeholderRows(detailCounts.reconciliation, "remote-reconciliation");
  next.untrackedRevenueBookings = placeholderRows(detailCounts.untracked, "remote-untracked");
  next.coveredBookings = placeholderRows(detailCounts.covered, "remote-covered");

  if (Array.isArray(remote.cashMethodsDetailed)) {
    next.cashByMethod = new Map(
      remote.cashMethodsDetailed.map((row) => [String(row?.method || "unknown"), { ...(row || {}) }])
    );
  }

  if (Array.isArray(remote.coveredMethods)) {
    next.coveredByMethod = new Map(
      remote.coveredMethods.map((row) => [String(row?.label || "Cubierto"), numeric(row?.amount)])
    );
  }

  if (Array.isArray(remoteUiRows)) {
    const uiRows = uiRowsAsDates(remoteUiRows);
    next.financeRows = uiRows;
    next.materialCostByService = sumMapFromRows(uiRows, "service", "inventoryCost");
    next.materialCostByWorker = sumMapFromRows(uiRows, "worker", "inventoryCost");
    next.payoutByWorker = sumMapFromRows(uiRows, "worker", "payout");
    next.netByWorker = sumMapFromRows(uiRows, "worker", "net");

    const pendingPayout = new Map();
    const paidPayout = new Map();
    uiRows.forEach((row) => {
      const worker = String(row?.worker || "").trim();
      if (!worker) return;
      const target = String(row?.payoutStatus || "").toLowerCase() === "paid" ? paidPayout : pendingPayout;
      target.set(worker, numeric(target.get(worker)) + numeric(row?.payout));
    });
    next.payoutPendingByWorker = pendingPayout;
    next.payoutPaidByWorker = paidPayout;
  }

  next.loopRemoteSummary = true;
  next.loopRemoteUiRows = Array.isArray(remoteUiRows);
  next.loopRemoteCounts = counts;
  next.loopRemoteDetailCounts = detailCounts;
  next.loopRemoteRange = remote.range || null;
  return next;
}

function makeRangePayload({
  businessId,
  currentStart,
  currentEnd,
  comparisonEnabled,
  previousStart,
  previousEnd,
  workerId,
}) {
  const payload = {
    businessId: String(businessId || ""),
    from: iso(currentStart),
    to: iso(currentEnd),
    workerId: String(workerId || "all"),
    includeUiRows: true,
  };

  if (comparisonEnabled && iso(previousStart) && iso(previousEnd)) {
    payload.previousFrom = iso(previousStart);
    payload.previousTo = iso(previousEnd);
  }
  return payload;
}

function makeDetailPayload({
  businessId,
  currentStart,
  currentEnd,
  workerId,
  detailView,
  detailSearch,
  detailPage,
  detailPageSize,
}) {
  return {
    businessId: String(businessId || ""),
    from: iso(currentStart),
    to: iso(currentEnd),
    workerId: String(workerId || "all"),
    view: String(detailView || ""),
    search: String(detailSearch || "").trim().slice(0, 120),
    page: Math.max(1, Number(detailPage || 1)),
    pageSize: Math.max(1, Math.min(50, Number(detailPageSize || 20))),
  };
}

export function useFinanceRemoteCutover({
  db,
  businessId,
  enabled,
  currentStart,
  currentEnd,
  forecastEnd = currentEnd,
  comparisonEnabled,
  previousStart,
  previousEnd,
  workerId,
  detailView,
  detailSearch,
  detailPage,
  detailPageSize = 20,
  profitabilityEnabled = enabled,
  agingEnabled = false,
}) {
  const rangePayload = useMemo(
    () => makeRangePayload({
      businessId,
      currentStart,
      currentEnd,
      comparisonEnabled,
      previousStart,
      previousEnd,
      workerId,
    }),
    [businessId, currentStart, currentEnd, comparisonEnabled, previousStart, previousEnd, workerId]
  );

  const rangeKey = useMemo(
    () => JSON.stringify(rangePayload),
    [rangePayload]
  );

  const profitabilityPayload = useMemo(() => {
    const { includeUiRows: _omitUiRows, ...payload } = rangePayload;
    return payload;
  }, [rangePayload]);

  const profitabilityKey = useMemo(
    () => JSON.stringify(profitabilityPayload),
    [profitabilityPayload]
  );

  const agingPayload = useMemo(
    () => ({
      ...profitabilityPayload,
      bucket: "all",
      search: "",
      page: 1,
      pageSize: 20,
    }),
    [profitabilityPayload]
  );

  const agingKey = useMemo(
    () => JSON.stringify(agingPayload),
    [agingPayload]
  );

  const detailPayload = useMemo(
    () => makeDetailPayload({
      businessId,
      currentStart,
      currentEnd,
      workerId,
      detailView,
      detailSearch,
      detailPage,
      detailPageSize,
    }),
    [businessId, currentStart, currentEnd, workerId, detailView, detailSearch, detailPage, detailPageSize]
  );

  const detailKey = useMemo(
    () => JSON.stringify(detailPayload),
    [detailPayload]
  );

  const [rangeState, setRangeState] = useState({
    status: enabled ? "idle" : "disabled",
    key: "",
    data: null,
    error: "",
  });

  const [detailState, setDetailState] = useState({
    status: "disabled",
    key: "",
    data: null,
    error: "",
  });

  const [profitabilityState, setProfitabilityState] = useState({
    status: "disabled",
    key: "",
    data: null,
    error: "",
  });

  const [agingState, setAgingState] = useState({
    status: "disabled",
    key: "",
    data: null,
    error: "",
  });

  useEffect(() => {
    if (!enabled || !businessId || !rangePayload.from || !rangePayload.to) {
      setRangeState({ status: "disabled", key: "", data: null, error: "" });
      return undefined;
    }

    let cancelled = false;
    setRangeState((prev) => ({
      status: "loading",
      key: rangeKey,
      data: prev.key === rangeKey ? prev.data : null,
      error: "",
    }));

    const run = async () => {
      const functions = resolveFunctions(db);
      const callable = httpsCallable(functions, "getFinanceAnalyticsRangeV1");
      const response = await callable(rangePayload);
      const data = response?.data || null;
      if (
        !data?.ok ||
        data?.queryMode !== RANGE_QUERY_MODE ||
        data?.uiRowsVersion !== UI_ROWS_VERSION ||
        !Array.isArray(data?.currentUiRows)
      ) {
        throw new Error(`unexpected_range_contract:${data?.queryMode || "missing"}:${data?.uiRowsVersion || "missing"}`);
      }
      return data;
    };

    cachedCall(RANGE_CACHE, RANGE_INFLIGHT, rangeKey, run)
      .then((data) => {
        if (!cancelled) setRangeState({ status: "ready", key: rangeKey, data, error: "" });
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("[FinanceRemoteCutover] range failed", error);
          setRangeState({
            status: "error",
            key: rangeKey,
            data: null,
            error: `${error?.code || "error"}: ${error?.message || String(error)}`,
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [db, businessId, enabled, rangeKey, rangePayload]);

  useEffect(() => {
    if (!enabled || !businessId || !detailPayload.from || !detailPayload.to || !detailPayload.view) {
      setDetailState({ status: "disabled", key: "", data: null, error: "" });
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      setDetailState((prev) => ({
        status: "loading",
        key: detailKey,
        data: prev.key === detailKey ? prev.data : null,
        error: "",
      }));

      const run = async () => {
        const functions = resolveFunctions(db);
        const callable = httpsCallable(functions, "getFinanceAnalyticsDetailV1");
        const response = await callable(detailPayload);
        const data = response?.data || null;
        if (!data?.ok || data?.queryMode !== DETAIL_QUERY_MODE) {
          throw new Error(`unexpected_detail_contract:${data?.queryMode || "missing"}`);
        }
        return data;
      };

      cachedCall(DETAIL_CACHE, DETAIL_INFLIGHT, detailKey, run)
        .then((data) => {
          if (!cancelled) setDetailState({ status: "ready", key: detailKey, data, error: "" });
        })
        .catch((error) => {
          if (!cancelled) {
            console.error("[FinanceRemoteCutover] detail failed", error);
            setDetailState({
              status: "error",
              key: detailKey,
              data: null,
              error: `${error?.code || "error"}: ${error?.message || String(error)}`,
            });
          }
        });
    }, 180);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [db, businessId, enabled, detailKey, detailPayload]);

  const rangeReady = rangeState.status === "ready" && rangeState.key === rangeKey && Boolean(rangeState.data);
  const detailReady = detailState.status === "ready" && detailState.key === detailKey && Boolean(detailState.data);
  const profitabilityReady = profitabilityState.status === "ready"
    && profitabilityState.key === profitabilityKey
    && Boolean(profitabilityState.data);
  const agingReady = agingState.status === "ready"
    && agingState.key === agingKey
    && Boolean(agingState.data);

  useEffect(() => {
    if (
      !profitabilityEnabled ||
      !enabled ||
      !rangeReady ||
      !businessId ||
      !profitabilityPayload.from ||
      !profitabilityPayload.to
    ) {
      setProfitabilityState({ status: "disabled", key: "", data: null, error: "" });
      return undefined;
    }

    let cancelled = false;
    setProfitabilityState((prev) => ({
      status: "loading",
      key: profitabilityKey,
      data: prev.key === profitabilityKey ? prev.data : null,
      error: "",
    }));

    const run = async () => {
      const functions = resolveFunctions(db);
      const callable = httpsCallable(functions, "getFinanceProfitabilityV1");
      const response = await callable(profitabilityPayload);
      const data = response?.data || null;
      if (
        !data?.ok ||
        data?.queryMode !== PROFITABILITY_QUERY_MODE ||
        data?.profitabilityVersion !== PROFITABILITY_VERSION ||
        data?.profitabilitySemanticsVersion !== PROFITABILITY_SEMANTICS_VERSION
      ) {
        throw new Error(
          `unexpected_profitability_contract:${data?.queryMode || "missing"}:${data?.profitabilityVersion || "missing"}`
        );
      }
      return data;
    };

    cachedCall(PROFITABILITY_CACHE, PROFITABILITY_INFLIGHT, profitabilityKey, run)
      .then((data) => {
        if (!cancelled) {
          setProfitabilityState({ status: "ready", key: profitabilityKey, data, error: "" });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("[FinanceRemoteCutover] profitability failed", error);
          setProfitabilityState({
            status: "error",
            key: profitabilityKey,
            data: null,
            error: `${error?.code || "error"}: ${error?.message || String(error)}`,
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    db,
    businessId,
    enabled,
    profitabilityEnabled,
    rangeReady,
    profitabilityKey,
    profitabilityPayload,
  ]);

  useEffect(() => {
    if (
      !agingEnabled ||
      !enabled ||
      !rangeReady ||
      !businessId ||
      !agingPayload.from ||
      !agingPayload.to
    ) {
      setAgingState({ status: "disabled", key: "", data: null, error: "" });
      return undefined;
    }

    let cancelled = false;
    setAgingState((prev) => ({
      status: "loading",
      key: agingKey,
      data: prev.key === agingKey ? prev.data : null,
      error: "",
    }));

    const run = async () => {
      const functions = resolveFunctions(db);
      const callable = httpsCallable(functions, "getFinanceAgingV1");
      const response = await callable(agingPayload);
      const data = response?.data || null;
      if (
        !data?.ok ||
        data?.queryMode !== AGING_QUERY_MODE ||
        data?.agingVersion !== AGING_VERSION ||
        data?.agingSemanticsVersion !== AGING_SEMANTICS_VERSION
      ) {
        throw new Error(
          `unexpected_aging_contract:${data?.queryMode || "missing"}:${data?.agingVersion || "missing"}`
        );
      }
      return data;
    };

    cachedCall(AGING_CACHE, AGING_INFLIGHT, agingKey, run)
      .then((data) => {
        if (!cancelled) {
          setAgingState({ status: "ready", key: agingKey, data, error: "" });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("[FinanceRemoteCutover] aging failed", error);
          setAgingState({
            status: "error",
            key: agingKey,
            data: null,
            error: `${error?.code || "error"}: ${error?.message || String(error)}`,
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    db,
    businessId,
    enabled,
    agingEnabled,
    rangeReady,
    agingKey,
    agingPayload,
  ]);

  const runUiRowsProbe = async () => {
    if (!enabled || !businessId || !rangePayload.from || !rangePayload.to) {
      throw new Error("finance_ui_rows_probe_not_ready");
    }
    const functions = resolveFunctions(db);
    const callable = httpsCallable(functions, "getFinanceAnalyticsRangeV1");
    const response = await callable({ ...rangePayload, includeUiRows: true });
    const data = response?.data || null;
    if (!data?.ok || data?.queryMode !== RANGE_QUERY_MODE || data?.uiRowsVersion !== UI_ROWS_VERSION || !Array.isArray(data?.currentUiRows)) {
      throw new Error(`unexpected_ui_rows_contract:${data?.queryMode || "missing"}:${data?.uiRowsVersion || "missing"}`);
    }
    return data;
  };

  const runProfitabilityProbe = async () => {
    if (!enabled || !businessId || !profitabilityPayload.from || !profitabilityPayload.to) {
      throw new Error("finance_profitability_probe_not_ready");
    }
    const run = async () => {
      const functions = resolveFunctions(db);
      const callable = httpsCallable(functions, "getFinanceProfitabilityV1");
      const response = await callable(profitabilityPayload);
      const data = response?.data || null;
      if (
        !data?.ok ||
        data?.queryMode !== PROFITABILITY_QUERY_MODE ||
        data?.profitabilityVersion !== PROFITABILITY_VERSION ||
        data?.profitabilitySemanticsVersion !== PROFITABILITY_SEMANTICS_VERSION
      ) {
        throw new Error(
          `unexpected_profitability_contract:${data?.queryMode || "missing"}:${data?.profitabilityVersion || "missing"}`
        );
      }
      return data;
    };
    return cachedCall(PROFITABILITY_CACHE, PROFITABILITY_INFLIGHT, profitabilityKey, run);
  };

  const runAgingQuery = async ({
    bucket = "all",
    search = "",
    page = 1,
    pageSize = 20,
  } = {}) => {
    if (!enabled || !businessId || !agingPayload.from || !agingPayload.to) {
      throw new Error("finance_aging_query_not_ready");
    }

    const payload = {
      ...agingPayload,
      bucket: String(bucket || "all"),
      search: String(search || "").trim().slice(0, 120),
      page: Math.max(1, Number(page || 1)),
      pageSize: Math.max(1, Math.min(50, Number(pageSize || 20))),
    };
    const key = JSON.stringify(payload);

    const run = async () => {
      const functions = resolveFunctions(db);
      const callable = httpsCallable(functions, "getFinanceAgingV1");
      const response = await callable(payload);
      const data = response?.data || null;

      if (
        !data?.ok ||
        data?.queryMode !== AGING_QUERY_MODE ||
        data?.agingVersion !== AGING_VERSION ||
        data?.agingSemanticsVersion !== AGING_SEMANTICS_VERSION
      ) {
        throw new Error(
          `unexpected_aging_contract:${data?.queryMode || "missing"}:${data?.agingVersion || "missing"}`
        );
      }

      return data;
    };

    return cachedCall(AGING_CACHE, AGING_INFLIGHT, key, run);
  };

  const runAgingProbe = async () => runAgingQuery({
    bucket: "all",
    search: "",
    page: 1,
    pageSize: 20,
  });

  const runForecastQuery = async ({ targetAmount = 0 } = {}) => {
    if (!enabled || !businessId || !rangePayload.from || !rangePayload.to) {
      throw new Error("finance_forecast_query_not_ready");
    }

    const safeTarget = Math.max(0, Number(targetAmount || 0));
    const payload = {
      ...rangePayload,
      forecastTo: iso(forecastEnd),
      targetAmount: safeTarget,
    };
    const key = JSON.stringify(payload);

    const run = async () => {
      const functions = resolveFunctions(db);
      const callable = httpsCallable(functions, "getFinanceForecastV1");
      const response = await callable(payload);
      const data = response?.data || null;

      if (
        !data?.ok ||
        data?.queryMode !== FORECAST_QUERY_MODE ||
        data?.forecastVersion !== FORECAST_VERSION ||
        data?.forecastSemanticsVersion !== FORECAST_SEMANTICS_VERSION
      ) {
        throw new Error(
          `unexpected_forecast_contract:${data?.queryMode || "missing"}:${data?.forecastVersion || "missing"}`
        );
      }

      return data;
    };

    return cachedCall(FORECAST_CACHE, FORECAST_INFLIGHT, key, run);
  };

  const runForecastProbe = async () => runForecastQuery({
    targetAmount: FORECAST_CERT_TARGET,
  });

  const runCloseoutQuery = async ({ actualByMethod = null } = {}) => {
    if (!enabled || !businessId || !rangePayload.from || !rangePayload.to) {
      throw new Error("finance_closeout_query_not_ready");
    }

    const payload = {
      ...rangePayload,
      actualByMethod,
    };
    const key = JSON.stringify(payload);

    const run = async () => {
      const functions = resolveFunctions(db);
      const callable = httpsCallable(functions, "getFinanceCloseoutV1");
      const response = await callable(payload);
      const data = response?.data || null;

      if (
        !data?.ok ||
        data?.queryMode !== CLOSEOUT_QUERY_MODE ||
        data?.closeoutVersion !== CLOSEOUT_VERSION ||
        data?.closeoutSemanticsVersion !== CLOSEOUT_SEMANTICS_VERSION
      ) {
        throw new Error(
          `unexpected_closeout_contract:${data?.queryMode || "missing"}:${data?.closeoutVersion || "missing"}`
        );
      }

      return data;
    };

    return cachedCall(CLOSEOUT_CACHE, CLOSEOUT_INFLIGHT, key, run);
  };

  const runCloseoutProbe = async () => runCloseoutQuery({
    actualByMethod: null,
  });

  const api = {
    rangeStatus: rangeState.status,
    rangeError: rangeState.error,
    rangeReady,
    rangeResult: rangeReady ? rangeState.data : null,
    current: rangeReady ? rangeState.data?.current || null : null,
    previous: rangeReady ? rangeState.data?.previous || null : null,
    uiRowsVersion: rangeReady ? String(rangeState.data?.uiRowsVersion || "") : "",
    currentUiRows: rangeReady && Array.isArray(rangeState.data?.currentUiRows) ? rangeState.data.currentUiRows : [],
    rangeDiagnostics: rangeReady ? rangeState.data?.diagnostics || null : null,
    currentRangeFrom: String(rangePayload.from || ""),
    currentRangeTo: String(rangePayload.to || ""),

    detailStatus: detailState.status,
    detailError: detailState.error,
    detailReady,
    detailResult: detailReady ? detailState.data : null,
    detailRows: detailReady && Array.isArray(detailState.data?.rows) ? uiRowsAsDates(detailState.data.rows) : [],
    detailTotal: detailReady ? numeric(detailState.data?.total) : 0,
    detailTotalPages: detailReady ? Math.max(1, numeric(detailState.data?.totalPages) || 1) : 1,
    detailPage: detailReady ? Math.max(1, numeric(detailState.data?.page) || 1) : Math.max(1, numeric(detailPage) || 1),
    detailSummaryAmount: detailReady ? numeric(detailState.data?.summaryAmount) : 0,

    profitabilityStatus: profitabilityState.status,
    profitabilityError: profitabilityState.error,
    profitabilityReady,
    profitabilityResult: profitabilityReady ? profitabilityState.data : null,
    profitabilityCurrent: profitabilityReady ? profitabilityState.data?.current || null : null,
    profitabilityPrevious: profitabilityReady ? profitabilityState.data?.previous || null : null,
    profitabilityDrivers: profitabilityReady ? profitabilityState.data?.drivers || null : null,

    agingStatus: agingState.status,
    agingError: agingState.error,
    agingReady,
    agingResult: agingReady ? agingState.data : null,
    agingSummary: agingReady ? agingState.data?.summary || null : null,
    agingBuckets: agingReady && Array.isArray(agingState.data?.buckets) ? agingState.data.buckets : [],
    agingClients: agingReady && Array.isArray(agingState.data?.clients) ? agingState.data.clients : [],
    agingPriorityBookings: agingReady && Array.isArray(agingState.data?.priorityBookings) ? agingState.data.priorityBookings : [],

    runUiRowsProbe,
    runProfitabilityProbe,
    forecastPeriodStart: rangePayload.from,
    forecastPeriodEnd: iso(forecastEnd),

    runAgingProbe,
    runAgingQuery,
    runForecastProbe,
    runForecastQuery,
    runCloseoutProbe,
    runCloseoutQuery,
  };

  useEffect(() => {
    if (typeof window === "undefined" || process.env.NODE_ENV !== "development") return;
    window.__loopFinanceCutoverA4S15 = {
      rangeStatus: api.rangeStatus,
      rangeReady: api.rangeReady,
      rangeQueryMode: api.rangeResult?.queryMode || "",
      rangeScannedBookings: api.rangeDiagnostics?.scanned?.bookings ?? null,
      rangeBounded: api.rangeDiagnostics?.boundedQuery || null,
      uiRowsVersion: api.uiRowsVersion,
      uiRowsCount: api.currentUiRows.length,
      detailStatus: api.detailStatus,
      detailReady: api.detailReady,
      detailView: api.detailResult?.view || detailView || "",
      detailTotal: api.detailTotal,
      detailSummaryAmount: api.detailSummaryAmount,
      profitabilityStatus: api.profitabilityStatus,
      profitabilityReady: api.profitabilityReady,
      profitabilityQueryMode: api.profitabilityResult?.queryMode || "",
      profitabilityContribution: api.profitabilityCurrent?.business?.contribution ?? null,
      profitabilityMargin: api.profitabilityCurrent?.business?.margin ?? null,
    };
  }, [
    api.rangeStatus,
    api.rangeReady,
    api.rangeResult,
    api.rangeDiagnostics,
    api.uiRowsVersion,
    api.currentUiRows,
    api.detailStatus,
    api.detailReady,
    api.detailResult,
    api.detailTotal,
    api.detailSummaryAmount,
    api.profitabilityStatus,
    api.profitabilityReady,
    api.profitabilityResult,
    api.profitabilityCurrent,
    detailView,
  ]);

  return api;
}

function mapParity(localMap, remoteRows, remoteKey) {
  const local = localMap instanceof Map ? localMap : new Map();
  const remote = new Map(
    (Array.isArray(remoteRows) ? remoteRows : []).map((row) => [
      String(row?.[remoteKey] || ""),
      numeric(row?.revenue),
    ])
  );
  const keys = new Set([...local.keys(), ...remote.keys()]);
  return Array.from(keys).every((key) => moneyEqual(local.get(key), remote.get(key)));
}


function normalizeUiDate(value) {
  return iso(value);
}

function uiRowParity(localRows, remoteRows) {
  const local = Array.isArray(localRows) ? localRows : [];
  const remote = Array.isArray(remoteRows) ? remoteRows : [];
  const localById = new Map(local.map((row) => [String(row?.id || ""), row]));
  const textKeys = [
    "client",
    "service",
    "worker",
    "method",
    "status",
    "paymentMethod",
    "payoutStatus",
    "tipPaymentMethod",
    "tipPaymentMethodLabel",
    "workerPaymentNotes",
  ];
  const numericKeys = [
    "serviceRevenueAmount",
    "serviceRevenueDisplayAmount",
    "productSalesAmount",
    "membershipBusinessAmount",
    "totalBusinessAmount",
    "collected",
    "cashBaseAmount",
    "pending",
    "coveredServiceAmount",
    "loyaltyCoveredAmount",
    "loyaltyWorkerPayoutImpact",
    "loyaltyMaterialCostImpact",
    "loyaltyProductCostImpact",
    "loyaltyBusinessCashImpact",
    "tip",
    "workerTipAmount",
    "serviceCommissionAmount",
    "serviceCommissionDisplayAmount",
    "membershipWorkerIncomeAmount",
    "payout",
    "inventoryCost",
    "productSalesTotal",
    "productSalesCost",
    "productSalesPayout",
    "net",
  ];

  const details = remote.map((remoteRow) => {
    const id = String(remoteRow?.id || "");
    const localRow = localById.get(id);
    if (!localRow) {
      return { id, pass: false, found: false, text: false, date: false, numeric: false, rawAmount: false, loyaltyFlag: false };
    }

    const text = textKeys.every((key) => String(localRow?.[key] ?? "") === String(remoteRow?.[key] ?? ""));
    const date = normalizeUiDate(localRow?.date) === normalizeUiDate(remoteRow?.date);
    const numericFields = numericKeys.every((key) => moneyEqual(localRow?.[key], remoteRow?.[key]));
    const localExpectedRawAmount = numeric(localRow?.serviceRevenueAmount) + numeric(localRow?.productSalesAmount ?? localRow?.productSalesTotal);
    const rawAmount = moneyEqual(localExpectedRawAmount, remoteRow?.amount);
    const localDisplayAmount = localRow?.totalBusinessAmount ?? localRow?.amount;
    const displayAmount = moneyEqual(localDisplayAmount, remoteRow?.totalBusinessAmount);
    const loyaltyFlag = Boolean(localRow?.paymentIsLoyaltyReward) === Boolean(remoteRow?.paymentIsLoyaltyReward);

    return {
      id,
      pass: text && date && numericFields && rawAmount && displayAmount && loyaltyFlag,
      found: true,
      text,
      date,
      numeric: numericFields,
      rawAmount,
      displayAmount,
      loyaltyFlag,
    };
  });

  return {
    localCount: local.length,
    remoteCount: remote.length,
    matched: details.filter((row) => row.pass).length,
    pass: local.length === remote.length && details.length === remote.length && details.every((row) => row.pass),
    details,
  };
}

function cashMethodsParity(localMap, remoteRows) {
  const local = localMap instanceof Map ? localMap : new Map();
  const remote = new Map(
    (Array.isArray(remoteRows) ? remoteRows : []).map((row) => [String(row?.method || ""), row])
  );
  const keys = new Set([...local.keys(), ...remote.keys()]);
  return Array.from(keys).every((key) => {
    const left = local.get(key) || {};
    const right = remote.get(key) || {};
    return ["service", "product", "tip", "total"].every((field) => moneyEqual(left?.[field], right?.[field]));
  });
}

function coveredMethodsParity(localMap, remoteRows) {
  const local = localMap instanceof Map ? localMap : new Map();
  const remote = new Map(
    (Array.isArray(remoteRows) ? remoteRows : []).map((row) => [String(row?.label || ""), numeric(row?.amount)])
  );
  const keys = new Set([...local.keys(), ...remote.keys()]);
  return Array.from(keys).every((key) => moneyEqual(local.get(key), remote.get(key)));
}


function profitabilityMarginLocal(contribution, revenue) {
  if (Math.abs(numeric(revenue)) <= 0.005) return null;
  return (numeric(contribution) / numeric(revenue)) * 100;
}

function createProfitabilityLocalBucket(keyName, keyValue) {
  return {
    [keyName]: keyValue,
    bookings: 0,
    recognizedRevenue: 0,
    serviceRevenue: 0,
    productRevenue: 0,
    directLaborCost: 0,
    serviceLaborCost: 0,
    productCommissionCost: 0,
    materialCost: 0,
    productCost: 0,
    directCost: 0,
    contribution: 0,
    margin: null,
    tipsExcluded: 0,
    membershipIncome: 0,
  };
}

function addProfitabilityLocal(bucket, row) {
  bucket.bookings += 1;
  bucket.recognizedRevenue += numeric(row.recognizedRevenue);
  bucket.serviceRevenue += numeric(row.serviceRevenue);
  bucket.productRevenue += numeric(row.productRevenue);
  bucket.directLaborCost += numeric(row.directLaborCost);
  bucket.serviceLaborCost += numeric(row.serviceLaborCost);
  bucket.productCommissionCost += numeric(row.productCommissionCost);
  bucket.materialCost += numeric(row.materialCost);
  bucket.productCost += numeric(row.productCost);
  bucket.directCost += numeric(row.directCost);
  bucket.contribution += numeric(row.contribution);
  bucket.tipsExcluded += numeric(row.tipsExcluded);
  bucket.membershipIncome += numeric(row.membershipIncome);
  return bucket;
}

function finalizeProfitabilityLocal(bucket) {
  return {
    ...bucket,
    margin: profitabilityMarginLocal(bucket.contribution, bucket.recognizedRevenue),
  };
}

function buildLocalProfitabilityFromUiRows(rows, operatingExpenses = 0) {
  const source = Array.isArray(rows) ? rows : [];
  const business = createProfitabilityLocalBucket("scope", "business");
  const services = new Map();
  const workers = new Map();
  let ambiguousLoyaltyRows = 0;

  source.forEach((row) => {
    const paymentMethod = String(row?.paymentMethod || "").trim().toLowerCase();
    const paymentStatus = String(row?.status || "").trim().toLowerCase();
    const refunded = paymentStatus === "refunded";
    const covered = numeric(row?.coveredTrackedAmount) > 0 ||
      ["membership", "loyalty_reward", "courtesy"].includes(paymentMethod);
    const membership = covered && paymentMethod === "membership";
    const loyalty = covered && paymentMethod === "loyalty_reward";
    const courtesy = covered && paymentMethod === "courtesy";

    if (loyalty) ambiguousLoyaltyRows += 1;

    const serviceNominalRevenue = Math.max(0, numeric(row?.serviceRevenueAmount));
    const productRevenueRaw = Math.max(0, numeric(row?.productSalesAmount ?? row?.productSalesTotal));

    let recognizedServiceRevenue = 0;
    if (!refunded) {
      if (!covered) recognizedServiceRevenue = serviceNominalRevenue;
      else if (membership) recognizedServiceRevenue = Math.max(0, numeric(row?.membershipBusinessAmount));
      else if (loyalty) recognizedServiceRevenue = Math.max(0, numeric(row?.loyaltyCoveredAmount));
      else if (courtesy) recognizedServiceRevenue = 0;
    }

    const productRevenue = refunded ? 0 : productRevenueRaw;
    const recognizedRevenue = recognizedServiceRevenue + productRevenue;
    const workerTipAmount = Math.max(0, numeric(row?.workerTipAmount ?? row?.tip));
    const productCommissionCost = Math.max(0, numeric(row?.productSalesPayout));
    const totalWorkerPayout = Math.max(0, numeric(row?.payout));
    const explicitServiceCommission = Math.max(0, numeric(row?.serviceCommissionAmount));
    const payoutResidualForService = Math.max(0, totalWorkerPayout - workerTipAmount - productCommissionCost);
    const serviceLaborCost = Math.max(explicitServiceCommission, payoutResidualForService);
    const directLaborCost = serviceLaborCost + productCommissionCost;
    const materialCost = Math.max(0, numeric(row?.inventoryCost));
    const productCost = Math.max(0, numeric(row?.productSalesCost));
    const directCost = directLaborCost + materialCost + productCost;
    const contribution = recognizedRevenue - directCost;
    const membershipIncome = membership ? Math.max(0, numeric(row?.membershipBusinessAmount)) : 0;

    const localRow = {
      recognizedRevenue,
      serviceRevenue: recognizedServiceRevenue,
      productRevenue,
      directLaborCost,
      serviceLaborCost,
      productCommissionCost,
      materialCost,
      productCost,
      directCost,
      contribution,
      tipsExcluded: workerTipAmount,
      membershipIncome,
    };

    addProfitabilityLocal(business, localRow);

    const serviceName = String(row?.service || "Sin servicio").trim() || "Sin servicio";
    const serviceBucket = services.get(serviceName) || createProfitabilityLocalBucket("service", serviceName);
    addProfitabilityLocal(serviceBucket, {
      ...localRow,
      recognizedRevenue: recognizedServiceRevenue,
      serviceRevenue: recognizedServiceRevenue,
      productRevenue: 0,
      directLaborCost: serviceLaborCost,
      serviceLaborCost,
      productCommissionCost: 0,
      productCost: 0,
      directCost: serviceLaborCost + materialCost,
      contribution: recognizedServiceRevenue - serviceLaborCost - materialCost,
    });
    services.set(serviceName, serviceBucket);

    const workerName = String(row?.worker || "Sin trabajador").trim() || "Sin trabajador";
    const workerBucket = workers.get(workerName) || createProfitabilityLocalBucket("worker", workerName);
    addProfitabilityLocal(workerBucket, localRow);
    workers.set(workerName, workerBucket);
  });

  const businessFinal = finalizeProfitabilityLocal(business);
  const operatingExpenseValue = numeric(operatingExpenses);

  const sortRows = (values) => Array.from(values)
    .map(finalizeProfitabilityLocal)
    .sort((a, b) =>
      numeric(b.contribution) - numeric(a.contribution) ||
      numeric(b.recognizedRevenue) - numeric(a.recognizedRevenue)
    );

  return {
    comparable: ambiguousLoyaltyRows === 0,
    ambiguousLoyaltyRows,
    business: {
      ...businessFinal,
      operatingExpenses: operatingExpenseValue,
      operatingProfit: businessFinal.contribution - operatingExpenseValue,
      operatingMargin: profitabilityMarginLocal(
        businessFinal.contribution - operatingExpenseValue,
        businessFinal.recognizedRevenue
      ),
    },
    services: sortRows(services.values()),
    workers: sortRows(workers.values()),
  };
}

function profitabilityRowsParity(localRows, remoteRows, keyName) {
  const local = new Map((Array.isArray(localRows) ? localRows : []).map((row) => [String(row?.[keyName] || ""), row]));
  const remote = new Map((Array.isArray(remoteRows) ? remoteRows : []).map((row) => [String(row?.[keyName] || ""), row]));
  const keys = new Set([...local.keys(), ...remote.keys()]);
  const fields = ["bookings", "recognizedRevenue", "directLaborCost", "materialCost", "productCost", "directCost", "contribution"];

  const details = Array.from(keys).map((key) => {
    const left = local.get(key);
    const right = remote.get(key);
    const found = Boolean(left && right);
    const numericFields = found && fields.every((field) => moneyEqual(left?.[field], right?.[field]));
    const margin = found && (
      (left?.margin === null && right?.margin === null) ||
      moneyEqual(left?.margin, right?.margin)
    );
    return { key, pass: found && numericFields && margin, found, numericFields, margin };
  });

  return {
    pass: local.size === remote.size && details.every((row) => row.pass),
    localCount: local.size,
    remoteCount: remote.size,
    details,
  };
}


const PROFITABILITY_MONEY = new Intl.NumberFormat("es-PA", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

function profitabilityPercentLabel(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "—";
  return `${Number(value).toFixed(1)}%`;
}

function profitabilitySignedMoney(value) {
  const amount = numeric(value);
  if (Math.abs(amount) <= 0.005) return PROFITABILITY_MONEY.format(0);
  return `${amount > 0 ? "+" : "−"}${PROFITABILITY_MONEY.format(Math.abs(amount))}`;
}

function profitabilityTone(value) {
  const amount = numeric(value);
  return amount > 0.005 ? "#047857" : amount < -0.005 ? "#b91c1c" : "#475569";
}

function profitabilityGridRow(label, revenue, cost, contribution, margin) {
  return (
    <div
      key={label}
      className="finance-profitability-row"
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(140px, 1.5fr) repeat(4, minmax(92px, 1fr))",
        gap: 10,
        alignItems: "center",
        padding: "10px 0",
        borderTop: "1px solid #eef2f7",
        minWidth: 620,
      }}
    >
      <strong style={{ fontSize: 13, color: "#0f172a" }}>{label || "Sin nombre"}</strong>
      <span data-label="Ingreso" style={{ fontSize: 13, textAlign: "right" }}>{PROFITABILITY_MONEY.format(numeric(revenue))}</span>
      <span data-label="Costo" style={{ fontSize: 13, textAlign: "right" }}>{PROFITABILITY_MONEY.format(numeric(cost))}</span>
      <strong data-label="Contribución" style={{ fontSize: 13, textAlign: "right", color: profitabilityTone(contribution) }}>
        {PROFITABILITY_MONEY.format(numeric(contribution))}
      </strong>
      <span data-label="Margen" style={{ fontSize: 13, textAlign: "right" }}>{profitabilityPercentLabel(margin)}</span>
    </div>
  );
}



const AGING_BUCKET_UI = [
  { key: "all", label: "Todo por cobrar" },
  { key: "recent_0_7", label: "0–7 días" },
  { key: "watch_8_30", label: "8–30 días" },
  { key: "overdue_31_59", label: "31–59 días" },
  { key: "critical_60_plus", label: "60+ días" },
];

function agingMoney(value) {
  return new Intl.NumberFormat("es-PA", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(numeric(value));
}

function agingDate(value) {
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return "Sin fecha";
  return d.toLocaleDateString("es-PA", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).replace(".", "");
}

export function FinanceAgingPanel({
  remote,
  periodLabel = "",
  workerId = "all",
}) {
 const region=useBusinessRegion();
 const PROFITABILITY_MONEY=useRegionMoney();
 const CLOSEOUT_MONEY=PROFITABILITY_MONEY;
 const agingMoney=value=>PROFITABILITY_MONEY.format(numeric(value));
 const forecastMoney=agingMoney;

  const status = String(remote?.agingStatus || "disabled");
  const result = remote?.agingResult || null;
  const summary = result?.summary || {};
  const buckets = Array.isArray(result?.buckets) ? result.buckets : [];
  const clients = Array.isArray(result?.clients) ? result.clients : [];

  const [bucket, setBucket] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [detailState, setDetailState] = useState({
    status: "idle",
    key: "",
    data: null,
    error: "",
  });

  useEffect(() => {
    setBucket("all");
    setSearch("");
    setPage(1);
    setDetailState({ status: "idle", key: "", data: null, error: "" });
  }, [periodLabel, workerId]);

  useEffect(() => {
    if (!remote?.agingReady || !result || typeof remote?.runAgingQuery !== "function") {
      return undefined;
    }

    const normalizedSearch = String(search || "").trim();
    const defaultQuery = bucket === "all" && !normalizedSearch && page === 1;

    if (defaultQuery) {
      setDetailState({
        status: "ready",
        key: "default",
        data: result,
        error: "",
      });
      return undefined;
    }

    let cancelled = false;
    const requestKey = JSON.stringify({ bucket, search: normalizedSearch, page, pageSize: 20 });
    const timer = setTimeout(() => {
      setDetailState((previous) => ({
        status: "loading",
        key: requestKey,
        data: previous.key === requestKey ? previous.data : null,
        error: "",
      }));

      remote.runAgingQuery({
        bucket,
        search: normalizedSearch,
        page,
        pageSize: 20,
      })
        .then((data) => {
          if (!cancelled) {
            setDetailState({ status: "ready", key: requestKey, data, error: "" });
          }
        })
        .catch((error) => {
          if (!cancelled) {
            setDetailState({
              status: "error",
              key: requestKey,
              data: null,
              error: `${error?.code || "error"}: ${error?.message || String(error)}`,
            });
          }
        });
    }, 180);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [remote, result, bucket, search, page]);

  const detailData = detailState.status === "ready" ? detailState.data : result;
  const detail = detailData?.detail || {};
  const detailRows = Array.isArray(detail?.rows) ? detail.rows : [];
  const totalPages = Math.max(1, numeric(detail?.totalPages) || 1);
  const safePage = Math.max(1, numeric(detail?.page) || page);
  const totalOutstanding = numeric(summary?.totalOutstanding);
  const overdueAmount = numeric(summary?.overdueAmount);
  const criticalAmount = numeric(summary?.criticalAmount);
  const carryoverAmount = numeric(summary?.carryoverAmount);
  const currentPeriodOutstanding = numeric(summary?.currentPeriodOutstanding);

  const bucketTotal = buckets.reduce((sum, row) => sum + numeric(row?.amount), 0);
  const runtimeChecks = result ? [
    {
      label: "status ready",
      pass: status === "ready" && Boolean(remote?.agingReady),
      left: "ready",
      right: status,
    },
    {
      label: "queryMode",
      pass: result?.queryMode === AGING_QUERY_MODE,
      left: AGING_QUERY_MODE,
      right: String(result?.queryMode || ""),
    },
    {
      label: "version",
      pass: result?.agingVersion === AGING_VERSION,
      left: AGING_VERSION,
      right: String(result?.agingVersion || ""),
    },
    {
      label: "bucket reconciliation",
      pass: moneyEqual(bucketTotal, totalOutstanding),
      left: bucketTotal.toFixed(2),
      right: totalOutstanding.toFixed(2),
    },
    {
      label: "current + carryover",
      pass: moneyEqual(currentPeriodOutstanding + carryoverAmount, totalOutstanding),
      left: (currentPeriodOutstanding + carryoverAmount).toFixed(2),
      right: totalOutstanding.toFixed(2),
    },
  ] : [];

  const runtimeFailures = runtimeChecks.filter((row) => !row.pass);
  const runtimeReport = [
    "LOOP A4.5.3 AGING UI RUNTIME",
    `Period: ${periodLabel || "n/a"}`,
    `Worker: ${String(workerId || "all")}`,
    `Status: ${status}`,
    `Query mode: ${result?.queryMode || ""}`,
    `Version: ${result?.agingVersion || ""}`,
    `Semantics: ${result?.agingSemanticsVersion || ""}`,
    `Total outstanding: ${totalOutstanding.toFixed(2)}`,
    `Current-period outstanding: ${currentPeriodOutstanding.toFixed(2)}`,
    `Carryover: ${carryoverAmount.toFixed(2)}`,
    `Overdue 31+: ${overdueAmount.toFixed(2)}`,
    `Critical 60+: ${criticalAmount.toFixed(2)}`,
    `Bookings: ${numeric(summary?.bookings)}`,
    `Clients: ${numeric(summary?.clients)}`,
    `Oldest days: ${numeric(summary?.oldestDays)}`,
    "",
    ...runtimeChecks.map((row) =>
      `${row.pass ? "PASS" : "FAIL"} | ${row.label} | local=${row.left} | remote=${row.right}`
    ),
    "",
    `Result: ${result && runtimeFailures.length === 0 ? "PASS" : result ? "FAIL" : status.toUpperCase()}`,
  ].join("\n");

  const copyRuntime = async () => {
    try {
      await navigator.clipboard.writeText(runtimeReport);
    } catch (_) {
      window.prompt("Copia el reporte A4.5.3:", runtimeReport);
    }
  };

  const selectBucket = (nextBucket) => {
    setBucket(nextBucket);
    setPage(1);
  };

  if (status === "loading" || status === "idle" || status === "disabled") {
    return (
      <article
        data-loop-aging="a4-5-3"
        style={{
          gridColumn: "1 / -1",
          border: "1px solid #dbe7ea",
          borderRadius: 16,
          background: "#fff",
          padding: 18,
        }}
      >
        <strong style={{ color: "#0f172a" }}>Antigüedad de cuentas por cobrar</strong>
        <div style={{ marginTop: 6, color: "#64748b", fontSize: 12 }}>
          Preparando saldos pendientes y antigüedad…
        </div>
      </article>
    );
  }

  if (status === "error") {
    return (
      <article
        data-loop-aging="a4-5-3"
        style={{
          gridColumn: "1 / -1",
          border: "1px solid #fecaca",
          borderRadius: 16,
          background: "#fff",
          padding: 18,
        }}
      >
        <strong style={{ color: "#991b1b" }}>No se pudo cargar la antigüedad de cobro</strong>
        <div style={{ marginTop: 6, color: "#64748b", fontSize: 12 }}>
          {remote?.agingError || "Error desconocido"}
        </div>
      </article>
    );
  }

  return (
    <article
      data-loop-aging="a4-5-3"
      style={{
        gridColumn: "1 / -1",
        border: "1px solid #dbe7ea",
        borderRadius: 16,
        background: "#fff",
        padding: 18,
        minWidth: 0,
      }}
    >
      <style>{`
        .loop-aging-kpis{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}
        .loop-aging-buckets{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:8px}
        .loop-aging-main{display:flex;flex-direction:column;gap:12px}
        .loop-aging-priority{order:-1}
        .loop-aging-priority-grid{display:grid!important;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px!important;margin-top:10px!important}
        .loop-aging-priority-card{padding:9px 10px!important;border:1px solid #eef2f7!important;border-radius:11px!important;display:grid!important;grid-template-columns:24px minmax(0,1fr) auto!important;gap:8px!important;align-items:center!important}
        .loop-aging-detail{width:100%}
        .loop-aging-row{display:grid;grid-template-columns:minmax(150px,1.25fr) minmax(130px,1fr) minmax(110px,.8fr) 105px 80px 110px;gap:10px;align-items:center}
        .loop-aging-row>span,.loop-aging-row>strong{min-width:0}
        @media(max-width:1100px){
          .loop-aging-kpis{grid-template-columns:repeat(3,minmax(0,1fr))}
          .loop-aging-priority-grid{grid-template-columns:repeat(2,minmax(0,1fr))}
        }
        @media(max-width:760px){
          .loop-aging-kpis{grid-template-columns:1fr 1fr}
          .loop-aging-buckets{grid-template-columns:1fr 1fr}
          .loop-aging-priority-grid{grid-template-columns:1fr}
          .loop-aging-row{grid-template-columns:1fr auto;gap:4px 10px}
          .loop-aging-row .aging-hide-mobile{display:none}
        }
      `}</style>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <span style={{ display: "block", color: "#0f766e", fontSize: 10, fontWeight: 900, letterSpacing: ".09em", textTransform: "uppercase" }}>
            Cobranza · Antigüedad
          </span>
          <h4 style={{ margin: "5px 0 0", color: "#0f172a", fontSize: 19 }}>
            Qué cobrar primero
          </h4>
          <p style={{ margin: "5px 0 0", color: "#64748b", fontSize: 12, maxWidth: 760 }}>
            Cartera total pendiente al cierre del rango, incluyendo saldos de períodos anteriores, ordenada por antigüedad del servicio.
          </p>
        </div>
        <div style={{ textAlign: "right" }}>
          <span style={{ display: "block", color: "#64748b", fontSize: 10 }}>Por cobrar total</span>
          <strong style={{ display: "block", color: "#0f172a", fontSize: 21 }}>{agingMoney(totalOutstanding)}</strong>
          <span style={{ display: "block", color: "#64748b", fontSize: 10 }}>
            {numeric(summary?.bookings)} reserva{numeric(summary?.bookings) === 1 ? "" : "s"} · {numeric(summary?.clients)} cliente{numeric(summary?.clients) === 1 ? "" : "s"}
          </span>
        </div>
      </div>

      <div className="loop-aging-kpis" style={{ marginTop: 16 }}>
        {[
          ["Del período", currentPeriodOutstanding, "Saldo originado en el rango seleccionado"],
          ["De períodos anteriores", carryoverAmount, "Pendiente originado antes del rango seleccionado"],
          ["Más antiguo", numeric(summary?.oldestDays), "días"],
        ].map(([label, value, help], index) => (
          <div key={label} style={{ border: "1px solid #e2e8f0", borderRadius: 13, padding: "12px 13px", background: "#fbfcfd" }}>
            <span style={{ display: "block", color: "#64748b", fontSize: 10, fontWeight: 750 }}>{label}</span>
            <strong style={{ display: "block", marginTop: 5, color: "#0f172a", fontSize: 17 }}>
              {index === 2 ? `${numeric(value)} días` : agingMoney(value)}
            </strong>
            <em style={{ display: "block", marginTop: 4, color: "#94a3b8", fontSize: 9, fontStyle: "normal" }}>{help}</em>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
          <strong style={{ color: "#334155", fontSize: 12 }}>Antigüedad del saldo</strong>
          <span style={{ color: "#94a3b8", fontSize: 10 }}>Selecciona una antigüedad para filtrar el detalle</span>
        </div>
        <div className="loop-aging-buckets">
          {buckets.map((row) => {
            const active = bucket === row.key;
            return (
              <button
                type="button"
                key={row.key}
                onClick={() => selectBucket(row.key)}
                style={{
                  border: active ? "1px solid #0f766e" : "1px solid #e2e8f0",
                  borderRadius: 12,
                  background: active ? "#f0fdfa" : "#fff",
                  textAlign: "left",
                  padding: "11px 12px",
                  cursor: "pointer",
                }}
              >
                <span style={{ display: "block", color: active ? "#0f766e" : "#64748b", fontSize: 10, fontWeight: 800 }}>{row.label}</span>
                <strong style={{ display: "block", marginTop: 4, color: "#0f172a", fontSize: 16 }}>{agingMoney(row.amount)}</strong>
                <em style={{ display: "block", marginTop: 3, color: "#94a3b8", fontSize: 9, fontStyle: "normal" }}>
                  {numeric(row.bookings)} reservas · {numeric(row.clients)} clientes
                </em>
              </button>
            );
          })}
        </div>
      </div>

      <div className="loop-aging-main" style={{ marginTop: 16 }}>
        <section className="loop-aging-detail" style={{ border: "1px solid #e2e8f0", borderRadius: 14, overflow: "hidden", minWidth: 0 }}>
          <div style={{ padding: 12, borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <strong style={{ color: "#0f172a", fontSize: 12 }}>Detalle de cuentas por cobrar</strong>
              <span style={{ display: "block", marginTop: 2, color: "#94a3b8", fontSize: 9 }}>
                {AGING_BUCKET_UI.find((row) => row.key === bucket)?.label || "Todo por cobrar"} · {numeric(detail?.total)} registros
              </span>
            </div>
            <div style={{ display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ position: "relative", minWidth: 150 }}>
                <select
                  value={bucket}
                  onChange={(event) => selectBucket(event.target.value)}
                  style={{ width: "100%", minHeight: 34, appearance: "none", cursor: "pointer", border: "1px solid #dbe4e8", borderRadius: 9, background: "#fff", padding: "0 34px 0 10px", fontSize: 11, color: "#334155", fontWeight: 750 }}
                >
                  {AGING_BUCKET_UI.map((row) => <option value={row.key} key={row.key}>{row.label}</option>)}
                </select>
                <span aria-hidden="true" style={{ pointerEvents: "none", position: "absolute", inset: "0 0 0 auto", width: 30, display: "grid", placeItems: "center", borderLeft: "1px solid #e5eaed", color: "#64748b", fontSize: 12 }}>⌄</span>
              </div>
              <input
                type="search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                placeholder="Buscar cliente, servicio o trabajador"
                style={{ minHeight: 34, minWidth: 220, border: "1px solid #dbe4e8", borderRadius: 9, padding: "0 9px", fontSize: 11 }}
              />
            </div>
          </div>

          <div className="loop-aging-row loop-aging-table-head" style={{ padding: "8px 12px", background: "#f8fafc", color: "#64748b", fontSize: 9, fontWeight: 850, textTransform: "uppercase" }}>
            <span>Cliente</span>
            <span className="aging-hide-mobile">Servicio</span>
            <span className="aging-hide-mobile">Trabajador</span>
            <span className="aging-hide-mobile">Fecha</span>
            <span>Edad</span>
            <span style={{ textAlign: "right" }}>Saldo</span>
          </div>

          {detailState.status === "loading" ? (
            <div style={{ padding: 18, color: "#64748b", fontSize: 11 }}>Actualizando detalle…</div>
          ) : detailState.status === "error" ? (
            <div style={{ padding: 18, color: "#991b1b", fontSize: 11 }}>{detailState.error}</div>
          ) : detailRows.length ? (
            detailRows.map((row) => (
              <div
                className="loop-aging-row loop-aging-detail-card"
                key={`${row.id || row.bookingId || "row"}-${row.date || ""}`}
                style={{ padding: "10px 12px", borderTop: "1px solid #eef2f7", fontSize: 11 }}
              >
                <span style={{ color: "#0f172a", fontWeight: 750, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.client || "Cliente"}</span>
                <span data-label="Servicio" className="aging-hide-mobile" style={{ color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.service || "Servicio"}</span>
                <span data-label="Equipo" className="aging-hide-mobile" style={{ color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.worker || "—"}</span>
                <span data-label="Fecha" className="aging-hide-mobile" style={{ color: "#64748b" }}>{agingDate(row.date)}</span>
                <span data-label="Antigüedad" style={{ color: numeric(row.ageDays) >= 31 ? "#b45309" : "#475569", fontWeight: 750 }}>{numeric(row.ageDays)}d</span>
                <strong data-label="Por cobrar" style={{ color: "#0f172a", textAlign: "right" }}>{agingMoney(row.pending)}</strong>
              </div>
            ))
          ) : (
            <div style={{ padding: 18, color: "#64748b", fontSize: 11 }}>No hay cuentas por cobrar para este filtro.</div>
          )}

          <div style={{ padding: 10, borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <span style={{ color: "#94a3b8", fontSize: 10 }}>
              Página {safePage} de {totalPages}
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                type="button"
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                disabled={safePage <= 1 || detailState.status === "loading"}
                style={{ minHeight: 30, borderRadius: 8, border: "1px solid #dbe4e8", background: "#fff", padding: "0 9px" }}
              >
                Anterior
              </button>
              <button
                type="button"
                onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
                disabled={safePage >= totalPages || detailState.status === "loading"}
                style={{ minHeight: 30, borderRadius: 8, border: "1px solid #dbe4e8", background: "#fff", padding: "0 9px" }}
              >
                Siguiente
              </button>
            </div>
          </div>
        </section>

        <section className="loop-aging-priority" style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: 13, minWidth: 0 }}>
          <span style={{ display: "block", color: "#0f766e", fontSize: 9, fontWeight: 900, letterSpacing: ".08em", textTransform: "uppercase" }}>
            Prioridad de cobro
          </span>
          <strong style={{ display: "block", marginTop: 4, color: "#0f172a", fontSize: 14 }}>Clientes a revisar primero</strong>
          <div className="loop-aging-priority-grid">
            {clients.length ? clients.slice(0, 6).map((row, index) => (
              <button
                type="button"
                className="loop-aging-priority-card"
                key={`${row.client}-${index}`}
                title="Filtrar el detalle por este cliente"
                onClick={() => {
                  setBucket("all");
                  setSearch(String(row.client || ""));
                  setPage(1);
                }}
                style={{ appearance: "none", font: "inherit", textAlign: "left", cursor: "pointer" }}
              >
                <i style={{ width: 22, height: 22, borderRadius: 8, background: "#f1f5f9", color: "#475569", display: "grid", placeItems: "center", fontSize: 9, fontStyle: "normal", fontWeight: 800 }}>{index + 1}</i>
                <div style={{ minWidth: 0 }}>
                  <strong style={{ display: "block", color: "#0f172a", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.client || "Cliente"}</strong>
                  <span style={{ display: "block", marginTop: 2, color: "#94a3b8", fontSize: 9 }}>
                    {numeric(row.bookings)} saldo{numeric(row.bookings) === 1 ? "" : "s"} · más antiguo {numeric(row.oldestDays)}d
                  </span>
                </div>
                <strong style={{ color: numeric(row.oldestDays) >= 31 ? "#b45309" : "#0f766e", fontSize: 11 }}>{agingMoney(row.amount)}</strong>
              </button>
            )) : (
              <div style={{ marginTop: 10, color: "#64748b", fontSize: 11 }}>Sin clientes pendientes.</div>
            )}
          </div>
        </section>
      </div>

      <div style={{ marginTop: 14, borderTop: "1px solid #eef2f7", paddingTop: 11, display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ color: "#64748b", fontSize: 10 }}>
          Estado actual del saldo al cierre del rango. Loop no reconstruye un snapshot histórico previo sin historial de eventos de cobro.
        </span>

      </div>
    </article>
  );
}


function forecastMoney(value) {
  return new Intl.NumberFormat("es-PA", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(numeric(value));
}

function forecastPct(value) {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  return `${Number(value).toFixed(1)}%`;
}

function forecastStatusMeta(status) {
  const key = String(status || "no_target");
  if (key === "booked_to_target") return { label: "Meta cubierta con agenda", tone: "#047857", bg: "#ecfdf5" };
  if (key === "on_track") return { label: "En camino", tone: "#047857", bg: "#ecfdf5" };
  if (key === "at_risk") return { label: "En riesgo", tone: "#b45309", bg: "#fffbeb" };
  if (key === "hit") return { label: "Meta cumplida", tone: "#047857", bg: "#ecfdf5" };
  if (key === "missed") return { label: "Meta no alcanzada", tone: "#b91c1c", bg: "#fef2f2" };
  return { label: "Sin meta definida", tone: "#475569", bg: "#f8fafc" };
}

export function FinanceForecastPanel({
  remote,
  businessId = "",
  db,
  auth,
  periodLabel = "",
  workerId = "all",
}) {
 const region=useBusinessRegion();
 const PROFITABILITY_MONEY=useRegionMoney();
 const CLOSEOUT_MONEY=PROFITABILITY_MONEY;
 const agingMoney=value=>PROFITABILITY_MONEY.format(numeric(value));
 const forecastMoney=agingMoney;

  const fullPeriodStart = String(remote?.forecastPeriodStart || "");
  const fullPeriodEnd = String(remote?.forecastPeriodEnd || "");
  const targetScopeAll = String(workerId || "all") === "all";
  const periodStartKey = fullPeriodStart ? fullPeriodStart.slice(0, 10) : "";
  const periodEndKey = fullPeriodEnd ? fullPeriodEnd.slice(0, 10) : "";
  const targetDocId = periodStartKey && periodEndKey
    ? `revenue_${periodStartKey}_${periodEndKey}`
    : "";
  const targetDocPath = businessId && targetDocId
    ? `businessFinance/${businessId}/targets/${targetDocId}`
    : "";
  const storageKey = `loop.finance.target.v1:${String(businessId || "business")}:${fullPeriodEnd || periodLabel || "period"}`;

  const [draftTarget, setDraftTarget] = useState("");
  const [appliedTarget, setAppliedTarget] = useState(0);
  const [targetLoaded, setTargetLoaded] = useState(false);
  const [targetRecord, setTargetRecord] = useState(null);
  const [targetSource, setTargetSource] = useState("none");
  const [targetPersistenceState, setTargetPersistenceState] = useState({
    status: "idle",
    error: "",
    message: "",
  });
  const [forecastState, setForecastState] = useState({
    status: "idle",
    data: null,
    error: "",
  });

  useEffect(() => {
    let cancelled = false;

    const loadTarget = async () => {
      setTargetLoaded(false);
      setTargetRecord(null);
      setTargetSource("none");
      setTargetPersistenceState({ status: "loading", error: "", message: "" });
      setForecastState({ status: "idle", data: null, error: "" });

      if (!targetScopeAll || !businessId || !targetDocPath || !db) {
        if (!cancelled) {
          setAppliedTarget(0);
          setDraftTarget("");
          setTargetLoaded(true);
          setTargetPersistenceState({ status: "idle", error: "", message: "" });
        }
        return;
      }

      let localFallback = 0;
      if (typeof window !== "undefined") {
        try {
          const raw = window.localStorage.getItem(storageKey);
          const parsed = Number(raw);
          if (Number.isFinite(parsed) && parsed > 0) localFallback = parsed;
        } catch (_) {
          localFallback = 0;
        }
      }

      try {
        const snap = await getDoc(doc(db, targetDocPath));
        if (cancelled) return;
        const record = snap.exists() ? (snap.data() || {}) : null;
        const centralAmount = record?.active !== false && numeric(record?.amountCents) > 0
          ? numeric(record.amountCents) / 100
          : 0;

        if (centralAmount > 0) {
          setAppliedTarget(centralAmount);
          setDraftTarget(String(centralAmount));
          setTargetRecord(record);
          setTargetSource("firestore");
          setTargetPersistenceState({
            status: "ready",
            error: "",
            message: "Meta sincronizada con Loop.",
          });
          if (typeof window !== "undefined") {
            try {
              window.localStorage.removeItem(storageKey);
            } catch (_) {
              // no-op
            }
          }
        } else if (localFallback > 0) {
          setAppliedTarget(localFallback);
          setDraftTarget(String(localFallback));
          setTargetRecord(record);
          setTargetSource("local_migration");
          setTargetPersistenceState({
            status: "ready",
            error: "",
            message: "Meta local detectada. Pulsa Aplicar meta para sincronizarla con Loop.",
          });
        } else {
          setAppliedTarget(0);
          setDraftTarget("");
          setTargetRecord(record);
          setTargetSource(record ? "firestore_inactive" : "none");
          setTargetPersistenceState({
            status: "ready",
            error: "",
            message: record ? "No hay una meta activa para este período." : "",
          });
        }
      } catch (error) {
        if (cancelled) return;
        setAppliedTarget(localFallback);
        setDraftTarget(localFallback > 0 ? String(localFallback) : "");
        setTargetSource(localFallback > 0 ? "local_fallback" : "error");
        setTargetPersistenceState({
          status: "error",
          error: `${error?.code || "error"}: ${error?.message || String(error)}`,
          message: localFallback > 0
            ? "No pudimos leer la meta central; se muestra la copia local temporal."
            : "No pudimos leer la meta central.",
        });
      } finally {
        if (!cancelled) setTargetLoaded(true);
      }
    };

    loadTarget();

    return () => {
      cancelled = true;
    };
  }, [db, businessId, targetDocPath, storageKey, targetScopeAll, periodLabel, workerId]);

  useEffect(() => {
    if (
      !targetLoaded ||
      !remote?.rangeReady ||
      typeof remote?.runForecastQuery !== "function"
    ) return undefined;

    let cancelled = false;
    setForecastState((previous) => ({
      status: "loading",
      data: previous.data,
      error: "",
    }));

    remote.runForecastQuery({
      targetAmount: targetScopeAll ? appliedTarget : 0,
    })
      .then((data) => {
        if (!cancelled) setForecastState({ status: "ready", data, error: "" });
      })
      .catch((error) => {
        if (!cancelled) {
          setForecastState({
            status: "error",
            data: null,
            error: `${error?.code || "error"}: ${error?.message || String(error)}`,
          });
        }
      });

    return () => {
      cancelled = true;
    };
    // The panel only mounts inside the Forecast workspace; query is cached/deduped upstream.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetLoaded, appliedTarget, targetScopeAll, periodLabel, workerId, remote?.rangeReady, fullPeriodEnd]);

  const applyTarget = async () => {
    if (!targetScopeAll || !targetDocPath || !db) return;
    const next = Math.max(0, Number(String(draftTarget || "").replace(",", ".")) || 0);
    if (!(next > 0)) {
      setTargetPersistenceState({
        status: "error",
        error: "target_must_be_positive",
        message: "La meta debe ser mayor que cero.",
      });
      return;
    }

    const uid = String(auth?.currentUser?.uid || "").trim();
    if (!uid) {
      setTargetPersistenceState({
        status: "error",
        error: "auth_required",
        message: "Necesitas una sesión autenticada para guardar la meta.",
      });
      return;
    }

    setTargetPersistenceState({ status: "saving", error: "", message: "Guardando meta…" });
    try {
      const ref = doc(db, targetDocPath);
      const snap = await getDoc(ref);
      const existing = snap.exists() ? (snap.data() || {}) : {};
      const now = new Date().toISOString();
      const amountCents = Math.round(next * 100);
      const payload = {
        schemaVersion: 1,
        businessId: String(businessId),
        metric: "recognized_revenue",
        periodStartIso: fullPeriodStart,
        periodEndIso: fullPeriodEnd,
        amountCents,
        currency: region.currency || "USD",
        active: true,
        createdAt: String(existing.createdAt || now),
        updatedAt: now,
        createdByUid: String(existing.createdByUid || uid),
        updatedByUid: uid,
        voidedAt: "",
        voidedByUid: "",
      };

      await setDoc(ref, payload, { merge: false });

      setAppliedTarget(next);
      setDraftTarget(String(next));
      setTargetRecord(payload);
      setTargetSource("firestore");
      setTargetPersistenceState({
        status: "ready",
        error: "",
        message: "Meta sincronizada con Loop.",
      });

      if (typeof window !== "undefined") {
        try {
          window.localStorage.removeItem(storageKey);
        } catch (_) {
          // no-op
        }
      }
    } catch (error) {
      setTargetPersistenceState({
        status: "error",
        error: `${error?.code || "error"}: ${error?.message || String(error)}`,
        message: "No pudimos guardar la meta central.",
      });
    }
  };

  const clearTarget = async () => {
    if (!targetScopeAll || !targetDocPath || !db) return;
    const uid = String(auth?.currentUser?.uid || "").trim();

    if (targetSource === "local_migration" || targetSource === "local_fallback") {
      setDraftTarget("");
      setAppliedTarget(0);
      setTargetSource("none");
      setTargetRecord(null);
      setTargetPersistenceState({ status: "ready", error: "", message: "Meta local descartada." });
      if (typeof window !== "undefined") {
        try {
          window.localStorage.removeItem(storageKey);
        } catch (_) {
          // no-op
        }
      }
      return;
    }

    if (!uid) {
      setTargetPersistenceState({
        status: "error",
        error: "auth_required",
        message: "Necesitas una sesión autenticada para quitar la meta.",
      });
      return;
    }

    setTargetPersistenceState({ status: "saving", error: "", message: "Quitando meta…" });
    try {
      const ref = doc(db, targetDocPath);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        setDraftTarget("");
        setAppliedTarget(0);
        setTargetRecord(null);
        setTargetSource("none");
        setTargetPersistenceState({ status: "ready", error: "", message: "No había una meta central activa." });
        return;
      }

      const existing = snap.data() || {};
      const now = new Date().toISOString();
      const payload = {
        schemaVersion: 1,
        businessId: String(businessId),
        metric: "recognized_revenue",
        periodStartIso: String(existing.periodStartIso || fullPeriodStart),
        periodEndIso: String(existing.periodEndIso || fullPeriodEnd),
        amountCents: Math.max(1, Math.round(numeric(existing.amountCents))),
        currency: region.currency || "USD",
        active: false,
        createdAt: String(existing.createdAt || now),
        updatedAt: now,
        createdByUid: String(existing.createdByUid || uid),
        updatedByUid: uid,
        voidedAt: now,
        voidedByUid: uid,
      };

      await setDoc(ref, payload, { merge: false });

      setDraftTarget("");
      setAppliedTarget(0);
      setTargetRecord(payload);
      setTargetSource("firestore_inactive");
      setTargetPersistenceState({
        status: "ready",
        error: "",
        message: "Meta quitada. El registro queda conservado para auditoría.",
      });

      if (typeof window !== "undefined") {
        try {
          window.localStorage.removeItem(storageKey);
        } catch (_) {
          // no-op
        }
      }
    } catch (error) {
      setTargetPersistenceState({
        status: "error",
        error: `${error?.code || "error"}: ${error?.message || String(error)}`,
        message: "No pudimos quitar la meta central.",
      });
    }
  };

  const data = forecastState.data || null;
  const period = data?.period || {};
  const target = data?.target || {};
  const performance = data?.performance || {};
  const previous = data?.previous || null;
  const targetAmount = numeric(target?.amount);
  const actual = numeric(performance?.actualToDate);
  const futureBooked = numeric(performance?.futureBookedRevenue);
  const bookedFloor = numeric(performance?.bookedFloor);
  const runRate = numeric(performance?.runRateForecast);
  const forecastClose = numeric(performance?.forecastClose);
  const required = numeric(performance?.requiredPerRemainingDay);
  const remaining = numeric(period?.remainingDays);
  const statusMeta = forecastStatusMeta(performance?.status);
  const targetGap = targetAmount > 0 ? forecastClose - targetAmount : 0;
  const previousRevenue = previous ? numeric(previous?.revenue) : null;
  const progressWidth = targetAmount > 0
    ? Math.max(0, Math.min(100, numeric(target?.progressPct)))
    : 0;

  const runtimeChecks = data ? [
    {
      label: "status ready",
      pass: forecastState.status === "ready",
      left: "ready",
      right: forecastState.status,
    },
    {
      label: "queryMode",
      pass: data?.queryMode === FORECAST_QUERY_MODE,
      left: FORECAST_QUERY_MODE,
      right: String(data?.queryMode || ""),
    },
    {
      label: "semantics",
      pass: data?.forecastSemanticsVersion === FORECAST_SEMANTICS_VERSION,
      left: FORECAST_SEMANTICS_VERSION,
      right: String(data?.forecastSemanticsVersion || ""),
    },
    {
      label: "full period boundary",
      pass: String(period?.toExclusive || "") === fullPeriodEnd,
      left: fullPeriodEnd,
      right: String(period?.toExclusive || ""),
    },
    {
      label: "timeline reconciliation",
      pass: numeric(period?.elapsedDays) + numeric(period?.remainingDays) === numeric(period?.totalDays),
      left: String(numeric(period?.totalDays)),
      right: String(numeric(period?.elapsedDays) + numeric(period?.remainingDays)),
    },
    {
      label: "booked floor",
      pass: moneyEqual(bookedFloor, actual + futureBooked),
      left: (actual + futureBooked).toFixed(2),
      right: bookedFloor.toFixed(2),
    },
    {
      label: "forecast close",
      pass: moneyEqual(
        forecastClose,
        period?.phase === "complete" ? actual : Math.max(runRate, bookedFloor)
      ),
      left: (period?.phase === "complete" ? actual : Math.max(runRate, bookedFloor)).toFixed(2),
      right: forecastClose.toFixed(2),
    },
    {
      label: "target applied",
      pass: !targetScopeAll || moneyEqual(targetAmount, appliedTarget),
      left: targetScopeAll ? appliedTarget.toFixed(2) : "worker-filter-no-business-target",
      right: targetAmount.toFixed(2),
    },
    {
      label: "central target persistence",
      pass: !targetScopeAll || appliedTarget <= 0 || (
        targetSource === "firestore" &&
        targetRecord?.active === true &&
        moneyEqual(numeric(targetRecord?.amountCents) / 100, appliedTarget)
      ),
      left: targetScopeAll && appliedTarget > 0 ? "firestore-active" : "n/a",
      right: `${targetSource}:${targetRecord?.active === true ? "active" : "inactive"}`,
    },
    {
      label: "target period identity",
      pass: !targetScopeAll || appliedTarget <= 0 || (
        String(targetRecord?.periodStartIso || "") === fullPeriodStart &&
        String(targetRecord?.periodEndIso || "") === fullPeriodEnd &&
        String(targetRecord?.metric || "") === "recognized_revenue"
      ),
      left: `${fullPeriodStart}|${fullPeriodEnd}|recognized_revenue`,
      right: `${targetRecord?.periodStartIso || ""}|${targetRecord?.periodEndIso || ""}|${targetRecord?.metric || ""}`,
    },
  ] : [];
  const runtimeFailures = runtimeChecks.filter((row) => !row.pass);

  const runtimeReport = [
    "LOOP A4.6.4 TARGETS CENTRAL PERSISTENCE RUNTIME",
    `Period: ${periodLabel || "n/a"}`,
    `Worker: ${String(workerId || "all")}`,
    `UI status: ${forecastState.status}`,
    `Query mode: ${data?.queryMode || ""}`,
    `Version: ${data?.forecastVersion || ""}`,
    `Semantics: ${data?.forecastSemanticsVersion || ""}`,
    `Phase: ${period?.phase || ""}`,
    `Total days: ${numeric(period?.totalDays)}`,
    `Elapsed days: ${numeric(period?.elapsedDays)}`,
    `Remaining days: ${numeric(period?.remainingDays)}`,
    `Target: ${targetAmount.toFixed(2)}`,
    `Target source: ${targetSource}`,
    `Target doc: ${targetDocPath}`,
    `Target persistence: ${targetPersistenceState.status}`,
    `Actual to date: ${actual.toFixed(2)}`,
    `Future booked: ${futureBooked.toFixed(2)}`,
    `Booked floor: ${bookedFloor.toFixed(2)}`,
    `Run-rate forecast: ${runRate.toFixed(2)}`,
    `Forecast close: ${forecastClose.toFixed(2)}`,
    `Required/day: ${required.toFixed(2)}`,
    `Status: ${performance?.status || ""}`,
    "",
    ...runtimeChecks.map((row) =>
      `${row.pass ? "PASS" : "FAIL"} | ${row.label} | local=${row.left} | remote=${row.right}`
    ),
    "",
    `Result: ${data && runtimeFailures.length === 0 ? "PASS" : data ? "FAIL" : forecastState.status.toUpperCase()}`,
  ].join("\n");

  const copyRuntime = async () => {
    try {
      await navigator.clipboard.writeText(runtimeReport);
    } catch (_) {
      window.prompt("Copia el reporte A4.6.4:", runtimeReport);
    }
  };

  const narrative = targetAmount > 0
    ? performance?.status === "booked_to_target"
      ? `La meta ya está cubierta con lo realizado y la agenda actual.`
      : performance?.status === "on_track"
        ? `Al ritmo actual cerrarías cerca de ${forecastMoney(forecastClose)}, por encima de la meta de ${forecastMoney(targetAmount)}.`
        : performance?.status === "at_risk"
          ? `Al ritmo actual cerrarías cerca de ${forecastMoney(forecastClose)} vs una meta de ${forecastMoney(targetAmount)}. Necesitas ${forecastMoney(required)} por día restante después de considerar la agenda actual.`
          : performance?.status === "hit"
            ? `El período cerró por encima de la meta.`
            : performance?.status === "missed"
              ? `El período cerró por debajo de la meta.`
              : `Meta definida para el período.`
    : (period?.phase === "complete" ? `Período finalizado con ${forecastMoney(actual)} en ventas reconocidas.` : `Define una meta para convertir el ritmo actual y las reservas futuras en una lectura accionable.`);

  return (
    <article
      data-loop-forecast="a4-6-3"
      style={{
        gridColumn: "1 / -1",
        border: "1px solid #dbe7ea",
        borderRadius: 16,
        background: "#fff",
        padding: 18,
        minWidth: 0,
      }}
    >
      <style>{`
        .loop-forecast-kpis{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px}
        .loop-forecast-lower{display:grid;grid-template-columns:minmax(0,1.35fr) minmax(260px,.65fr);gap:12px}
        @media(max-width:980px){
          .loop-forecast-kpis{grid-template-columns:1fr 1fr}
          .loop-forecast-lower{grid-template-columns:1fr}
        }
        @media(max-width:620px){
          .loop-forecast-kpis{grid-template-columns:1fr}
        }
      `}</style>

      <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div>
          <span style={{ display: "block", color: "#0f766e", fontSize: 10, fontWeight: 900, letterSpacing: ".09em", textTransform: "uppercase" }}>
            Planeación · Metas
          </span>
          <h4 style={{ margin: "5px 0 0", color: "#0f172a", fontSize: 19 }}>{period?.phase === "complete" ? "Meta y resultado del período" : "Meta y cierre esperado"}</h4>
          <p style={{ margin: "5px 0 0", color: "#64748b", fontSize: 12, maxWidth: 720 }}>
            {period?.phase === "complete" ? "Resume el resultado real del período y su cumplimiento frente a la meta." : "Combina ventas reconocidas, reservas futuras y ritmo del período para anticipar el cierre."}
          </p>
        </div>

        <details><summary style={{cursor:"pointer",color:"#0f766e",fontSize:13,padding:8}}>Editar meta</summary>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          <label style={{ display: "grid", gap: 4, color: "#64748b", fontSize: 9, fontWeight: 800 }}>
            META DEL PERÍODO
            <input
              type="number"
              min="0"
              step="1"
              value={draftTarget}
              disabled={!targetScopeAll || targetPersistenceState.status === "saving"}
              onChange={(event) => setDraftTarget(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") applyTarget();
              }}
              placeholder="Ej. 12000"
              style={{ width: 150, minHeight: 34, border: "1px solid #dbe4e8", borderRadius: 9, padding: "0 9px", fontSize: 12 }}
            />
          </label>
          <button
            type="button"
            onClick={applyTarget}
            disabled={!targetScopeAll || targetPersistenceState.status === "saving"}
            style={{ minHeight: 34, borderRadius: 9, border: "1px solid #0f766e", background: "#0f766e", color: "#fff", padding: "0 11px", fontSize: 11, fontWeight: 800 }}
          >
            {targetPersistenceState.status === "saving" ? "Guardando…" : "Aplicar meta"}
          </button>
          {appliedTarget > 0 && targetScopeAll ? (
            <button
              type="button"
              onClick={clearTarget}
              disabled={targetPersistenceState.status === "saving"}
              style={{ minHeight: 34, borderRadius: 9, border: "1px solid #dbe4e8", background: "#fff", color: "#475569", padding: "0 10px", fontSize: 10 }}
            >
              Quitar
            </button>
          ) : null}
        </div>
        </details>
      </div>

      {!targetScopeAll ? (
        <div style={{ marginTop: 12, border: "1px solid #e2e8f0", borderRadius: 11, padding: "9px 11px", color: "#64748b", fontSize: 10, background: "#f8fafc" }}>
          La meta v1 es del negocio completo. Con filtro de trabajador mostramos una proyección operativa, pero no aplicamos la meta total del negocio.
        </div>
      ) : (
        <div
          style={{
            marginTop: 8,
            color: targetPersistenceState.status === "error" ? "#b91c1c" : targetSource === "firestore" ? "#047857" : "#64748b",
            fontSize: 9,
          }}
        >
          {targetPersistenceState.message || (
            targetSource === "firestore"
              ? "Meta sincronizada con Loop."
              : "La meta se guarda en Loop para dueños y administradores y se comparte entre dispositivos."
          )}
          {targetPersistenceState.error ? ` · ${targetPersistenceState.error}` : ""}
        </div>
      )}

      {forecastState.status === "loading" && !data ? (
        <div style={{ marginTop: 18, color: "#64748b", fontSize: 11 }}>Calculando proyección…</div>
      ) : forecastState.status === "error" ? (
        <div style={{ marginTop: 18, color: "#991b1b", fontSize: 11 }}>{forecastState.error}</div>
      ) : data ? (
        <ForecastOverview data={data} />
      ) : (
        <div style={{ marginTop: 18, color: "#64748b", fontSize: 11 }}>
          Preparando proyección…
        </div>
      )}

      <div style={{ marginTop: 14, borderTop: "1px solid #eef2f7", paddingTop: 10, display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ color: "#94a3b8", fontSize: 9 }}>
          {period?.phase === "complete" ? "El período ya cerró: las reservas futuras no modifican este resultado." : "Las reservas futuras son una base agendada, no ingreso garantizado. La proyección combina lo realizado con el resto del período."}
        </span>
      </div>
    </article>
  );
}


const CLOSEOUT_MONEY = new Intl.NumberFormat("es-PA", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const CLOSEOUT_INPUT_METHODS = [
  { key: "cash", label: "Efectivo" },
  { key: "yappy", label: "Yappy" },
  { key: "card", label: "Tarjeta" },
  { key: "transfer", label: "Transferencia" },
  { key: "other", label: "Otro" },
];

function closeoutStatusLabel(status) {
  const labels = {
    no_activity: "Sin actividad",
    attention: "Revisar",
    variance: "Con diferencia",
    open_receivables: "Con saldos por cobrar",
    ready_to_close: "Listo para confirmar",
    balanced: "Cuadrado",
  };
  return labels[String(status || "")] || String(status || "Sin estado");
}

function closeoutChecklistTone(status) {
  if (status === "ok") return { text: "Listo", color: "#047857", background: "#ecfdf5" };
  if (status === "attention") return { text: "Revisar", color: "#b45309", background: "#fff7ed" };
  if (status === "open") return { text: "Pendiente", color: "#b45309", background: "#fff7ed" };
  if (status === "pending") return { text: "Confirmar", color: "#475569", background: "#f8fafc" };
  return { text: "Info", color: "#475569", background: "#f8fafc" };
}

export function FinanceCloseoutPanel({
  remote,
  businessId = "",
  db,
  auth,
  period = "day",
  periodLabel = "",
  workerId = "all",
}) {
 const region=useBusinessRegion();
 const PROFITABILITY_MONEY=useRegionMoney();
 const CLOSEOUT_MONEY=PROFITABILITY_MONEY;
 const agingMoney=value=>PROFITABILITY_MONEY.format(numeric(value));
 const forecastMoney=agingMoney;

  const [state, setState] = useState({
    status: "idle",
    data: null,
    error: "",
    actualConfirmed: false,
  });
  const [actualDraft, setActualDraft] = useState({});
  const [persistenceState, setPersistenceState] = useState({
    status: "idle",
    record: null,
    source: "none",
    error: "",
    message: "",
  });

  const rangeKey = `${String(remote?.currentRangeFrom || "")}|${String(remote?.currentRangeTo || "")}|${String(workerId || "all")}`;
  const businessScope = String(workerId || "all") === "all";
  const officialDailyScope = period === "day" && businessScope;
  const closeoutDayKey = String(remote?.currentRangeFrom || "").slice(0, 10);
  const closeoutDocId = closeoutDayKey ? `daily_${closeoutDayKey}` : "";
  const closeoutDocPath = businessId && closeoutDocId
    ? `businessFinance/${businessId}/closeouts/${closeoutDocId}`
    : "";

  useEffect(() => {
    let cancelled = false;

    const loadCloseout = async () => {
      if (!remote?.rangeReady || !remote?.currentRangeFrom || !remote?.currentRangeTo || typeof remote?.runCloseoutQuery !== "function") {
        setState({ status: "idle", data: null, error: "", actualConfirmed: false });
        setActualDraft({});
        setPersistenceState({ status: "idle", record: null, source: "none", error: "", message: "" });
        return;
      }

      setState({ status: "loading", data: null, error: "", actualConfirmed: false });
      setActualDraft({});
      setPersistenceState({
        status: officialDailyScope ? "loading" : "idle",
        record: null,
        source: "none",
        error: "",
        message: officialDailyScope ? "Buscando cierre guardado…" : "",
      });

      try {
        const liveData = await remote.runCloseoutQuery({ actualByMethod: null });
        if (cancelled) return;

        if (!officialDailyScope || !db || !closeoutDocPath) {
          setState({ status: "ready", data: liveData, error: "", actualConfirmed: false });
          setPersistenceState({
            status: "ready",
            record: null,
            source: "not_applicable",
            error: "",
            message: businessScope
              ? "Para guardar un cierre oficial, selecciona la vista Día."
              : "El cierre oficial se guarda a nivel negocio, no por trabajador.",
          });
          return;
        }

        const snap = await getDoc(doc(db, closeoutDocPath));
        if (cancelled) return;

        if (!snap.exists()) {
          setState({ status: "ready", data: liveData, error: "", actualConfirmed: false });
          setPersistenceState({
            status: "ready",
            record: null,
            source: "none",
            error: "",
            message: "Todavía no hay un cierre oficial guardado para este día.",
          });
          return;
        }

        const record = snap.data() || {};
        const cents = record?.actualByMethodCents || {};
        const actualByMethod = {};
        for (const method of CLOSEOUT_INPUT_METHODS) {
          actualByMethod[method.key] = numeric(cents?.[method.key]) / 100;
        }

        const dataWithActual = await remote.runCloseoutQuery({ actualByMethod });
        if (cancelled) return;

        const nextDraft = {};
        for (const method of CLOSEOUT_INPUT_METHODS) {
          nextDraft[method.key] = numeric(actualByMethod[method.key]).toFixed(2);
        }

        setActualDraft(nextDraft);
        setState({ status: "ready", data: dataWithActual, error: "", actualConfirmed: true });
        setPersistenceState({
          status: "ready",
          record,
          source: "firestore",
          error: "",
          message: `Cierre guardado en Loop · revisión ${numeric(record?.revision) || 1}.`,
        });
      } catch (error) {
        if (cancelled) return;
        setState({
          status: "error",
          data: null,
          error: `${error?.code || "error"}: ${error?.message || String(error)}`,
          actualConfirmed: false,
        });
        setPersistenceState({
          status: "error",
          record: null,
          source: "error",
          error: `${error?.code || "error"}: ${error?.message || String(error)}`,
          message: "No pudimos cargar el cierre central.",
        });
      }
    };

    loadCloseout();

    return () => {
      cancelled = true;
    };
  }, [
    remote?.rangeReady,
    remote?.currentRangeFrom,
    remote?.currentRangeTo,
    rangeKey,
    officialDailyScope,
    businessScope,
    db,
    closeoutDocPath,
  ]);

  const data = state.data || null;
  const summary = data?.summary || {};
  const methods = Array.isArray(data?.methods) ? data.methods : [];
  const checklist = Array.isArray(data?.checklist) ? data.checklist : [];
  const days = Array.isArray(data?.days) ? data.days : [];

  const methodMap = new Map(methods.map((row) => [String(row?.method || ""), row]));
  const visibleMethods = CLOSEOUT_INPUT_METHODS.map((base) => ({
    ...base,
    systemAmount: numeric(methodMap.get(base.key)?.systemAmount),
    actualAmount: methodMap.get(base.key)?.actualAmount ?? null,
    variance: methodMap.get(base.key)?.variance ?? null,
  }));

  const systemInflow = numeric(summary?.systemRecordedInflow);
  const declaredActual = summary?.declaredActualInflow == null ? null : numeric(summary?.declaredActualInflow);
  const declaredVariance = summary?.declaredVariance == null ? null : numeric(summary?.declaredVariance);
  const paidOpex = numeric(summary?.paidOperatingExpenses);
  const pending = numeric(summary?.pendingReceivables);
  const netKnown = numeric(summary?.netKnownCash);
  const declaredNet = summary?.declaredNetKnownCash == null ? null : numeric(summary?.declaredNetKnownCash);
  const actualPresent = declaredActual !== null;

  const fillFromSystem = () => {
    const next = {};
    for (const row of visibleMethods) next[row.key] = numeric(row.systemAmount).toFixed(2);
    setActualDraft(next);
  };

  const clearActual = () => {
    const next = {};
    for (const row of visibleMethods) next[row.key] = "";
    setActualDraft(next);
  };

  const applyActual = async () => {
    if (state.status === "loading" || typeof remote?.runCloseoutQuery !== "function") return;

    const actualByMethod = {};
    let hasValue = false;
    for (const row of visibleMethods) {
      const raw = String(actualDraft[row.key] ?? "").trim().replace(",", ".");
      if (!raw) continue;
      const amount = Number(raw);
      if (!Number.isFinite(amount) || amount < 0) {
        setState((previous) => ({
          ...previous,
          status: "error",
          error: `Monto inválido en ${row.label}.`,
        }));
        return;
      }
      actualByMethod[row.key] = Math.round(amount * 100) / 100;
      hasValue = true;
    }

    if (!hasValue) {
      setState((previous) => ({
        ...previous,
        status: "error",
        error: "Ingresa o copia al menos un monto real antes de confirmar.",
      }));
      return;
    }

    setState((previous) => ({ ...previous, status: "loading", error: "" }));
    try {
      const next = await remote.runCloseoutQuery({ actualByMethod });
      setState({
        status: "ready",
        data: next,
        error: "",
        actualConfirmed: true,
      });
    } catch (error) {
      setState((previous) => ({
        ...previous,
        status: "error",
        error: `${error?.code || "error"}: ${error?.message || String(error)}`,
      }));
    }
  };

  const saveOfficialCloseout = async () => {
    if (!officialDailyScope || !db || !closeoutDocPath || !data || !actualPresent) {
      setPersistenceState((previous) => ({
        ...previous,
        status: "error",
        error: "closeout_not_ready",
        message: officialDailyScope
          ? "Confirma primero el cobro real antes de guardar."
          : "El cierre oficial solo se guarda en vista Día y a nivel negocio.",
      }));
      return;
    }

    const uid = String(auth?.currentUser?.uid || "").trim();
    if (!uid) {
      setPersistenceState((previous) => ({
        ...previous,
        status: "error",
        error: "auth_required",
        message: "Necesitas una sesión autenticada para guardar el cierre.",
      }));
      return;
    }

    setPersistenceState((previous) => ({
      ...previous,
      status: "saving",
      error: "",
      message: "Guardando cierre oficial…",
    }));

    try {
      const ref = doc(db, closeoutDocPath);
      const snap = await getDoc(ref);
      const existing = snap.exists() ? (snap.data() || {}) : {};
      const now = new Date().toISOString();
      const revision = Math.max(0, Math.trunc(numeric(existing?.revision))) + 1;

      const systemByMethodCents = {};
      const actualByMethodCents = {};
      for (const method of CLOSEOUT_INPUT_METHODS) {
        const row = methodMap.get(method.key) || {};
        systemByMethodCents[method.key] = Math.round(numeric(row?.systemAmount) * 100);
        actualByMethodCents[method.key] = Math.round(numeric(row?.actualAmount) * 100);
      }

      const payload = {
        schemaVersion: 1,
        businessId: String(businessId),
        dayKey: closeoutDayKey,
        periodStartIso: String(data?.period?.from || remote?.currentRangeFrom || ""),
        periodEndIso: String(data?.period?.toExclusive || remote?.currentRangeTo || ""),
        currency: region.currency || "USD",
        closeoutVersion: String(data?.closeoutVersion || CLOSEOUT_VERSION),
        closeoutSemanticsVersion: String(data?.closeoutSemanticsVersion || CLOSEOUT_SEMANTICS_VERSION),
        queryMode: String(data?.queryMode || CLOSEOUT_QUERY_MODE),
        status: String(data?.status || ""),
        actualByMethodCents,
        systemByMethodCents,
        recognizedRevenueCents: Math.round(numeric(summary?.recognizedRevenue) * 100),
        systemInflowCents: Math.round(systemInflow * 100),
        declaredActualCents: Math.round(numeric(declaredActual) * 100),
        varianceCents: Math.round(numeric(declaredVariance) * 100),
        pendingReceivablesCents: Math.round(pending * 100),
        paidOpexCents: Math.round(paidOpex * 100),
        netKnownCashCents: Math.round(netKnown * 100),
        declaredNetCashCents: Math.round(numeric(declaredNet) * 100),
        revision,
        source: "reports_v3_a4_7_4",
        createdAt: String(existing?.createdAt || now),
        updatedAt: now,
        createdByUid: String(existing?.createdByUid || uid),
        updatedByUid: uid,
      };

      await setDoc(ref, payload, { merge: false });

      setPersistenceState({
        status: "ready",
        record: payload,
        source: "firestore",
        error: "",
        message: `Cierre guardado en Loop · revisión ${revision}.`,
      });
    } catch (error) {
      setPersistenceState((previous) => ({
        ...previous,
        status: "error",
        error: `${error?.code || "error"}: ${error?.message || String(error)}`,
        message: "No pudimos guardar el cierre oficial.",
      }));
    }
  };

  const systemMethodSum = methods.reduce((sum, row) => sum + numeric(row?.systemAmount), 0);
  const actualMethodSum = methods.reduce((sum, row) => sum + numeric(row?.actualAmount), 0);
  const dailyRevenue = days.reduce((sum, row) => sum + numeric(row?.summary?.recognizedRevenue), 0);
  const dailyInflow = days.reduce((sum, row) => sum + numeric(row?.summary?.systemRecordedInflow), 0);
  const dailyPending = days.reduce((sum, row) => sum + numeric(row?.summary?.pendingReceivables), 0);
  const dailyPaidOpex = days.reduce((sum, row) => sum + numeric(row?.summary?.paidOperatingExpenses), 0);

  const savedRecord = persistenceState?.record || null;
  const savedActualByMethod = savedRecord?.actualByMethodCents || {};
  const savedSystemByMethod = savedRecord?.systemByMethodCents || {};
  const currentActualCentsByMethod = {};
  const currentSystemCentsByMethod = {};
  for (const method of CLOSEOUT_INPUT_METHODS) {
    const row = methodMap.get(method.key) || {};
    currentActualCentsByMethod[method.key] = Math.round(numeric(row?.actualAmount) * 100);
    currentSystemCentsByMethod[method.key] = Math.round(numeric(row?.systemAmount) * 100);
  }

  const runtimeChecks = data ? [
    {
      label: "status ready",
      pass: state.status === "ready",
      left: "ready",
      right: state.status,
    },
    {
      label: "queryMode",
      pass: data?.queryMode === CLOSEOUT_QUERY_MODE,
      left: CLOSEOUT_QUERY_MODE,
      right: String(data?.queryMode || ""),
    },
    {
      label: "version",
      pass: data?.closeoutVersion === CLOSEOUT_VERSION,
      left: CLOSEOUT_VERSION,
      right: String(data?.closeoutVersion || ""),
    },
    {
      label: "semantics",
      pass: data?.closeoutSemanticsVersion === CLOSEOUT_SEMANTICS_VERSION,
      left: CLOSEOUT_SEMANTICS_VERSION,
      right: String(data?.closeoutSemanticsVersion || ""),
    },
    {
      label: "system methods",
      pass: moneyEqual(systemMethodSum, systemInflow),
      left: systemMethodSum.toFixed(2),
      right: systemInflow.toFixed(2),
    },
    {
      label: "manual actual confirmed",
      pass: actualPresent,
      left: "present",
      right: actualPresent ? declaredActual.toFixed(2) : "null",
    },
    {
      label: "actual methods",
      pass: actualPresent && moneyEqual(actualMethodSum, declaredActual),
      left: actualMethodSum.toFixed(2),
      right: actualPresent ? declaredActual.toFixed(2) : "null",
    },
    {
      label: "variance arithmetic",
      pass: actualPresent && moneyEqual(declaredActual - systemInflow, declaredVariance),
      left: actualPresent ? (declaredActual - systemInflow).toFixed(2) : "n/a",
      right: declaredVariance == null ? "null" : declaredVariance.toFixed(2),
    },
    {
      label: "net known cash",
      pass: moneyEqual(systemInflow - paidOpex, netKnown),
      left: (systemInflow - paidOpex).toFixed(2),
      right: netKnown.toFixed(2),
    },
    {
      label: "declared net cash",
      pass: actualPresent && moneyEqual(declaredActual - paidOpex, declaredNet),
      left: actualPresent ? (declaredActual - paidOpex).toFixed(2) : "n/a",
      right: declaredNet == null ? "null" : declaredNet.toFixed(2),
    },
    {
      label: "daily revenue reconciliation",
      pass: moneyEqual(dailyRevenue, summary?.recognizedRevenue),
      left: dailyRevenue.toFixed(2),
      right: numeric(summary?.recognizedRevenue).toFixed(2),
    },
    {
      label: "daily inflow reconciliation",
      pass: moneyEqual(dailyInflow, systemInflow),
      left: dailyInflow.toFixed(2),
      right: systemInflow.toFixed(2),
    },
    {
      label: "daily pending reconciliation",
      pass: moneyEqual(dailyPending, pending),
      left: dailyPending.toFixed(2),
      right: pending.toFixed(2),
    },
    {
      label: "daily OPEX reconciliation",
      pass: moneyEqual(dailyPaidOpex, paidOpex),
      left: dailyPaidOpex.toFixed(2),
      right: paidOpex.toFixed(2),
    },
    {
      label: "checklist",
      pass: checklist.length === 5,
      left: "5",
      right: String(checklist.length),
    },
    {
      label: "no bank reconciliation claim",
      pass: data?.policy?.bankReconciliation === "not_claimed_without_external_bank_or_processor_settlement_data",
      left: "not_claimed_without_external_bank_or_processor_settlement_data",
      right: String(data?.policy?.bankReconciliation || ""),
    },
    {
      label: "official daily scope",
      pass: officialDailyScope,
      left: "day + worker=all",
      right: `${period} + ${String(workerId || "all")}`,
    },
    {
      label: "central closeout persistence",
      pass: persistenceState?.source === "firestore" && numeric(savedRecord?.revision) >= 1,
      left: "firestore revision>=1",
      right: `${persistenceState?.source || "none"} revision=${numeric(savedRecord?.revision)}`,
    },
    {
      label: "closeout day identity",
      pass: String(savedRecord?.dayKey || "") === closeoutDayKey &&
        String(savedRecord?.periodStartIso || "") === String(data?.period?.from || ""),
      left: `${closeoutDayKey}|${data?.period?.from || ""}`,
      right: `${savedRecord?.dayKey || ""}|${savedRecord?.periodStartIso || ""}`,
    },
    {
      label: "saved actual methods snapshot",
      pass: CLOSEOUT_INPUT_METHODS.every((method) =>
        numeric(savedActualByMethod?.[method.key]) === numeric(currentActualCentsByMethod?.[method.key])
      ),
      left: JSON.stringify(currentActualCentsByMethod),
      right: JSON.stringify(savedActualByMethod || {}),
    },
    {
      label: "saved system methods snapshot",
      pass: CLOSEOUT_INPUT_METHODS.every((method) =>
        numeric(savedSystemByMethod?.[method.key]) === numeric(currentSystemCentsByMethod?.[method.key])
      ),
      left: JSON.stringify(currentSystemCentsByMethod),
      right: JSON.stringify(savedSystemByMethod || {}),
    },
    {
      label: "saved summary snapshot",
      pass: numeric(savedRecord?.systemInflowCents) === Math.round(systemInflow * 100) &&
        numeric(savedRecord?.declaredActualCents) === Math.round(numeric(declaredActual) * 100) &&
        numeric(savedRecord?.varianceCents) === Math.round(numeric(declaredVariance) * 100) &&
        numeric(savedRecord?.pendingReceivablesCents) === Math.round(pending * 100) &&
        numeric(savedRecord?.paidOpexCents) === Math.round(paidOpex * 100),
      left: `${Math.round(systemInflow * 100)}|${Math.round(numeric(declaredActual) * 100)}|${Math.round(numeric(declaredVariance) * 100)}|${Math.round(pending * 100)}|${Math.round(paidOpex * 100)}`,
      right: `${numeric(savedRecord?.systemInflowCents)}|${numeric(savedRecord?.declaredActualCents)}|${numeric(savedRecord?.varianceCents)}|${numeric(savedRecord?.pendingReceivablesCents)}|${numeric(savedRecord?.paidOpexCents)}`,
    },
  ] : [];

  const runtimeFailures = runtimeChecks.filter((row) => !row.pass);
  const runtimeReport = [
    "LOOP A4.7.4 CENTRAL DAILY CLOSEOUT RUNTIME",
    `Period: ${periodLabel || "n/a"}`,
    `Worker: ${String(workerId || "all")}`,
    `UI status: ${state.status}`,
    `Query mode: ${data?.queryMode || ""}`,
    `Version: ${data?.closeoutVersion || ""}`,
    `Semantics: ${data?.closeoutSemanticsVersion || ""}`,
    `From: ${data?.period?.from || ""}`,
    `ToExclusive: ${data?.period?.toExclusive || ""}`,
    `Status: ${data?.status || ""}`,
    `Persistence source: ${persistenceState?.source || "none"}`,
    `Persistence status: ${persistenceState?.status || ""}`,
    `Closeout doc: ${closeoutDocPath}`,
    `Revision: ${numeric(savedRecord?.revision)}`,
    `Recognized revenue: ${numeric(summary?.recognizedRevenue).toFixed(2)}`,
    `System inflow: ${systemInflow.toFixed(2)}`,
    `Declared actual: ${declaredActual == null ? "null" : declaredActual.toFixed(2)}`,
    `Variance: ${declaredVariance == null ? "null" : declaredVariance.toFixed(2)}`,
    `Pending receivables: ${pending.toFixed(2)}`,
    `Paid OPEX: ${paidOpex.toFixed(2)}`,
    `Net known cash: ${netKnown.toFixed(2)}`,
    `Declared net cash: ${declaredNet == null ? "null" : declaredNet.toFixed(2)}`,
    `Daily rows: ${days.length}`,
    "",
    ...methods.map((row) =>
      `METHOD | ${row?.method || ""} | system=${numeric(row?.systemAmount).toFixed(2)} | actual=${row?.actualAmount == null ? "null" : numeric(row.actualAmount).toFixed(2)} | variance=${row?.variance == null ? "null" : numeric(row.variance).toFixed(2)}`
    ),
    "",
    ...runtimeChecks.map((row) =>
      `${row.pass ? "PASS" : "FAIL"} | ${row.label} | local=${row.left} | remote=${row.right}`
    ),
    "",
    `Result: ${data && runtimeFailures.length === 0 ? "PASS" : data ? "FAIL" : state.status.toUpperCase()}`,
  ].join("\n");

  const copyRuntime = async () => {
    try {
      await navigator.clipboard.writeText(runtimeReport);
    } catch (_) {
      window.prompt("Copia el reporte A4.7.4:", runtimeReport);
    }
  };

  const statusColor = data?.status === "balanced"
    ? "#047857"
    : data?.status === "variance" || data?.status === "attention"
      ? "#b45309"
      : data?.status === "open_receivables"
        ? "#b45309"
        : "#475569";

  return (
    <article
      data-loop-closeout="a4-7-4"
      className="ba-panel"
      style={{
        gridColumn: "1 / -1",
        border: "1px solid #dbe7ea",
        borderRadius: 16,
        background: "#fff",
        padding: 18,
      }}
    >
      <style>{`
        .ba-closeout-kpis{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:10px}
        .ba-closeout-body{display:grid;grid-template-columns:minmax(0,1.35fr) minmax(280px,.65fr);gap:12px;margin-top:12px}
        .ba-closeout-method-grid{display:grid;grid-template-columns:1.1fr .8fr .8fr .8fr;gap:7px;align-items:center}
        .ba-closeout-days{display:grid;grid-template-columns:120px repeat(4,minmax(90px,1fr));gap:7px;align-items:center}
        @media(max-width:980px){
          .ba-closeout-kpis{grid-template-columns:repeat(2,minmax(0,1fr))}
          .ba-closeout-body{grid-template-columns:1fr}
        }
        @media(max-width:620px){
          .ba-closeout-kpis{grid-template-columns:1fr}
          .ba-closeout-method-grid{grid-template-columns:1fr 1fr}
          .ba-closeout-days{grid-template-columns:1fr 1fr}
          .ba-closeout-hide-mobile{display:none}
        }
      `}</style>

      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div>
          <span style={{ display: "block", fontSize: 10, fontWeight: 900, letterSpacing: ".08em", textTransform: "uppercase", color: "#0f766e" }}>
            Caja · cierre diario
          </span>
          <h3 style={{ margin: "5px 0 0", fontSize: 18, color: "#0f172a" }}>Qué entró y qué falta cerrar</h3>
          <p style={{ margin: "5px 0 0", color: "#64748b", fontSize: 11 }}>
            Los abonos de paquetes aparecen en la fecha del pago, aunque la cita sea futura. Las correcciones ajustan el registro; no son devoluciones de dinero.
          </p>
        </div>

        <div style={{ textAlign: "right" }}>
          <strong style={{ display: "block", color: statusColor, fontSize: 12 }}>
            {closeoutStatusLabel(data?.status)}
          </strong>
          <span style={{ display: "block", marginTop: 4, color: "#94a3b8", fontSize: 9 }}>
            {data?.period?.days ? `${data.period.days} día${data.period.days === 1 ? "" : "s"} en el rango` : "Preparando cierre…"}
          </span>
        </div>
      </div>

      {state.status === "error" ? (
        <div style={{ marginTop: 12, borderRadius: 10, background: "#fef2f2", color: "#b91c1c", padding: "9px 11px", fontSize: 11 }}>
          {state.error}
        </div>
      ) : null}

      {data ? (
        <>
          <div className="ba-closeout-kpis" style={{ marginTop: 14 }}>
            {[
              ["Cobro Loop", systemInflow, "Cobros registrados + propinas"],
              ["Cobro real", declaredActual, actualPresent ? "Conteo confirmado" : "Pendiente confirmar"],
              ["Diferencia", declaredVariance, actualPresent ? "Real − Loop" : "Sin conteo aún"],
              ["Gastos pagados", paidOpex, "Gastos operativos"],
              ["Caja neta conocida", actualPresent ? declaredNet : netKnown, actualPresent ? "Real − gastos pagados" : "Loop − gastos pagados"],
            ].map(([label, value, note]) => (
              <div key={label} style={{ border: "1px solid #e2e8f0", borderRadius: 12, padding: "11px 12px", background: "#fbfcfd" }}>
                <span style={{ display: "block", fontSize: 9, fontWeight: 800, color: "#64748b" }}>{label}</span>
                <strong style={{ display: "block", marginTop: 6, fontSize: 17, color: "#0f172a" }}>
                  {value == null ? "—" : CLOSEOUT_MONEY.format(numeric(value))}
                </strong>
                <span style={{ display: "block", marginTop: 4, fontSize: 9, color: "#94a3b8" }}>{note}</span>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 10, color: "#475569" }}>
              <strong style={{ color: "#0f172a" }}>{CLOSEOUT_MONEY.format(pending)}</strong> por cobrar
            </span>
            <span style={{ color: "#cbd5e1" }}>·</span>
            <span style={{ fontSize: 10, color: "#475569" }}>
              {CLOSEOUT_MONEY.format(numeric(summary?.recognizedRevenue))} ventas reconocidas
            </span>
            {numeric(summary?.paymentMismatch) > 0 ? (
              <>
                <span style={{ color: "#cbd5e1" }}>·</span>
                <span style={{ fontSize: 10, color: "#b45309" }}>
                  {CLOSEOUT_MONEY.format(numeric(summary.paymentMismatch))} con diferencia de pago
                </span>
              </>
            ) : null}
          </div>

          <div className="ba-closeout-body">
            <section style={{ border: "1px solid #e2e8f0", borderRadius: 12, padding: 13 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <div>
                  <strong style={{ display: "block", fontSize: 12, color: "#0f172a" }}>Confirmar cobro real por método</strong>
                  <span style={{ display: "block", marginTop: 3, color: "#64748b", fontSize: 9 }}>
                    Confirma el conteo primero. En vista Día puedes guardarlo como cierre oficial compartido en Loop.
                  </span>
                </div>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={fillFromSystem}
                    disabled={state.status === "loading"}
                    style={{ minHeight: 31, border: "1px solid #dbe4e8", borderRadius: 8, background: "#fff", padding: "0 9px", fontSize: 10 }}
                  >
                    Igualar a Loop
                  </button>
                  <button
                    type="button"
                    onClick={clearActual}
                    disabled={state.status === "loading"}
                    style={{ minHeight: 31, border: "1px solid #dbe4e8", borderRadius: 8, background: "#fff", padding: "0 9px", fontSize: 10 }}
                  >
                    Limpiar
                  </button>
                  <button
                    type="button"
                    onClick={applyActual}
                    disabled={state.status === "loading"}
                    style={{ minHeight: 31, border: "1px solid #0f766e", borderRadius: 8, background: "#0f766e", color: "#fff", padding: "0 10px", fontSize: 10, fontWeight: 800 }}
                  >
                    {state.status === "loading" ? "Calculando…" : "Confirmar conteo"}
                  </button>
                </div>
              </div>

              <div className="ba-closeout-method-grid" style={{ marginTop: 12 }}>
                <strong style={{ fontSize: 9, color: "#64748b" }}>Método</strong>
                <strong style={{ fontSize: 9, color: "#64748b", textAlign: "right" }}>Loop</strong>
                <strong style={{ fontSize: 9, color: "#64748b", textAlign: "right" }}>Real</strong>
                <strong style={{ fontSize: 9, color: "#64748b", textAlign: "right" }}>Diferencia</strong>

                {visibleMethods.map((row) => {
                  const resultRow = methodMap.get(row.key) || null;
                  const variance = resultRow?.variance;
                  return (
                    <div className="finance-closeout-method-card" key={row.key}>
                      <span style={{ fontSize: 10, color: "#334155", fontWeight: 700 }}>{row.label}</span>
                      <span data-label="Registrado en Loop" style={{ fontSize: 10, color: "#475569", textAlign: "right" }}>{CLOSEOUT_MONEY.format(row.systemAmount)}</span>
                      <label className="finance-closeout-actual"><span>Conteo real</span><input
                        aria-label={`Conteo real: ${row.label}`}
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        value={actualDraft[row.key] ?? (resultRow?.actualAmount == null ? "" : String(resultRow.actualAmount))}
                        onChange={(event) => setActualDraft((previous) => ({ ...previous, [row.key]: event.target.value }))}
                        disabled={state.status === "loading"}
                        placeholder="0.00"
                        style={{ width: "100%", minHeight: 31, border: "1px solid #dbe4e8", borderRadius: 8, padding: "0 8px", textAlign: "right", fontSize: 10 }}
                      /></label>
                      <span data-label="Diferencia" style={{ fontSize: 10, color: variance == null ? "#94a3b8" : Math.abs(numeric(variance)) <= 0.01 ? "#047857" : "#b45309", textAlign: "right", fontWeight: 700 }}>
                        {variance == null ? "—" : `${numeric(variance) >= 0 ? "+" : ""}${CLOSEOUT_MONEY.format(numeric(variance))}`}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div style={{ marginTop: 12, borderTop: "1px solid #eef2f7", paddingTop: 10, display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <div>
                  <span style={{ display: "block", fontSize: 9, color: persistenceState.status === "error" ? "#b91c1c" : persistenceState.source === "firestore" ? "#047857" : "#64748b" }}>
                    {persistenceState.message || (
                      officialDailyScope
                        ? "Confirma el conteo para guardar el cierre oficial."
                        : businessScope
                          ? "Para guardar un cierre oficial, selecciona la vista Día."
                          : "El cierre oficial se guarda a nivel negocio."
                    )}
                  </span>
                  {persistenceState.error ? (
                    <span style={{ display: "block", marginTop: 3, fontSize: 9, color: "#b91c1c" }}>
                      {persistenceState.error}
                    </span>
                  ) : null}
                </div>
                {officialDailyScope ? (
                  <button
                    type="button"
                    onClick={saveOfficialCloseout}
                    disabled={!actualPresent || persistenceState.status === "saving" || state.status === "loading"}
                    style={{ minHeight: 31, border: "1px solid #0f766e", borderRadius: 8, background: actualPresent ? "#0f766e" : "#f8fafc", color: actualPresent ? "#fff" : "#94a3b8", padding: "0 10px", fontSize: 10, fontWeight: 800 }}
                  >
                    {persistenceState.status === "saving"
                      ? "Guardando…"
                      : persistenceState.source === "firestore"
                        ? "Actualizar cierre oficial"
                        : "Guardar cierre oficial"}
                  </button>
                ) : null}
              </div>
            </section>

            <section style={{ border: "1px solid #e2e8f0", borderRadius: 12, padding: 13 }}>
              <strong style={{ display: "block", fontSize: 12, color: "#0f172a" }}>Checklist de cierre</strong>
              <div style={{ marginTop: 9, display: "grid", gap: 7 }}>
                {checklist.map((row) => {
                  const tone = closeoutChecklistTone(row?.status);
                  return (
                    <div key={row?.key} style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                      <div>
                        <span style={{ display: "block", fontSize: 10, color: "#334155" }}>{row?.label || row?.key}</span>
                        {row?.amount != null ? (
                          <span style={{ display: "block", marginTop: 2, fontSize: 9, color: "#94a3b8" }}>
                            {CLOSEOUT_MONEY.format(numeric(row.amount))}
                          </span>
                        ) : null}
                      </div>
                      <span style={{ borderRadius: 999, padding: "4px 7px", fontSize: 9, fontWeight: 800, color: tone.color, background: tone.background }}>
                        {tone.text}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>

          {period !== "day" ? (
          <section style={{ marginTop: 12, border: "1px solid #e2e8f0", borderRadius: 12, padding: 13, overflowX: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <strong style={{ fontSize: 12, color: "#0f172a" }}>Cierre por día</strong>
              <span style={{ fontSize: 9, color: "#94a3b8" }}>
                Los días muestran el estado del sistema; el conteo manual de A4.7.3 se aplica al rango seleccionado.
              </span>
            </div>

            <div className="ba-closeout-days" style={{ marginTop: 10, minWidth: 540 }}>
              <strong style={{ fontSize: 9, color: "#64748b" }}>Día</strong>
              <strong style={{ fontSize: 9, color: "#64748b", textAlign: "right" }}>Ventas</strong>
              <strong style={{ fontSize: 9, color: "#64748b", textAlign: "right" }}>Cobrado</strong>
              <strong style={{ fontSize: 9, color: "#64748b", textAlign: "right" }}>Por cobrar</strong>
              <strong style={{ fontSize: 9, color: "#64748b", textAlign: "right" }}>Gastos pagados</strong>

              {days.map((row) => (
                <div className="finance-closeout-day-card" key={row?.dayKey}>
                  <span style={{ fontSize: 10, color: "#334155", fontWeight: 700 }}>
                    {String(row?.dayKey || "")}
                    {row?.partial ? <small style={{ display: "block", color: "#94a3b8", fontWeight: 500 }}>parcial</small> : null}
                  </span>
                  <span data-label="Ventas" style={{ fontSize: 10, color: "#475569", textAlign: "right" }}>{CLOSEOUT_MONEY.format(numeric(row?.summary?.recognizedRevenue))}</span>
                  <span data-label="Cobrado" style={{ fontSize: 10, color: "#475569", textAlign: "right" }}>{CLOSEOUT_MONEY.format(numeric(row?.summary?.systemRecordedInflow))}</span>
                  <span data-label="Por cobrar" style={{ fontSize: 10, color: numeric(row?.summary?.pendingReceivables) > 0 ? "#b45309" : "#475569", textAlign: "right", fontWeight: numeric(row?.summary?.pendingReceivables) > 0 ? 800 : 500 }}>
                    {CLOSEOUT_MONEY.format(numeric(row?.summary?.pendingReceivables))}
                  </span>
                  <span data-label="Gastos pagados" style={{ fontSize: 10, color: "#475569", textAlign: "right" }}>{CLOSEOUT_MONEY.format(numeric(row?.summary?.paidOperatingExpenses))}</span>
                </div>
              ))}
            </div>
          </section>
        ) : null}
        </>
      ) : (
        <div style={{ marginTop: 14, color: "#64748b", fontSize: 11 }}>
          {state.status === "loading" ? "Preparando cierre de caja…" : "Esperando datos de Finance…"}
        </div>
      )}

      <div style={{ marginTop: 13, borderTop: "1px solid #eef2f7", paddingTop: 10, display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ color: "#94a3b8", fontSize: 9 }}>
          No es conciliación bancaria. Pagos al equipo y costos sin fecha real de pago no se descuentan como salida de caja.
        </span>

      </div>
    </article>
  );
}

const FINANCE_WORKSPACE_VIEWS = [
  {
    key: "profitability",
    label: "Rentabilidad",
    shortLabel: "Rentabilidad",
    eyebrow: "Decisión",
    description: "Qué deja dinero: contribución, margen, servicios y equipo.",
  },
  {
    key: "cash",
    label: "Caja y cobranza",
    shortLabel: "Caja",
    eyebrow: "Liquidez",
    description: "Qué entró, cierre por método, qué falta cobrar y dónde hay diferencias.",
  },
  {
    key: "pnl",
    label: "Estado de resultados",
    shortLabel: "Estado de resultados",
    eyebrow: "Resultado",
    description: "Ventas, costos directos, gastos operativos y utilidad del negocio.",
  },
  {
    key: "trends",
    label: "Tendencias",
    shortLabel: "Tendencias",
    eyebrow: "Evolución",
    description: "Cómo se movieron ventas, cobros y resultado en el período.",
  },
  {
    key: "forecast",
    label: "Metas y proyección",
    shortLabel: "Forecast",
    eyebrow: "Planeación",
    description: "Meta, ritmo requerido, agenda futura y cierre esperado.",
  },
  {
    key: "team",
    label: "Servicios y equipo",
    shortLabel: "Servicios y equipo",
    eyebrow: "Desempeño",
    description: "Ranking de ventas y detalle operativo por servicio y trabajador.",
  },
];

function financeWorkspaceIsVisible(selector) {
  try {
    const node = document.querySelector(selector);
    if (!node) return false;
    const style = window.getComputedStyle(node);
    return style.display !== "none" && style.visibility !== "hidden";
  } catch (_) {
    return false;
  }
}

// LOOP REPORTS V3 A4.8.9 — final workspace polish
export function FinanceWorkspaceNav({ value = "profitability", onChange }) {
  const safeValue = FINANCE_WORKSPACE_VIEWS.some((row) => row.key === value)
    ? value
    : "profitability";
  const current = FINANCE_WORKSPACE_VIEWS.find((row) => row.key === safeValue) || FINANCE_WORKSPACE_VIEWS[0];
  const [visited, setVisited] = useState(() => [safeValue]);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setVisited((previous) => previous.includes(safeValue) ? previous : [...previous, safeValue]);
  }, [safeValue]);

  const selectView = (next) => {
    const normalized = FINANCE_WORKSPACE_VIEWS.some((row) => row.key === next)
      ? next
      : "profitability";
    setMenuOpen(false);
    if (typeof onChange === "function") onChange(normalized);
  };

  const copyWorkspaceReport = async () => {
    const visibility = {
      profitability: financeWorkspaceIsVisible('[data-loop-profitability="a4-4-3"]'),
      cash: financeWorkspaceIsVisible(".ba-finance-v3-collections"),
      pnl: financeWorkspaceIsVisible(".ba-finance-v3-pnl"),
      trends: financeWorkspaceIsVisible(".ba-finance-v3-trend"),
      forecast: financeWorkspaceIsVisible('[data-loop-forecast="a4-6-3"]'),
      team: financeWorkspaceIsVisible(".ba-finance-v3-performance-grid"),
      teamDetail: financeWorkspaceIsVisible(".ba-finance-v3-worker-detail"),
    };
    const expectedCurrentVisible = safeValue === "team"
      ? visibility.team && visibility.teamDetail
      : Boolean(visibility[safeValue]);
    const otherCoreVisible = FINANCE_WORKSPACE_VIEWS
      .filter((row) => row.key !== safeValue)
      .some((row) => Boolean(visibility[row.key]));
    const allVisited = FINANCE_WORKSPACE_VIEWS.every((row) => visited.includes(row.key));
    const pass = expectedCurrentVisible && !otherCoreVisible && allVisited;

    const report = [
      "LOOP A4.8.2 FINANCE VIEW DROPDOWN",
      `Active view: ${safeValue}`,
      `Active label: ${current.label}`,
      `Visited: ${visited.join(",")}`,
      `All views visited: ${allVisited}`,
      `Profitability visible: ${visibility.profitability}`,
      `Cash visible: ${visibility.cash}`,
      `Estado de resultados visible: ${visibility.pnl}`,
      `Trends visible: ${visibility.trends}`,
      `Forecast visible: ${visibility.forecast}`,
      `Team visible: ${visibility.team}`,
      `Team detail visible: ${visibility.teamDetail}`,
      `Only active core view visible: ${expectedCurrentVisible && !otherCoreVisible}`,
      "",
      `Result: ${pass ? "PASS" : "FAIL"}`,
    ].join("\n");

    try {
      await navigator.clipboard.writeText(report);
    } catch (_) {
      window.prompt("Copia el reporte A4.8.2:", report);
    }
  };

  return (
    <section
      data-loop-finance-workspace="a4-8-2"
      className="ba-finance-view-picker"
      style={{
        width: "100%",
        minWidth: 0,
        border: 0,
        background: "transparent",
        padding: 0,
        margin: 0,
      }}
    >
      <style>{`
        .ba-finance-workspace-hidden{display:none!important}
        .ba-finance-view-picker-main{display:grid;grid-template-columns:minmax(250px,340px) minmax(0,1fr);gap:10px;align-items:center}
        .ba-finance-view-menu-wrap{position:relative}
        .ba-finance-view-trigger{width:100%;min-height:42px;display:grid;grid-template-columns:minmax(0,1fr) auto;gap:10px;align-items:center;padding:6px 10px;border:1px solid #dbe4e8;border-radius:10px;background:#fbfdfd;text-align:left;cursor:pointer}
        .ba-finance-view-trigger span{display:grid;gap:2px;min-width:0}
        .ba-finance-view-trigger small{font-size:8px;font-weight:900;letter-spacing:.1em;text-transform:uppercase;color:#0f766e}
        .ba-finance-view-trigger strong{font-size:12.5px;color:#0f172a;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .ba-finance-view-trigger i{font-style:normal;color:#64748b;font-size:13px}
        /* LOOP A4.8.7 — finance dropdown affordance */
        .ba-finance-view-trigger{
          min-height:46px;
          grid-template-columns:minmax(0,1fr) 40px;
          gap:0;
          padding:0 0 0 11px;
          overflow:hidden;
          border-color:#dbe4e8;
          background:#fff;
          transition:border-color .16s ease,box-shadow .16s ease,background .16s ease;
        }
        .ba-finance-view-trigger>span{padding:6px 9px 6px 0;}
        .ba-finance-view-trigger i{
          align-self:stretch;
          width:40px;
          height:auto;
          display:grid;
          place-items:center;
          border-left:1px solid #e5eaed;
          background:#f7faf9;
          color:#64748b;
          font-size:15px;
          transition:transform .16s ease,background .16s ease,color .16s ease;
        }
        .ba-finance-view-trigger:hover{
          border-color:rgba(15,118,110,.40);
          background:#fcfffe;
          box-shadow:0 4px 14px rgba(15,118,110,.06);
        }
        .ba-finance-view-trigger:hover i{background:#eef9f6;color:#0f766e;}
        .ba-finance-view-trigger[aria-expanded="true"]{
          border-color:rgba(15,118,110,.52);
          background:#fff;
          box-shadow:0 6px 18px rgba(15,118,110,.08);
        }
        .ba-finance-view-trigger[aria-expanded="true"] i{
          background:#e9f7f3;
          color:#0f766e;
          transform:rotate(180deg);
        }
        .ba-finance-view-trigger:focus-visible{outline:2px solid rgba(15,118,110,.20);outline-offset:2px;}
        .ba-finance-view-menu{position:absolute;left:0;top:calc(100% + 6px);z-index:120;width:min(440px,80vw);padding:6px;border:1px solid #dbe4e8;border-radius:13px;background:#fff;box-shadow:0 18px 44px rgba(15,23,42,.14)}
        .ba-finance-view-option{width:100%;display:block;padding:9px 10px;border:0;border-radius:9px;background:transparent;text-align:left;cursor:pointer}
        .ba-finance-view-option:hover{background:#f8fafc}
        .ba-finance-view-option.active{background:#ecfdf8;box-shadow:inset 0 0 0 1px rgba(15,118,110,.13)}
        .ba-finance-view-option-copy{display:grid;gap:2px;min-width:0}
        .ba-finance-view-option-copy strong{font-size:11px;color:#0f172a}
        .ba-finance-view-option-copy span{font-size:9px;line-height:1.25;color:#64748b}
        .ba-finance-view-option em{display:none}
        .ba-finance-view-current-copy{min-width:0}
        .ba-finance-view-current-copy strong{display:block;font-size:9px;color:#0f766e;text-transform:uppercase;letter-spacing:.06em}
        .ba-finance-view-current-copy span{display:block;margin-top:2px;font-size:9.5px;line-height:1.25;color:#64748b}
        @media(max-width:860px){.ba-finance-view-picker-main{grid-template-columns:1fr}.ba-finance-view-menu{width:100%}}
      `}</style>

      <label className="ba-finance-mobile-picker">Vista financiera<select aria-label="Vista financiera" value={safeValue} onChange={event => selectView(event.target.value)}>{FINANCE_WORKSPACE_VIEWS.map(row => <option key={row.key} value={row.key}>{row.label}</option>)}</select><span>{current.description}</span></label>
      <div className="ba-finance-view-picker-main">
        <div
          className="ba-finance-view-menu-wrap"
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) setMenuOpen(false);
          }}
        >
          <button
            type="button"
            className="ba-finance-view-trigger"
            aria-haspopup="listbox"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span>
              <small>Vista financiera</small>
              <strong>{current.label}</strong>
            </span>
            <i aria-hidden="true">⌄</i>
          </button>

          {menuOpen ? (
            <div className="ba-finance-view-menu" role="listbox" aria-label="Seleccionar vista financiera">
              {FINANCE_WORKSPACE_VIEWS.map((row) => (
                <button
                  type="button"
                  key={row.key}
                  role="option"
                  aria-selected={row.key === safeValue}
                  className={`ba-finance-view-option ${row.key === safeValue ? "active" : ""}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectView(row.key)}
                >
                  <span className="ba-finance-view-option-copy">
                    <strong>{row.label}</strong>
                    <span>{row.description}</span>
                  </span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="ba-finance-view-current-copy">
          <strong>{current.eyebrow}</strong>
          <span>{current.description}</span>
        </div>

      </div>
    </section>
  );
}

export function FinanceProfitabilityPanel({
  remote,
  periodLabel,
  workerId,
}) {
 const region=useBusinessRegion();
 const PROFITABILITY_MONEY=useRegionMoney();
 const CLOSEOUT_MONEY=PROFITABILITY_MONEY;
 const agingMoney=value=>PROFITABILITY_MONEY.format(numeric(value));
 const forecastMoney=agingMoney;

  const status = remote?.profitabilityStatus || "disabled";
  const data = remote?.profitabilityResult || null;
  const current = data?.current || null;
  const business = current?.business || null;
  const services = Array.isArray(current?.services) ? current.services : [];
  const workers = Array.isArray(current?.workers) ? current.workers : [];
  const drivers = data?.drivers || {};
  const previous = data?.previous || null;

  if (status === "disabled") return null;

  const shellStyle = {
    gridColumn: "1 / -1",
    margin: "0 0 18px",
    padding: "18px",
    border: "1px solid #e2e8f0",
    borderRadius: 18,
    background: "#fff",
    boxShadow: "0 8px 30px rgba(15, 23, 42, 0.04)",
  };

  if (status === "error") {
    return (
      <section data-loop-profitability="a4-4-3" style={shellStyle}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".04em" }}>
          Rentabilidad
        </span>
        <h3 style={{ margin: "6px 0 4px", fontSize: 20, color: "#0f172a" }}>Rentabilidad temporalmente no disponible</h3>
        <p style={{ margin: 0, color: "#64748b", fontSize: 13 }}>
          El resto de Finanzas no se ve afectado. {remote?.profitabilityError || ""}
        </p>
      </section>
    );
  }

  if (status === "loading" || !current || !business) {
    return (
      <section data-loop-profitability="a4-4-3" style={shellStyle}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".04em" }}>
          Rentabilidad
        </span>
        <h3 style={{ margin: "6px 0 4px", fontSize: 20, color: "#0f172a" }}>Calculando contribución del período…</h3>
        <p style={{ margin: 0, color: "#64748b", fontSize: 13 }}>
          Loop está usando el análisis bounded certificado; Finanzas sigue disponible mientras termina.
        </p>
      </section>
    );
  }

  const directCost = numeric(business.directCost);
  const contribution = numeric(business.contribution);
  const operatingExpenses = numeric(business.operatingExpenses);
  const operatingProfit = numeric(business.operatingProfit);
  const recognizedRevenue = numeric(business.recognizedRevenue);
  const margin = business.margin;
  const topService = drivers?.topService || services[0] || null;
  const topWorker = drivers?.topWorker || workers[0] || null;
  const marginErosion = drivers?.marginErosion || null;
  const contributionDelta = numeric(drivers?.contributionDelta);
  const marginDelta = drivers?.marginDelta;

  const workerContribution = workers.reduce((sum, row) => sum + numeric(row?.contribution), 0);
  const serviceContribution = services.reduce((sum, row) => sum + numeric(row?.contribution), 0);
  const productContribution =
    numeric(business.productRevenue) -
    numeric(business.productCommissionCost) -
    numeric(business.productCost);

  const checks = [
    {
      label: "status ready",
      pass: status === "ready" && Boolean(data?.ok),
      left: "ready",
      right: status,
    },
    {
      label: "queryMode",
      pass: data?.queryMode === PROFITABILITY_QUERY_MODE,
      left: PROFITABILITY_QUERY_MODE,
      right: String(data?.queryMode || ""),
    },
    {
      label: "version",
      pass: data?.profitabilityVersion === PROFITABILITY_VERSION,
      left: PROFITABILITY_VERSION,
      right: String(data?.profitabilityVersion || ""),
    },
    {
      label: "semantics",
      pass: data?.profitabilitySemanticsVersion === PROFITABILITY_SEMANTICS_VERSION,
      left: PROFITABILITY_SEMANTICS_VERSION,
      right: String(data?.profitabilitySemanticsVersion || ""),
    },
    {
      label: "policy no OPEX allocation",
      pass: current?.policy?.operatingExpenseAllocation === "none",
      left: "none",
      right: String(current?.policy?.operatingExpenseAllocation || ""),
    },
    {
      label: "policy tips excluded",
      pass: current?.policy?.tips === "excluded_from_profitability",
      left: "excluded_from_profitability",
      right: String(current?.policy?.tips || ""),
    },
    {
      label: "margin formula",
      pass: margin === null
        ? Math.abs(recognizedRevenue) <= 0.005
        : moneyEqual(margin, recognizedRevenue ? (contribution / recognizedRevenue) * 100 : 0),
      left: profitabilityPercentLabel(recognizedRevenue ? (contribution / recognizedRevenue) * 100 : null),
      right: profitabilityPercentLabel(margin),
    },
    {
      label: "worker contribution reconciliation",
      pass: moneyEqual(workerContribution, contribution),
      left: workerContribution.toFixed(2),
      right: contribution.toFixed(2),
    },
    {
      label: "service + product reconciliation",
      pass: moneyEqual(serviceContribution + productContribution, contribution),
      left: (serviceContribution + productContribution).toFixed(2),
      right: contribution.toFixed(2),
    },
    {
      label: "previous presence",
      pass: Boolean(previous) === Boolean(remote?.previous),
      left: String(Boolean(remote?.previous)),
      right: String(Boolean(previous)),
    },
  ];

  const failures = checks.filter((row) => !row.pass);
  const report = [
    "LOOP A4.4.3 PROFITABILITY UI RUNTIME",
    `Period: ${periodLabel || "n/a"}`,
    `Worker: ${String(workerId || "all")}`,
    `Status: ${status}`,
    `Query mode: ${data?.queryMode || ""}`,
    `Version: ${data?.profitabilityVersion || ""}`,
    `Semantics: ${data?.profitabilitySemanticsVersion || ""}`,
    `Scanned bookings: ${data?.diagnostics?.scanned?.bookings ?? ""}`,
    `Recognized revenue: ${recognizedRevenue.toFixed(2)}`,
    `Direct cost: ${directCost.toFixed(2)}`,
    `Contribution: ${contribution.toFixed(2)}`,
    `Margin: ${margin === null ? "null" : numeric(margin).toFixed(2)}`,
    `Operating expenses: ${operatingExpenses.toFixed(2)}`,
    `Operating profit: ${operatingProfit.toFixed(2)}`,
    `Services: ${services.length}`,
    `Workers: ${workers.length}`,
    `Top service: ${String(topService?.service || "")}`,
    `Top worker: ${String(topWorker?.worker || "")}`,
    "",
    ...checks.map((row) =>
      `${row.pass ? "PASS" : "FAIL"} | ${row.label} | local=${row.left} | remote=${row.right}`
    ),
    "",
    `Result: ${failures.length ? "FAIL" : "PASS"}`,
  ].join("\n");

  const copyReport = async () => {
    try {
      await navigator.clipboard.writeText(report);
    } catch (_) {
      window.prompt("Copia el reporte A4.4.3:", report);
    }
  };

  return (
    <section data-loop-profitability="a4-4-3" style={shellStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
        <div>
          <span style={{ fontSize: 12, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em" }}>
            Rentabilidad
          </span>
          <h3 style={{ margin: "5px 0 5px", fontSize: 22, color: "#0f172a" }}>
            Qué realmente está dejando dinero
          </h3>
          <p style={{ margin: 0, color: "#64748b", fontSize: 13, maxWidth: 720 }}>
            Contribución = ingreso reconocido − costos directos. Los gastos operativos se muestran solo a nivel negocio y las propinas no inflan el margen.
          </p>
        </div>
        {previous ? (
          <div style={{ textAlign: "right", minWidth: 180 }}>
            <span style={{ display: "block", fontSize: 11, color: "#64748b" }}>vs período anterior</span>
            <strong style={{ display: "block", marginTop: 3, color: profitabilityTone(contributionDelta), fontSize: 15 }}>
              {profitabilitySignedMoney(contributionDelta)} contribución
            </strong>
            <span style={{ display: "block", marginTop: 2, fontSize: 12, color: "#64748b" }}>
              Margen {marginDelta === null || marginDelta === undefined ? "—" : `${numeric(marginDelta) >= 0 ? "+" : ""}${numeric(marginDelta).toFixed(1)} pts.`}
            </span>
          </div>
        ) : null}
      </div>

      <div className="finance-profitability-kpis" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 160px), 1fr))", gap: 10, marginTop: 16 }}>
        {[
          ["Ingreso reconocido", recognizedRevenue, "Base económica del período"],
          ["Costos directos", directCost, "Equipo + materiales + producto"],
          ["Contribución", contribution, "Antes de gastos operativos"],
          ["Resultado operativo", operatingProfit, operatingExpenses > 0 ? `Después de ${PROFITABILITY_MONEY.format(operatingExpenses)} en gastos` : "Sin gastos operativos registrados"],
        ].map(([label, value, hint]) => (
          <div key={label} style={{ padding: "13px 14px", borderRadius: 14, background: "#f8fafc", border: "1px solid #eef2f7" }}>
            <span style={{ display: "block", fontSize: 11, color: "#64748b", fontWeight: 700 }}>{label}</span>
            <strong style={{ display: "block", marginTop: 5, fontSize: 21, color: label.includes("Contribución") || label.includes("Resultado") ? profitabilityTone(value) : "#0f172a" }}>
              {PROFITABILITY_MONEY.format(numeric(value))}
            </strong>
            <span style={{ display: "block", marginTop: 3, fontSize: 11, color: "#64748b" }}>{hint}</span>
          </div>
        ))}
        <div style={{ padding: "13px 14px", borderRadius: 14, background: "#f8fafc", border: "1px solid #eef2f7" }}>
          <span style={{ display: "block", fontSize: 11, color: "#64748b", fontWeight: 700 }}>Margen de contribución</span>
          <strong style={{ display: "block", marginTop: 5, fontSize: 21, color: profitabilityTone(contribution) }}>
            {profitabilityPercentLabel(margin)}
          </strong>
          <span style={{ display: "block", marginTop: 3, fontSize: 11, color: "#64748b" }}>Contribución / ingreso reconocido</span>
        </div>
      </div>

      {recognizedRevenue > 0 && operatingExpenses <= 0.005 ? (
        <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 12, background: "#fffbeb", border: "1px solid #fde68a", fontSize: 12, color: "#92400e" }}>
          No hay gastos operativos registrados en este período. Por eso el resultado operativo coincide con la contribución y el margen puede verse especialmente alto.
        </div>
      ) : null}

      <div className="finance-profitability-highlights" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap: 12, marginTop: 14 }}>
        <div style={{ padding: "13px", borderRadius: 14, border: "1px solid #e2e8f0" }}>
          <span style={{ display: "block", fontSize: 11, color: "#64748b", fontWeight: 700 }}>Mayor aporte por servicio</span>
          <strong style={{ display: "block", marginTop: 4, fontSize: 16, color: "#0f172a" }}>{topService?.service || "Sin datos"}</strong>
          <span style={{ display: "block", marginTop: 4, fontSize: 12, color: profitabilityTone(topService?.contribution) }}>
            {topService ? `${PROFITABILITY_MONEY.format(numeric(topService.contribution))} · ${profitabilityPercentLabel(topService.margin)}` : "—"}
          </span>
        </div>
        <div style={{ padding: "13px", borderRadius: 14, border: "1px solid #e2e8f0" }}>
          <span style={{ display: "block", fontSize: 11, color: "#64748b", fontWeight: 700 }}>Mayor aporte del equipo</span>
          <strong style={{ display: "block", marginTop: 4, fontSize: 16, color: "#0f172a" }}>{topWorker?.worker || "Sin datos"}</strong>
          <span style={{ display: "block", marginTop: 4, fontSize: 12, color: profitabilityTone(topWorker?.contribution) }}>
            {topWorker ? `${PROFITABILITY_MONEY.format(numeric(topWorker.contribution))} · ${profitabilityPercentLabel(topWorker.margin)}` : "—"}
          </span>
        </div>
        <div style={{ padding: "13px", borderRadius: 14, border: "1px solid #e2e8f0" }}>
          <span style={{ display: "block", fontSize: 11, color: "#64748b", fontWeight: 700 }}>Margen a vigilar</span>
          <strong style={{ display: "block", marginTop: 4, fontSize: 16, color: "#0f172a" }}>{marginErosion?.service || "Sin alerta"}</strong>
          <span style={{ display: "block", marginTop: 4, fontSize: 12, color: marginErosion ? profitabilityTone(marginErosion?.contribution) : "#64748b" }}>
            {marginErosion ? `${profitabilityPercentLabel(marginErosion.margin)} · ${PROFITABILITY_MONEY.format(numeric(marginErosion.contribution))} contribución` : "No hay servicios con ingreso reconocido"}
          </span>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))", gap: 14, marginTop: 16 }}>
        <article style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: "13px", overflow: "hidden" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
            <div>
              <span style={{ display: "block", fontSize: 11, color: "#64748b", fontWeight: 700 }}>Servicios</span>
              <strong style={{ display: "block", marginTop: 3, fontSize: 15, color: "#0f172a" }}>Contribución por servicio</strong>
            </div>
            <span style={{ fontSize: 11, color: "#64748b" }}>Top {Math.min(5, services.length)}</span>
          </div>
          <div style={{ overflowX: "auto", marginTop: 8 }}>
            <div className="finance-profitability-table" style={{ minWidth: 620 }}>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(140px, 1.5fr) repeat(4, minmax(92px, 1fr))", gap: 10, padding: "7px 0", color: "#64748b", fontSize: 10, fontWeight: 800, textTransform: "uppercase" }}>
                <span>Servicio</span><span style={{ textAlign: "right" }}>Ingreso</span><span style={{ textAlign: "right" }}>Costo</span><span style={{ textAlign: "right" }}>Contrib.</span><span style={{ textAlign: "right" }}>Margen</span>
              </div>
              {services.length
                ? services.slice(0, 5).map((row) => profitabilityGridRow(row.service, row.recognizedRevenue, row.directCost, row.contribution, row.margin))
                : <div style={{ padding: "18px 0", color: "#64748b", fontSize: 12 }}>Sin servicios rentables para mostrar en este período.</div>}
            </div>
          </div>
        </article>

        <article style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: "13px", overflow: "hidden" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
            <div>
              <span style={{ display: "block", fontSize: 11, color: "#64748b", fontWeight: 700 }}>Equipo</span>
              <strong style={{ display: "block", marginTop: 3, fontSize: 15, color: "#0f172a" }}>Contribución por trabajador</strong>
            </div>
            <span style={{ fontSize: 11, color: "#64748b" }}>Top {Math.min(5, workers.length)}</span>
          </div>
          <div style={{ overflowX: "auto", marginTop: 8 }}>
            <div className="finance-profitability-table" style={{ minWidth: 620 }}>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(140px, 1.5fr) repeat(4, minmax(92px, 1fr))", gap: 10, padding: "7px 0", color: "#64748b", fontSize: 10, fontWeight: 800, textTransform: "uppercase" }}>
                <span>Trabajador</span><span style={{ textAlign: "right" }}>Ingreso</span><span style={{ textAlign: "right" }}>Costo</span><span style={{ textAlign: "right" }}>Contrib.</span><span style={{ textAlign: "right" }}>Margen</span>
              </div>
              {workers.length
                ? workers.slice(0, 5).map((row) => profitabilityGridRow(row.worker, row.recognizedRevenue, row.directCost, row.contribution, row.margin))
                : <div style={{ padding: "18px 0", color: "#64748b", fontSize: 12 }}>Sin trabajadores con actividad rentable para mostrar.</div>}
            </div>
          </div>
        </article>
      </div>

      <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #eef2f7", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: "#64748b" }}>
          Membresías reconocen ingreso del negocio · Fidelidad solo subsidio/ingreso explícito · Cortesías no reconocen revenue · Propinas excluidas.
        </span>
      </div>
    </section>
  );
}

export function FinanceCutoverStatus({
  remote,
  localCurrent,
  resolvedCurrent,
  localPrevious,
  resolvedPrevious,
  periodLabel,
  workerId,
  detailView,
  localDetailTotal = 0,
  localDetailSummaryAmount = 0,
  fastPath = false,
  loadedBookings = 0,
}) {
  const [lastDetailDiagnostic, setLastDetailDiagnostic] = useState(null);
  const [uiRowsProbe, setUiRowsProbe] = useState({
    status: "idle",
    attempted: false,
    data: null,
    error: "",
  });
  const [profitabilityProbe, setProfitabilityProbe] = useState({
    status: "idle",
    attempted: false,
    data: null,
    error: "",
  });
  const [agingProbe, setAgingProbe] = useState({
    status: "idle",
    attempted: false,
    data: null,
    error: "",
  });
  const [forecastProbe, setForecastProbe] = useState({
    status: "idle",
    attempted: false,
    data: null,
    error: "",
  });
  const [closeoutProbe, setCloseoutProbe] = useState({
    status: "idle",
    attempted: false,
    data: null,
    error: "",
  });

  useEffect(() => {
    setUiRowsProbe({ status: "idle", attempted: false, data: null, error: "" });
    setProfitabilityProbe({ status: "idle", attempted: false, data: null, error: "" });
    setAgingProbe({ status: "idle", attempted: false, data: null, error: "" });
    setForecastProbe({ status: "idle", attempted: false, data: null, error: "" });
    setCloseoutProbe({ status: "idle", attempted: false, data: null, error: "" });
  }, [periodLabel, workerId]);


  useEffect(() => {
    if (!detailView || !remote?.detailReady || !remote?.detailResult) return;
    setLastDetailDiagnostic({
      periodLabel: String(periodLabel || ""),
      workerId: String(workerId || "all"),
      view: String(detailView || remote?.detailResult?.view || ""),
      queryMode: String(remote?.detailResult?.queryMode || ""),
      page: numeric(remote?.detailResult?.page),
      pageSize: numeric(remote?.detailResult?.pageSize),
      remoteRows: Array.isArray(remote?.detailRows) ? remote.detailRows.length : 0,
      remoteDatedRows: (Array.isArray(remote?.detailRows) ? remote.detailRows : []).filter((row) => row?.date instanceof Date && Number.isFinite(row.date.getTime())).length,
      remoteDatesReady: (Array.isArray(remote?.detailRows) ? remote.detailRows : []).every((row) => !row?.date || (row.date instanceof Date && Number.isFinite(row.date.getTime()))),
      remoteTotal: numeric(remote?.detailTotal),
      remoteSummary: numeric(remote?.detailSummaryAmount),
      localTotal: numeric(localDetailTotal),
      localSummary: numeric(localDetailSummaryAmount),
    });
  }, [
    detailView,
    remote?.detailReady,
    remote?.detailResult,
    remote?.detailRows,
    remote?.detailTotal,
    remote?.detailSummaryAmount,
    localDetailTotal,
    localDetailSummaryAmount,
    periodLabel,
    workerId,
  ]);

  const financeDebugEnabled = process.env.NODE_ENV === "development"
    && typeof window !== "undefined"
    && new URLSearchParams(window.location.search).get("financeDebug") === "1";
  if (!financeDebugEnabled) return null;

  const remoteCurrent = remote?.rangeResult?.current || null;
  const remotePrevious = remote?.rangeResult?.previous || null;

  const liveDetailDiagnostic = detailView && remote?.detailReady && remote?.detailResult
    ? {
      periodLabel: String(periodLabel || ""),
      workerId: String(workerId || "all"),
      view: String(detailView || remote?.detailResult?.view || ""),
      queryMode: String(remote?.detailResult?.queryMode || ""),
      page: numeric(remote?.detailResult?.page),
      pageSize: numeric(remote?.detailResult?.pageSize),
      remoteRows: Array.isArray(remote?.detailRows) ? remote.detailRows.length : 0,
      remoteDatedRows: (Array.isArray(remote?.detailRows) ? remote.detailRows : []).filter((row) => row?.date instanceof Date && Number.isFinite(row.date.getTime())).length,
      remoteDatesReady: (Array.isArray(remote?.detailRows) ? remote.detailRows : []).every((row) => !row?.date || (row.date instanceof Date && Number.isFinite(row.date.getTime()))),
      remoteTotal: numeric(remote?.detailTotal),
      remoteSummary: numeric(remote?.detailSummaryAmount),
      localTotal: numeric(localDetailTotal),
      localSummary: numeric(localDetailSummaryAmount),
    }
    : null;

  const rememberedDetailMatchesScope = lastDetailDiagnostic
    && lastDetailDiagnostic.periodLabel === String(periodLabel || "")
    && lastDetailDiagnostic.workerId === String(workerId || "all");

  const detailDiagnostic = liveDetailDiagnostic
    || (rememberedDetailMatchesScope ? lastDetailDiagnostic : null);

  const runUiRowsParity = async () => {
    if (uiRowsProbe.attempted || uiRowsProbe.status === "running" || !remote?.rangeReady || typeof remote?.runUiRowsProbe !== "function") return;
    setUiRowsProbe({ status: "running", attempted: true, data: null, error: "" });
    try {
      const data = await remote.runUiRowsProbe();
      setUiRowsProbe({ status: "ready", attempted: true, data, error: "" });
    } catch (error) {
      console.error("[FinanceRemoteCutover] A4.S14 UI rows probe failed", error);
      setUiRowsProbe({
        status: "error",
        attempted: true,
        data: null,
        error: `${error?.code || "error"}: ${error?.message || String(error)}`,
      });
    }
  };

  const runProfitabilityParity = async () => {
    if (
      profitabilityProbe.attempted ||
      profitabilityProbe.status === "running" ||
      !remote?.rangeReady ||
      typeof remote?.runProfitabilityProbe !== "function"
    ) return;

    setProfitabilityProbe({ status: "running", attempted: true, data: null, error: "" });
    try {
      const data = await remote.runProfitabilityProbe();
      setProfitabilityProbe({ status: "ready", attempted: true, data, error: "" });
    } catch (error) {
      console.error("[A4.4.2 ProfitabilityParity]", error);
      setProfitabilityProbe({
        status: "error",
        attempted: true,
        data: null,
        error: `${error?.code || "error"}: ${error?.message || String(error)}`,
      });
    }
  };

  const runAgingParity = async () => {
    if (
      agingProbe.attempted ||
      agingProbe.status === "running" ||
      !remote?.rangeReady ||
      typeof remote?.runAgingProbe !== "function"
    ) return;

    setAgingProbe({ status: "running", attempted: true, data: null, error: "" });
    try {
      const data = await remote.runAgingProbe();
      setAgingProbe({ status: "ready", attempted: true, data, error: "" });
    } catch (error) {
      console.error("[A4.5.2 AgingParity]", error);
      setAgingProbe({
        status: "error",
        attempted: true,
        data: null,
        error: `${error?.code || "error"}: ${error?.message || String(error)}`,
      });
    }
  };

  const runForecastParity = async () => {
    if (
      forecastProbe.attempted ||
      forecastProbe.status === "running" ||
      !remote?.rangeReady ||
      typeof remote?.runForecastProbe !== "function"
    ) return;

    setForecastProbe({ status: "running", attempted: true, data: null, error: "" });
    try {
      const data = await remote.runForecastProbe();
      setForecastProbe({ status: "ready", attempted: true, data, error: "" });
    } catch (error) {
      console.error("[A4.6.2 ForecastParity]", error);
      setForecastProbe({
        status: "error",
        attempted: true,
        data: null,
        error: `${error?.code || "error"}: ${error?.message || String(error)}`,
      });
    }
  };

  const runCloseoutParity = async () => {
    if (
      closeoutProbe.attempted ||
      closeoutProbe.status === "running" ||
      !remote?.rangeReady ||
      typeof remote?.runCloseoutProbe !== "function"
    ) return;

    setCloseoutProbe({ status: "running", attempted: true, data: null, error: "" });
    try {
      const data = await remote.runCloseoutProbe();
      setCloseoutProbe({ status: "ready", attempted: true, data, error: "" });
    } catch (error) {
      console.error("[A4.7.2 CloseoutParity]", error);
      setCloseoutProbe({
        status: "error",
        attempted: true,
        data: null,
        error: `${error?.code || "error"}: ${error?.message || String(error)}`,
      });
    }
  };

  const uiRowsRemote = uiRowsProbe.status === "ready" ? uiRowsProbe.data : null;
  const uiRowsParity = uiRowsRemote
    ? uiRowParity(localCurrent?.financeRows, uiRowsRemote?.currentUiRows)
    : null;
  const localDetailCounts = {
    receivables: Array.isArray(localCurrent?.unpaidBookings) ? localCurrent.unpaidBookings.length : 0,
    reconciliation: Array.isArray(localCurrent?.paymentMismatchBookings) ? localCurrent.paymentMismatchBookings.length : 0,
    untracked: Array.isArray(localCurrent?.untrackedRevenueBookings) ? localCurrent.untrackedRevenueBookings.length : 0,
    covered: (Array.isArray(localCurrent?.financeRows) ? localCurrent.financeRows : []).filter((row) => numeric(row?.coveredTrackedAmount) > 0).length,
  };
  const remoteDetailCounts = uiRowsRemote?.current?.detailCounts || {};

  const uiChecks = uiRowsRemote ? [
    {
      label: "queryMode",
      pass: uiRowsRemote?.queryMode === RANGE_QUERY_MODE,
      left: RANGE_QUERY_MODE,
      right: String(uiRowsRemote?.queryMode || ""),
    },
    {
      label: "uiRowsVersion",
      pass: uiRowsRemote?.uiRowsVersion === UI_ROWS_VERSION,
      left: UI_ROWS_VERSION,
      right: String(uiRowsRemote?.uiRowsVersion || ""),
    },
    {
      label: "uiRows count",
      pass: uiRowsParity?.localCount === uiRowsParity?.remoteCount,
      left: String(uiRowsParity?.localCount ?? 0),
      right: String(uiRowsParity?.remoteCount ?? 0),
    },
    {
      label: "uiRows field parity",
      pass: Boolean(uiRowsParity?.pass),
      left: `${uiRowsParity?.matched ?? 0}/${uiRowsParity?.localCount ?? 0}`,
      right: `${uiRowsParity?.matched ?? 0}/${uiRowsParity?.remoteCount ?? 0}`,
    },
    {
      label: "cashMethodsDetailed",
      pass: cashMethodsParity(localCurrent?.cashByMethod, uiRowsRemote?.current?.cashMethodsDetailed),
      left: "local cashByMethod",
      right: "remote bounded",
    },
    {
      label: "coveredMethods",
      pass: coveredMethodsParity(localCurrent?.coveredByMethod, uiRowsRemote?.current?.coveredMethods),
      left: "local coveredByMethod",
      right: "remote bounded",
    },
    ...["receivables", "reconciliation", "untracked", "covered"].map((key) => ({
      label: `detailCounts.${key}`,
      pass: numeric(localDetailCounts[key]) === numeric(remoteDetailCounts[key]),
      left: String(numeric(localDetailCounts[key])),
      right: String(numeric(remoteDetailCounts[key])),
    })),
    {
      label: "counts.revenueBookings",
      pass: numeric(Array.isArray(localCurrent?.revenueBookings) ? localCurrent.revenueBookings.length : 0) === numeric(uiRowsRemote?.current?.counts?.revenueBookings),
      left: String(Array.isArray(localCurrent?.revenueBookings) ? localCurrent.revenueBookings.length : 0),
      right: String(numeric(uiRowsRemote?.current?.counts?.revenueBookings)),
    },
    {
      label: "bounded current rows",
      pass: String(uiRowsRemote?.current?.range?.from || "") === String(resolvedCurrent?.loopRemoteRange?.from || "")
        && String(uiRowsRemote?.current?.range?.to || "") === String(resolvedCurrent?.loopRemoteRange?.to || ""),
      left: `${resolvedCurrent?.loopRemoteRange?.from || ""} -> ${resolvedCurrent?.loopRemoteRange?.to || ""}`,
      right: `${uiRowsRemote?.current?.range?.from || ""} -> ${uiRowsRemote?.current?.range?.to || ""}`,
    },
  ] : [];

  const uiFailures = uiChecks.filter((row) => !row.pass);
  const uiRowsReport = [
    "LOOP A4.S14 FINANCE UI ROWS PARITY",
    `Period: ${periodLabel || "n/a"}`,
    `Worker: ${String(workerId || "all")}`,
    `Probe status: ${uiRowsProbe.status}`,
    `Query mode: ${uiRowsRemote?.queryMode || ""}`,
    `UI rows version: ${uiRowsRemote?.uiRowsVersion || ""}`,
    `Scanned bookings: ${uiRowsRemote?.diagnostics?.scanned?.bookings ?? ""}`,
    `Bounded from: ${uiRowsRemote?.diagnostics?.boundedQuery?.from || ""}`,
    `Bounded toExclusive: ${uiRowsRemote?.diagnostics?.boundedQuery?.toExclusive || ""}`,
    `UI rows returned: ${uiRowsRemote?.diagnostics?.uiRowsReturned ?? ""}`,
    "",
    ...uiChecks.map((row) => `${row.pass ? "PASS" : "FAIL"} | ${row.label} | local=${row.left} | remote=${row.right}`),
    "",
    ...(uiRowsParity?.details || []).slice(0, 10).map((row) =>
      `${row.pass ? "PASS" : "FAIL"} | row=${row.id} | found=${row.found} | text=${row.text} | date=${row.date} | numeric=${row.numeric} | rawAmount=${row.rawAmount} | displayAmount=${row.displayAmount} | loyalty=${row.loyaltyFlag}`
    ),
    "",
    `Result: ${uiRowsRemote && uiFailures.length === 0 ? "PASS" : uiRowsRemote ? "FAIL" : uiRowsProbe.status === "error" ? "ERROR" : "PENDING"}`,
  ].join("\n");

  const copyUiRowsReport = async () => {
    try {
      await navigator.clipboard.writeText(uiRowsReport);
    } catch (_) {
      window.prompt("Copia el reporte A4.S14:", uiRowsReport);
    }
  };

  const checks = [];
  if (remote?.rangeReady && remoteCurrent) {
    checks.push({
      label: "range query mode",
      pass: remote?.rangeResult?.queryMode === RANGE_QUERY_MODE,
      left: RANGE_QUERY_MODE,
      right: String(remote?.rangeResult?.queryMode || ""),
    });
    checks.push({
      label: "uiRowsVersion",
      pass: remote?.uiRowsVersion === UI_ROWS_VERSION,
      left: UI_ROWS_VERSION,
      right: String(remote?.uiRowsVersion || ""),
    });
    checks.push({
      label: "resolved financeRows",
      pass: Array.isArray(resolvedCurrent?.financeRows) &&
        resolvedCurrent.financeRows.length === (Array.isArray(remote?.currentUiRows) ? remote.currentUiRows.length : 0),
      left: String(Array.isArray(resolvedCurrent?.financeRows) ? resolvedCurrent.financeRows.length : 0),
      right: String(Array.isArray(remote?.currentUiRows) ? remote.currentUiRows.length : 0),
    });
    const resolvedFinanceRows = Array.isArray(resolvedCurrent?.financeRows) ? resolvedCurrent.financeRows : [];
    checks.push({
      label: "resolved UI row dates",
      pass: resolvedFinanceRows.every((row) => !row?.date || (row.date instanceof Date && Number.isFinite(row.date.getTime()))),
      left: String(resolvedFinanceRows.filter((row) => row?.date instanceof Date && Number.isFinite(row.date.getTime())).length),
      right: String(resolvedFinanceRows.filter((row) => Boolean(row?.date)).length),
    });
    if (fastPath) {
      checks.push({
        label: "full booking load skipped",
        pass: numeric(loadedBookings) === 0,
        left: String(numeric(loadedBookings)),
        right: "0",
      });
      checks.push({
        label: "remote UI rows active",
        pass: Boolean(resolvedCurrent?.loopRemoteUiRows),
        left: String(Boolean(resolvedCurrent?.loopRemoteUiRows)),
        right: "true",
      });
    }

    [
      "revenue",
      "collectedRevenue",
      "pendingRevenue",
      "paymentMismatchRevenue",
      "untrackedExpectedRevenue",
      "coveredRevenueTotal",
      "totalTips",
      "workerPayoutTotal",
      "materialCostTotal",
      "productSalesRevenueTotal",
      "productSalesCostTotal",
      "businessNetRevenue",
      "collectionRate",
    ].forEach((key) => {
      checks.push({
        label: `current.${key}`,
        pass: (fastPath || moneyEqual(localCurrent?.[key], remoteCurrent?.[key])) &&
          moneyEqual(resolvedCurrent?.[key], remoteCurrent?.[key]),
        left: numeric(fastPath ? resolvedCurrent?.[key] : localCurrent?.[key]).toFixed(2),
        right: numeric(remoteCurrent?.[key]).toFixed(2),
      });
    });

    checks.push({
      label: "current.services",
      pass: mapParity(fastPath ? resolvedCurrent?.revenueByService : localCurrent?.revenueByService, remoteCurrent?.services, "service"),
      left: fastPath ? "resolved remote map" : "local map",
      right: "remote bounded",
    });

    checks.push({
      label: "current.workers",
      pass: mapParity(fastPath ? resolvedCurrent?.revenueByWorker : localCurrent?.revenueByWorker, remoteCurrent?.workers, "worker"),
      left: fastPath ? "resolved remote map" : "local map",
      right: "remote bounded",
    });

    if (remotePrevious && localPrevious && resolvedPrevious) {
      [
        "revenue",
        "collectedRevenue",
        "pendingRevenue",
        "businessNetRevenue",
      ].forEach((key) => {
        checks.push({
          label: `previous.${key}`,
          pass: (fastPath || moneyEqual(localPrevious?.[key], remotePrevious?.[key])) &&
            moneyEqual(resolvedPrevious?.[key], remotePrevious?.[key]),
          left: numeric(fastPath ? resolvedPrevious?.[key] : localPrevious?.[key]).toFixed(2),
          right: numeric(remotePrevious?.[key]).toFixed(2),
        });
      });
    }
  }

  if (detailDiagnostic) {
    checks.push({
      label: `detail.${detailDiagnostic.view}.queryMode`,
      pass: detailDiagnostic.queryMode === DETAIL_QUERY_MODE,
      left: DETAIL_QUERY_MODE,
      right: detailDiagnostic.queryMode,
    });
    checks.push({
      label: `detail.${detailDiagnostic.view}.total`,
      pass: detailDiagnostic.localTotal === detailDiagnostic.remoteTotal,
      left: String(detailDiagnostic.localTotal),
      right: String(detailDiagnostic.remoteTotal),
    });
    checks.push({
      label: `detail.${detailDiagnostic.view}.summary`,
      pass: moneyEqual(detailDiagnostic.localSummary, detailDiagnostic.remoteSummary),
      left: detailDiagnostic.localSummary.toFixed(2),
      right: detailDiagnostic.remoteSummary.toFixed(2),
    });
    checks.push({
      label: `detail.${detailDiagnostic.view}.page`,
      pass: detailDiagnostic.page === 1,
      left: "1",
      right: String(detailDiagnostic.page),
    });
    checks.push({
      label: `detail.${detailDiagnostic.view}.pageSize`,
      pass: detailDiagnostic.pageSize === 20,
      left: "20",
      right: String(detailDiagnostic.pageSize),
    });
    checks.push({
      label: `detail.${detailDiagnostic.view}.rows`,
      pass: detailDiagnostic.remoteRows === Math.min(20, detailDiagnostic.remoteTotal),
      left: String(Math.min(20, detailDiagnostic.remoteTotal)),
      right: String(detailDiagnostic.remoteRows),
    });
    checks.push({
      label: `detail.${detailDiagnostic.view}.dates`,
      pass: Boolean(detailDiagnostic.remoteDatesReady),
      left: String(detailDiagnostic.remoteDatedRows),
      right: String(detailDiagnostic.remoteRows),
    });
  }

  const failures = checks.filter((row) => !row.pass);
  const ready = remote?.rangeReady;
  const detailStatusLabel = liveDetailDiagnostic
    ? "ready"
    : detailDiagnostic
      ? "remembered"
      : remote?.detailStatus || "disabled";
  const resultLabel = !ready
    ? remote?.rangeStatus === "error" ? "FALLBACK LOCAL" : "CARGANDO RANGE"
    : failures.length ? `FAIL · ${failures.length}` : `PASS · ${checks.length}/${checks.length}`;

  const profitabilityData = profitabilityProbe.status === "ready" ? profitabilityProbe.data : null;
  const localProfitability = buildLocalProfitabilityFromUiRows(
    resolvedCurrent?.financeRows,
    resolvedCurrent?.operatingExpenses
  );
  const profitabilityChecks = [];

  if (profitabilityData) {
    const currentProfitability = profitabilityData?.current || null;
    const serviceParity = profitabilityRowsParity(
      localProfitability.services,
      currentProfitability?.services,
      "service"
    );
    const workerParity = profitabilityRowsParity(
      localProfitability.workers,
      currentProfitability?.workers,
      "worker"
    );

    const remoteWorkerContribution = (Array.isArray(currentProfitability?.workers) ? currentProfitability.workers : [])
      .reduce((sum, row) => sum + numeric(row?.contribution), 0);
    const remoteServiceContribution = (Array.isArray(currentProfitability?.services) ? currentProfitability.services : [])
      .reduce((sum, row) => sum + numeric(row?.contribution), 0);
    const remoteProductContribution =
      numeric(currentProfitability?.business?.productRevenue) -
      numeric(currentProfitability?.business?.productCommissionCost) -
      numeric(currentProfitability?.business?.productCost);

    profitabilityChecks.push(
      {
        label: "queryMode",
        pass: profitabilityData?.queryMode === PROFITABILITY_QUERY_MODE,
        left: PROFITABILITY_QUERY_MODE,
        right: String(profitabilityData?.queryMode || ""),
      },
      {
        label: "profitabilityVersion",
        pass: profitabilityData?.profitabilityVersion === PROFITABILITY_VERSION,
        left: PROFITABILITY_VERSION,
        right: String(profitabilityData?.profitabilityVersion || ""),
      },
      {
        label: "semanticsVersion",
        pass: profitabilityData?.profitabilitySemanticsVersion === PROFITABILITY_SEMANTICS_VERSION,
        left: PROFITABILITY_SEMANTICS_VERSION,
        right: String(profitabilityData?.profitabilitySemanticsVersion || ""),
      },
      {
        label: "local semantic coverage",
        pass: localProfitability.comparable,
        left: `ambiguousLoyalty=${localProfitability.ambiguousLoyaltyRows}`,
        right: "ambiguousLoyalty=0",
      },
      {
        label: "bounded scan parity",
        pass: numeric(profitabilityData?.diagnostics?.scanned?.bookings) === numeric(remote?.rangeDiagnostics?.scanned?.bookings),
        left: String(numeric(remote?.rangeDiagnostics?.scanned?.bookings)),
        right: String(numeric(profitabilityData?.diagnostics?.scanned?.bookings)),
      },
      {
        label: "current range from",
        pass: String(currentProfitability?.range?.from || "") === String(remote?.current?.range?.from || ""),
        left: String(remote?.current?.range?.from || ""),
        right: String(currentProfitability?.range?.from || ""),
      },
      {
        label: "current range to",
        pass: String(currentProfitability?.range?.to || "") === String(remote?.current?.range?.to || ""),
        left: String(remote?.current?.range?.to || ""),
        right: String(currentProfitability?.range?.to || ""),
      },
      {
        label: "previous presence",
        pass: Boolean(profitabilityData?.previous) === Boolean(remote?.previous),
        left: String(Boolean(remote?.previous)),
        right: String(Boolean(profitabilityData?.previous)),
      },
      {
        label: "policy no OPEX allocation",
        pass: currentProfitability?.policy?.operatingExpenseAllocation === "none",
        left: "none",
        right: String(currentProfitability?.policy?.operatingExpenseAllocation || ""),
      },
      {
        label: "policy tips excluded",
        pass: currentProfitability?.policy?.tips === "excluded_from_profitability",
        left: "excluded_from_profitability",
        right: String(currentProfitability?.policy?.tips || ""),
      },
      {
        label: "business.recognizedRevenue",
        pass: localProfitability.comparable && moneyEqual(localProfitability.business.recognizedRevenue, currentProfitability?.business?.recognizedRevenue),
        left: numeric(localProfitability.business.recognizedRevenue).toFixed(2),
        right: numeric(currentProfitability?.business?.recognizedRevenue).toFixed(2),
      },
      {
        label: "business.directLaborCost",
        pass: localProfitability.comparable && moneyEqual(localProfitability.business.directLaborCost, currentProfitability?.business?.directLaborCost),
        left: numeric(localProfitability.business.directLaborCost).toFixed(2),
        right: numeric(currentProfitability?.business?.directLaborCost).toFixed(2),
      },
      {
        label: "business.materialCost",
        pass: localProfitability.comparable && moneyEqual(localProfitability.business.materialCost, currentProfitability?.business?.materialCost),
        left: numeric(localProfitability.business.materialCost).toFixed(2),
        right: numeric(currentProfitability?.business?.materialCost).toFixed(2),
      },
      {
        label: "business.productCost",
        pass: localProfitability.comparable && moneyEqual(localProfitability.business.productCost, currentProfitability?.business?.productCost),
        left: numeric(localProfitability.business.productCost).toFixed(2),
        right: numeric(currentProfitability?.business?.productCost).toFixed(2),
      },
      {
        label: "business.directCost",
        pass: localProfitability.comparable && moneyEqual(localProfitability.business.directCost, currentProfitability?.business?.directCost),
        left: numeric(localProfitability.business.directCost).toFixed(2),
        right: numeric(currentProfitability?.business?.directCost).toFixed(2),
      },
      {
        label: "business.contribution",
        pass: localProfitability.comparable && moneyEqual(localProfitability.business.contribution, currentProfitability?.business?.contribution),
        left: numeric(localProfitability.business.contribution).toFixed(2),
        right: numeric(currentProfitability?.business?.contribution).toFixed(2),
      },
      {
        label: "business.margin",
        pass: localProfitability.comparable && (
          (localProfitability.business.margin === null && currentProfitability?.business?.margin === null) ||
          moneyEqual(localProfitability.business.margin, currentProfitability?.business?.margin)
        ),
        left: localProfitability.business.margin === null ? "null" : numeric(localProfitability.business.margin).toFixed(2),
        right: currentProfitability?.business?.margin === null ? "null" : numeric(currentProfitability?.business?.margin).toFixed(2),
      },
      {
        label: "business.tipsExcluded",
        pass: localProfitability.comparable && moneyEqual(localProfitability.business.tipsExcluded, currentProfitability?.business?.tipsExcluded),
        left: numeric(localProfitability.business.tipsExcluded).toFixed(2),
        right: numeric(currentProfitability?.business?.tipsExcluded).toFixed(2),
      },
      {
        label: "business.operatingExpenses",
        pass: moneyEqual(localProfitability.business.operatingExpenses, currentProfitability?.business?.operatingExpenses),
        left: numeric(localProfitability.business.operatingExpenses).toFixed(2),
        right: numeric(currentProfitability?.business?.operatingExpenses).toFixed(2),
      },
      {
        label: "business.operatingProfit",
        pass: localProfitability.comparable && moneyEqual(localProfitability.business.operatingProfit, currentProfitability?.business?.operatingProfit),
        left: numeric(localProfitability.business.operatingProfit).toFixed(2),
        right: numeric(currentProfitability?.business?.operatingProfit).toFixed(2),
      },
      {
        label: "service rows parity",
        pass: localProfitability.comparable && serviceParity.pass,
        left: `${serviceParity.localCount}`,
        right: `${serviceParity.remoteCount}`,
      },
      {
        label: "worker rows parity",
        pass: localProfitability.comparable && workerParity.pass,
        left: `${workerParity.localCount}`,
        right: `${workerParity.remoteCount}`,
      },
      {
        label: "worker contribution reconciliation",
        pass: moneyEqual(remoteWorkerContribution, currentProfitability?.business?.contribution),
        left: numeric(remoteWorkerContribution).toFixed(2),
        right: numeric(currentProfitability?.business?.contribution).toFixed(2),
      },
      {
        label: "service + product contribution reconciliation",
        pass: moneyEqual(remoteServiceContribution + remoteProductContribution, currentProfitability?.business?.contribution),
        left: numeric(remoteServiceContribution + remoteProductContribution).toFixed(2),
        right: numeric(currentProfitability?.business?.contribution).toFixed(2),
      },
      {
        label: "drivers top service",
        pass: String(profitabilityData?.drivers?.topService?.service || "") === String(currentProfitability?.services?.[0]?.service || ""),
        left: String(currentProfitability?.services?.[0]?.service || ""),
        right: String(profitabilityData?.drivers?.topService?.service || ""),
      },
      {
        label: "drivers top worker",
        pass: String(profitabilityData?.drivers?.topWorker?.worker || "") === String(currentProfitability?.workers?.[0]?.worker || ""),
        left: String(currentProfitability?.workers?.[0]?.worker || ""),
        right: String(profitabilityData?.drivers?.topWorker?.worker || ""),
      }
    );
  }

  const profitabilityFailures = profitabilityChecks.filter((row) => !row.pass);
  const profitabilityResult = profitabilityProbe.status === "ready"
    ? profitabilityFailures.length
      ? `FAIL · ${profitabilityFailures.length}`
      : `PASS · ${profitabilityChecks.length}/${profitabilityChecks.length}`
    : profitabilityProbe.status === "error"
      ? "ERROR"
      : profitabilityProbe.status === "running"
        ? "RUNNING"
        : "READY TO RUN";

  const profitabilityReport = [
    "LOOP A4.4.2 PROFITABILITY REAL-DATA PARITY",
    `Period: ${periodLabel || "n/a"}`,
    `Worker: ${String(workerId || "all")}`,
    `Probe status: ${profitabilityProbe.status}`,
    `Query mode: ${profitabilityData?.queryMode || ""}`,
    `Version: ${profitabilityData?.profitabilityVersion || ""}`,
    `Semantics: ${profitabilityData?.profitabilitySemanticsVersion || ""}`,
    `Scanned bookings: ${profitabilityData?.diagnostics?.scanned?.bookings ?? ""}`,
    `Bounded from: ${profitabilityData?.diagnostics?.boundedQuery?.from || ""}`,
    `Bounded toExclusive: ${profitabilityData?.diagnostics?.boundedQuery?.toExclusive || ""}`,
    `Current rows used locally: ${Array.isArray(resolvedCurrent?.financeRows) ? resolvedCurrent.financeRows.length : 0}`,
    `Local semantic comparable: ${Boolean(localProfitability.comparable)}`,
    "",
    ...profitabilityChecks.map((row) =>
      `${row.pass ? "PASS" : "FAIL"} | ${row.label} | local=${row.left} | remote=${row.right}`
    ),
    "",
    `Result: ${profitabilityProbe.status === "ready" && profitabilityFailures.length === 0 ? "PASS" : profitabilityProbe.status === "ready" ? "FAIL" : profitabilityProbe.status.toUpperCase()}`,
  ].join("\n");

  const copyProfitability = async () => {
    try {
      await navigator.clipboard.writeText(profitabilityReport);
    } catch (_) {
      window.prompt("Copia el reporte A4.4.2:", profitabilityReport);
    }
  };

  const agingData = agingProbe.status === "ready" ? agingProbe.data : null;
  const agingSummary = agingData?.summary || {};
  const agingBuckets = Array.isArray(agingData?.buckets) ? agingData.buckets : [];
  const agingDetailRows = Array.isArray(agingData?.detail?.rows) ? agingData.detail.rows : [];
  const agingPriorityRows = Array.isArray(agingData?.priorityBookings) ? agingData.priorityBookings : [];
  const allowedAgingBuckets = new Set(["recent_0_7", "watch_8_30", "overdue_31_59", "critical_60_plus"]);

  const agingBucketAmount = agingBuckets.reduce((sum, row) => sum + numeric(row?.amount), 0);
  const agingBucketBookings = agingBuckets.reduce((sum, row) => sum + numeric(row?.bookings), 0);
  const agingOverdueBuckets = agingBuckets
    .filter((row) => ["overdue_31_59", "critical_60_plus"].includes(String(row?.key || "")))
    .reduce((sum, row) => sum + numeric(row?.amount), 0);
  const agingCriticalBucket = agingBuckets
    .filter((row) => String(row?.key || "") === "critical_60_plus")
    .reduce((sum, row) => sum + numeric(row?.amount), 0);

  const agingPrioritySorted = agingPriorityRows.every((row, index) => {
    if (index === 0) return true;
    const prev = agingPriorityRows[index - 1] || {};
    const cur = row || {};
    if (numeric(prev.agingSeverity) !== numeric(cur.agingSeverity)) {
      return numeric(prev.agingSeverity) >= numeric(cur.agingSeverity);
    }
    if (!moneyEqual(prev.pending, cur.pending)) {
      return numeric(prev.pending) >= numeric(cur.pending);
    }
    return numeric(prev.ageDays) >= numeric(cur.ageDays);
  });

  const agingRangePending = numeric(remote?.current?.pendingRevenue);
  const agingRangeCount = numeric(remote?.current?.detailCounts?.receivables);

  const agingChecks = agingData ? [
    {
      label: "queryMode",
      pass: agingData?.queryMode === AGING_QUERY_MODE,
      left: AGING_QUERY_MODE,
      right: String(agingData?.queryMode || ""),
    },
    {
      label: "agingVersion",
      pass: agingData?.agingVersion === AGING_VERSION,
      left: AGING_VERSION,
      right: String(agingData?.agingVersion || ""),
    },
    {
      label: "semanticsVersion",
      pass: agingData?.agingSemanticsVersion === AGING_SEMANTICS_VERSION,
      left: AGING_SEMANTICS_VERSION,
      right: String(agingData?.agingSemanticsVersion || ""),
    },
    {
      label: "traceable receivables only",
      pass: agingData?.policy?.receivableDefinition === "traceable_pending_or_partial_only",
      left: "traceable_pending_or_partial_only",
      right: String(agingData?.policy?.receivableDefinition || ""),
    },
    {
      label: "service-date aging",
      pass: agingData?.policy?.dueDate === "service_date" &&
        agingData?.policy?.ageAnchor === "scheduled_service_date",
      left: "service_date/scheduled_service_date",
      right: `${agingData?.policy?.dueDate || ""}/${agingData?.policy?.ageAnchor || ""}`,
    },
    {
      label: "history bounded",
      pass: numeric(agingData?.diagnostics?.boundedQuery?.historyDays) === AGING_HISTORY_DAYS,
      left: String(AGING_HISTORY_DAYS),
      right: String(numeric(agingData?.diagnostics?.boundedQuery?.historyDays)),
    },
    {
      label: "current-period outstanding parity",
      pass: moneyEqual(agingSummary?.currentPeriodOutstanding, agingRangePending),
      left: agingRangePending.toFixed(2),
      right: numeric(agingSummary?.currentPeriodOutstanding).toFixed(2),
    },
    {
      label: "current-period receivable count parity",
      pass: numeric(agingSummary?.currentPeriodBookings) === agingRangeCount,
      left: String(agingRangeCount),
      right: String(numeric(agingSummary?.currentPeriodBookings)),
    },
    {
      label: "bucket amount reconciliation",
      pass: moneyEqual(agingBucketAmount, agingSummary?.totalOutstanding),
      left: agingBucketAmount.toFixed(2),
      right: numeric(agingSummary?.totalOutstanding).toFixed(2),
    },
    {
      label: "bucket booking reconciliation",
      pass: agingBucketBookings === numeric(agingSummary?.bookings),
      left: String(agingBucketBookings),
      right: String(numeric(agingSummary?.bookings)),
    },
    {
      label: "overdue amount reconciliation",
      pass: moneyEqual(agingOverdueBuckets, agingSummary?.overdueAmount),
      left: agingOverdueBuckets.toFixed(2),
      right: numeric(agingSummary?.overdueAmount).toFixed(2),
    },
    {
      label: "critical amount reconciliation",
      pass: moneyEqual(agingCriticalBucket, agingSummary?.criticalAmount),
      left: agingCriticalBucket.toFixed(2),
      right: numeric(agingSummary?.criticalAmount).toFixed(2),
    },
    {
      label: "current + carryover = total",
      pass: moneyEqual(
        numeric(agingSummary?.currentPeriodOutstanding) + numeric(agingSummary?.carryoverAmount),
        agingSummary?.totalOutstanding
      ),
      left: (
        numeric(agingSummary?.currentPeriodOutstanding) + numeric(agingSummary?.carryoverAmount)
      ).toFixed(2),
      right: numeric(agingSummary?.totalOutstanding).toFixed(2),
    },
    {
      label: "detail total = summary bookings",
      pass: numeric(agingData?.detail?.total) === numeric(agingSummary?.bookings),
      left: String(numeric(agingSummary?.bookings)),
      right: String(numeric(agingData?.detail?.total)),
    },
    {
      label: "detail page contract",
      pass: agingDetailRows.length === Math.min(
        numeric(agingData?.detail?.pageSize),
        numeric(agingData?.detail?.total)
      ),
      left: String(Math.min(numeric(agingData?.detail?.pageSize), numeric(agingData?.detail?.total))),
      right: String(agingDetailRows.length),
    },
    {
      label: "detail rows valid A/R",
      pass: agingDetailRows.every((row) =>
        numeric(row?.pending) > 0 &&
        allowedAgingBuckets.has(String(row?.agingBucket || "")) &&
        numeric(row?.ageDays) >= 0
      ),
      left: "pending>0 + valid bucket + age>=0",
      right: `${agingDetailRows.length}/${agingDetailRows.length}`,
    },
    {
      label: "priority deterministic order",
      pass: agingPrioritySorted,
      left: "severity -> amount -> age",
      right: agingPrioritySorted ? "ordered" : "out-of-order",
    },
  ] : [];

  const agingFailures = agingChecks.filter((row) => !row.pass);
  const agingResult = agingProbe.status === "ready"
    ? agingFailures.length
      ? `FAIL · ${agingFailures.length}`
      : `PASS · ${agingChecks.length}/${agingChecks.length}`
    : agingProbe.status === "error"
      ? "ERROR"
      : agingProbe.status === "running"
        ? "RUNNING"
        : "READY TO RUN";

  const agingReport = [
    "LOOP A4.5.2 A/R AGING REAL-DATA CERTIFICATION",
    `Period: ${periodLabel || "n/a"}`,
    `Worker: ${String(workerId || "all")}`,
    `Probe status: ${agingProbe.status}`,
    `Query mode: ${agingData?.queryMode || ""}`,
    `Version: ${agingData?.agingVersion || ""}`,
    `Semantics: ${agingData?.agingSemanticsVersion || ""}`,
    `As of: ${agingData?.asOf || ""}`,
    `Scanned bookings: ${agingData?.diagnostics?.scanned?.bookings ?? ""}`,
    `Bounded from: ${agingData?.diagnostics?.boundedQuery?.from || ""}`,
    `Bounded toExclusive: ${agingData?.diagnostics?.boundedQuery?.toExclusive || ""}`,
    `History days: ${agingData?.diagnostics?.boundedQuery?.historyDays ?? ""}`,
    `Total outstanding: ${numeric(agingSummary?.totalOutstanding).toFixed(2)}`,
    `Receivable bookings: ${numeric(agingSummary?.bookings)}`,
    `Clients: ${numeric(agingSummary?.clients)}`,
    `Current-period outstanding: ${numeric(agingSummary?.currentPeriodOutstanding).toFixed(2)}`,
    `Carryover outstanding: ${numeric(agingSummary?.carryoverAmount).toFixed(2)}`,
    `Overdue 31+: ${numeric(agingSummary?.overdueAmount).toFixed(2)}`,
    `Critical 60+: ${numeric(agingSummary?.criticalAmount).toFixed(2)}`,
    `Oldest days: ${numeric(agingSummary?.oldestDays)}`,
    "",
    ...agingBuckets.map((row) =>
      `BUCKET | ${row?.label || row?.key || ""} | amount=${numeric(row?.amount).toFixed(2)} | bookings=${numeric(row?.bookings)} | clients=${numeric(row?.clients)}`
    ),
    "",
    ...agingChecks.map((row) =>
      `${row.pass ? "PASS" : "FAIL"} | ${row.label} | local=${row.left} | remote=${row.right}`
    ),
    "",
    `Result: ${agingProbe.status === "ready" && agingFailures.length === 0 ? "PASS" : agingProbe.status === "ready" ? "FAIL" : agingProbe.status.toUpperCase()}`,
  ].join("\n");

  const copyAging = async () => {
    try {
      await navigator.clipboard.writeText(agingReport);
    } catch (_) {
      window.prompt("Copia el reporte A4.5.2:", agingReport);
    }
  };

  const forecastData = forecastProbe.status === "ready" ? forecastProbe.data : null;
  const forecastPeriod = forecastData?.period || {};
  const forecastTarget = forecastData?.target || {};
  const forecastPerformance = forecastData?.performance || {};
  const forecastPrevious = forecastData?.previous || null;
  const forecastDiagnostics = forecastData?.diagnostics || {};

  const forecastTargetAmount = numeric(forecastTarget?.amount);
  const forecastActual = numeric(forecastPerformance?.actualToDate);
  const forecastFuture = numeric(forecastPerformance?.futureBookedRevenue);
  const forecastBookedFloor = numeric(forecastPerformance?.bookedFloor);
  const forecastPeriodBooked = numeric(forecastPerformance?.periodBookedRevenue);
  const forecastAverage = numeric(forecastPerformance?.averagePerElapsedDay);
  const forecastRequired = numeric(forecastPerformance?.requiredPerRemainingDay);
  const forecastRunRate = numeric(forecastPerformance?.runRateForecast);
  const forecastClose = numeric(forecastPerformance?.forecastClose);
  const forecastElapsed = numeric(forecastPeriod?.elapsedDays);
  const forecastRemaining = numeric(forecastPeriod?.remainingDays);
  const forecastTotalDays = numeric(forecastPeriod?.totalDays);

  const expectedForecastClose = forecastPeriod?.phase === "complete"
    ? forecastActual
    : Math.max(forecastBookedFloor, forecastRunRate);
  const expectedTargetProgress = forecastTargetAmount > 0
    ? (forecastActual / forecastTargetAmount) * 100
    : null;
  const expectedBookedCoverage = forecastTargetAmount > 0
    ? (forecastBookedFloor / forecastTargetAmount) * 100
    : null;
  const expectedForecastAttainment = forecastTargetAmount > 0
    ? (forecastClose / forecastTargetAmount) * 100
    : null;
  const expectedRemainingActual = forecastTargetAmount > 0
    ? Math.max(0, forecastTargetAmount - forecastActual)
    : 0;
  const expectedRemainingBooked = forecastTargetAmount > 0
    ? Math.max(0, forecastTargetAmount - forecastBookedFloor)
    : 0;
  const expectedRequired = forecastTargetAmount > 0 && forecastRemaining > 0
    ? expectedRemainingBooked / forecastRemaining
    : 0;

  let expectedForecastStatus = "no_target";
  if (forecastTargetAmount > 0) {
    if (forecastPeriod?.phase === "complete") {
      expectedForecastStatus = forecastClose + 0.005 >= forecastTargetAmount ? "hit" : "missed";
    } else if (forecastBookedFloor + 0.005 >= forecastTargetAmount) {
      expectedForecastStatus = "booked_to_target";
    } else if (forecastClose + 0.005 >= forecastTargetAmount) {
      expectedForecastStatus = "on_track";
    } else {
      expectedForecastStatus = "at_risk";
    }
  }

  const forecastChecks = forecastData ? [
    {
      label: "queryMode",
      pass: forecastData?.queryMode === FORECAST_QUERY_MODE,
      left: FORECAST_QUERY_MODE,
      right: String(forecastData?.queryMode || ""),
    },
    {
      label: "forecastVersion",
      pass: forecastData?.forecastVersion === FORECAST_VERSION,
      left: FORECAST_VERSION,
      right: String(forecastData?.forecastVersion || ""),
    },
    {
      label: "semanticsVersion",
      pass: forecastData?.forecastSemanticsVersion === FORECAST_SEMANTICS_VERSION,
      left: FORECAST_SEMANTICS_VERSION,
      right: String(forecastData?.forecastSemanticsVersion || ""),
    },
    {
      label: "certification target",
      pass: moneyEqual(forecastTargetAmount, FORECAST_CERT_TARGET),
      left: FORECAST_CERT_TARGET.toFixed(2),
      right: forecastTargetAmount.toFixed(2),
    },
    {
      label: "policy recognized revenue target",
      pass: forecastData?.policy?.targetMetric === "recognized_revenue",
      left: "recognized_revenue",
      right: String(forecastData?.policy?.targetMetric || ""),
    },
    {
      label: "target persistence separate",
      pass: forecastData?.policy?.targetPersistence === "not_part_of_forecast_callable",
      left: "not_part_of_forecast_callable",
      right: String(forecastData?.policy?.targetPersistence || ""),
    },
    {
      label: "full period boundary",
      pass: String(forecastPeriod?.toExclusive || "") === String(remote?.forecastPeriodEnd || ""),
      left: String(remote?.forecastPeriodEnd || ""),
      right: String(forecastPeriod?.toExclusive || ""),
    },
    {
      label: "in-progress phase",
      pass: forecastPeriod?.phase === "in_progress" && forecastRemaining > 0,
      left: "in_progress + remaining>0",
      right: `${forecastPeriod?.phase || ""} + ${forecastRemaining}`,
    },
    {
      label: "forecast scan extends Range when needed",
      pass: numeric(forecastDiagnostics?.scanned?.bookings) >= numeric(remote?.rangeDiagnostics?.scanned?.bookings),
      left: `>=${numeric(remote?.rangeDiagnostics?.scanned?.bookings)}`,
      right: String(numeric(forecastDiagnostics?.scanned?.bookings)),
    },
    {
      label: "bounded from parity",
      pass: String(forecastDiagnostics?.boundedQuery?.from || "") === String(remote?.rangeDiagnostics?.boundedQuery?.from || ""),
      left: String(remote?.rangeDiagnostics?.boundedQuery?.from || ""),
      right: String(forecastDiagnostics?.boundedQuery?.from || ""),
    },
    {
      label: "bounded to full forecast period",
      pass: String(forecastDiagnostics?.boundedQuery?.toExclusive || "") === String(remote?.forecastPeriodEnd || ""),
      left: String(remote?.forecastPeriodEnd || ""),
      right: String(forecastDiagnostics?.boundedQuery?.toExclusive || ""),
    },
    {
      label: "actual-to-date parity",
      pass: moneyEqual(forecastActual, remote?.current?.revenue),
      left: numeric(remote?.current?.revenue).toFixed(2),
      right: forecastActual.toFixed(2),
    },
    {
      label: "period booked includes actual",
      pass: forecastPeriodBooked + 0.005 >= forecastActual,
      left: `>=${forecastActual.toFixed(2)}`,
      right: forecastPeriodBooked.toFixed(2),
    },
    {
      label: "previous revenue parity",
      pass: remote?.previous
        ? moneyEqual(forecastPrevious?.revenue, remote?.previous?.revenue)
        : forecastPrevious === null,
      left: remote?.previous ? numeric(remote?.previous?.revenue).toFixed(2) : "null",
      right: forecastPrevious ? numeric(forecastPrevious?.revenue).toFixed(2) : "null",
    },
    {
      label: "timeline reconciliation",
      pass: forecastElapsed + forecastRemaining === forecastTotalDays,
      left: String(forecastTotalDays),
      right: String(forecastElapsed + forecastRemaining),
    },
    {
      label: "booked floor arithmetic",
      pass: moneyEqual(forecastBookedFloor, forecastActual + forecastFuture),
      left: (forecastActual + forecastFuture).toFixed(2),
      right: forecastBookedFloor.toFixed(2),
    },
    {
      label: "forecast close formula",
      pass: moneyEqual(forecastClose, expectedForecastClose),
      left: expectedForecastClose.toFixed(2),
      right: forecastClose.toFixed(2),
    },
    {
      label: "target progress formula",
      pass: expectedTargetProgress === null
        ? forecastTarget?.progressPct === null
        : Math.abs(numeric(forecastTarget?.progressPct) - expectedTargetProgress) <= 0.011,
      left: expectedTargetProgress === null ? "null" : expectedTargetProgress.toFixed(2),
      right: forecastTarget?.progressPct == null ? "null" : numeric(forecastTarget?.progressPct).toFixed(2),
    },
    {
      label: "booked coverage formula",
      pass: expectedBookedCoverage === null
        ? forecastTarget?.bookedCoveragePct === null
        : Math.abs(numeric(forecastTarget?.bookedCoveragePct) - expectedBookedCoverage) <= 0.011,
      left: expectedBookedCoverage === null ? "null" : expectedBookedCoverage.toFixed(2),
      right: forecastTarget?.bookedCoveragePct == null ? "null" : numeric(forecastTarget?.bookedCoveragePct).toFixed(2),
    },
    {
      label: "forecast attainment formula",
      pass: expectedForecastAttainment === null
        ? forecastTarget?.forecastAttainmentPct === null
        : Math.abs(numeric(forecastTarget?.forecastAttainmentPct) - expectedForecastAttainment) <= 0.011,
      left: expectedForecastAttainment === null ? "null" : expectedForecastAttainment.toFixed(2),
      right: forecastTarget?.forecastAttainmentPct == null ? "null" : numeric(forecastTarget?.forecastAttainmentPct).toFixed(2),
    },
    {
      label: "remaining from actual",
      pass: moneyEqual(forecastTarget?.remainingFromActual, expectedRemainingActual),
      left: expectedRemainingActual.toFixed(2),
      right: numeric(forecastTarget?.remainingFromActual).toFixed(2),
    },
    {
      label: "remaining after booked",
      pass: moneyEqual(forecastTarget?.remainingAfterBooked, expectedRemainingBooked),
      left: expectedRemainingBooked.toFixed(2),
      right: numeric(forecastTarget?.remainingAfterBooked).toFixed(2),
    },
    {
      label: "required pace formula",
      pass: moneyEqual(forecastRequired, expectedRequired),
      left: expectedRequired.toFixed(2),
      right: forecastRequired.toFixed(2),
    },
    {
      label: "status formula",
      pass: String(forecastPerformance?.status || "") === expectedForecastStatus,
      left: expectedForecastStatus,
      right: String(forecastPerformance?.status || ""),
    },
  ] : [];

  const forecastFailures = forecastChecks.filter((row) => !row.pass);
  const forecastResult = forecastProbe.status === "ready"
    ? forecastFailures.length
      ? `FAIL · ${forecastFailures.length}`
      : `PASS · ${forecastChecks.length}/${forecastChecks.length}`
    : forecastProbe.status === "error"
      ? "ERROR"
      : forecastProbe.status === "running"
        ? "RUNNING"
        : "READY TO RUN";

  const forecastReport = [
    "LOOP A4.6.2 TARGETS + FORECAST REAL-DATA CERTIFICATION",
    `Period: ${periodLabel || "n/a"}`,
    `Worker: ${String(workerId || "all")}`,
    `Probe status: ${forecastProbe.status}`,
    `Query mode: ${forecastData?.queryMode || ""}`,
    `Version: ${forecastData?.forecastVersion || ""}`,
    `Semantics: ${forecastData?.forecastSemanticsVersion || ""}`,
    `Certification target: ${FORECAST_CERT_TARGET.toFixed(2)}`,
    `Phase: ${forecastPeriod?.phase || ""}`,
    `As of: ${forecastPeriod?.asOf || ""}`,
    `Forecast toExclusive: ${forecastPeriod?.toExclusive || ""}`,
    `Total days: ${forecastTotalDays}`,
    `Elapsed days: ${forecastElapsed}`,
    `Remaining days: ${forecastRemaining}`,
    `Actual to date: ${forecastActual.toFixed(2)}`,
    `Future booked: ${forecastFuture.toFixed(2)}`,
    `Booked floor: ${forecastBookedFloor.toFixed(2)}`,
    `Period booked revenue: ${forecastPeriodBooked.toFixed(2)}`,
    `Average/day: ${forecastAverage.toFixed(2)}`,
    `Required/day: ${forecastRequired.toFixed(2)}`,
    `Run-rate forecast: ${forecastRunRate.toFixed(2)}`,
    `Forecast close: ${forecastClose.toFixed(2)}`,
    `Status: ${forecastPerformance?.status || ""}`,
    `Scanned bookings: ${forecastDiagnostics?.scanned?.bookings ?? ""}`,
    `Bounded from: ${forecastDiagnostics?.boundedQuery?.from || ""}`,
    `Bounded toExclusive: ${forecastDiagnostics?.boundedQuery?.toExclusive || ""}`,
    "",
    ...forecastChecks.map((row) =>
      `${row.pass ? "PASS" : "FAIL"} | ${row.label} | local=${row.left} | remote=${row.right}`
    ),
    "",
    `Result: ${forecastProbe.status === "ready" && forecastFailures.length === 0 ? "PASS" : forecastProbe.status === "ready" ? "FAIL" : forecastProbe.status.toUpperCase()}`,
  ].join("\n");

  const copyForecast = async () => {
    try {
      await navigator.clipboard.writeText(forecastReport);
    } catch (_) {
      window.prompt("Copia el reporte A4.6.2:", forecastReport);
    }
  };

  const closeoutData = closeoutProbe.status === "ready" ? closeoutProbe.data : null;
  const closeoutSummary = closeoutData?.summary || {};
  const closeoutMethods = Array.isArray(closeoutData?.methods) ? closeoutData.methods : [];
  const closeoutDays = Array.isArray(closeoutData?.days) ? closeoutData.days : [];
  const closeoutChecklist = Array.isArray(closeoutData?.checklist) ? closeoutData.checklist : [];
  const closeoutDiagnostics = closeoutData?.diagnostics || {};

  const closeoutSystemMethodSum = closeoutMethods.reduce(
    (sum, row) => sum + numeric(row?.systemAmount),
    0
  );
  const closeoutDaysRevenue = closeoutDays.reduce(
    (sum, row) => sum + numeric(row?.summary?.recognizedRevenue),
    0
  );
  const closeoutDaysInflow = closeoutDays.reduce(
    (sum, row) => sum + numeric(row?.summary?.systemRecordedInflow),
    0
  );
  const closeoutDaysPaidOpex = closeoutDays.reduce(
    (sum, row) => sum + numeric(row?.summary?.paidOperatingExpenses),
    0
  );
  const closeoutDaysPending = closeoutDays.reduce(
    (sum, row) => sum + numeric(row?.summary?.pendingReceivables),
    0
  );

  const closeoutRequiredChecklistKeys = new Set([
    "payment_trace",
    "payment_mismatch",
    "receivables",
    "manual_actual",
    "paid_opex",
  ]);
  const closeoutChecklistComplete = closeoutChecklist.length === closeoutRequiredChecklistKeys.size &&
    closeoutChecklist.every((row) => closeoutRequiredChecklistKeys.has(String(row?.key || "")));

  const closeoutDaysContiguous = closeoutDays.every((row, index) => {
    if (index === 0) {
      return String(row?.from || "") === String(closeoutData?.period?.from || "");
    }
    const previous = closeoutDays[index - 1] || {};
    return String(previous?.toExclusive || "") === String(row?.from || "");
  }) && (
    !closeoutDays.length ||
    String(closeoutDays[closeoutDays.length - 1]?.toExclusive || "") ===
      String(closeoutData?.period?.toExclusive || "")
  );

  const closeoutHasActuals = closeoutMethods.some((row) => row?.actualAmount !== null) ||
    closeoutSummary?.declaredActualInflow !== null ||
    closeoutSummary?.declaredVariance !== null;

  let expectedCloseoutStatus = "ready_to_close";
  const closeoutHasActivity = (
    numeric(closeoutSummary?.expectedSalesDue) > 0 ||
    numeric(closeoutSummary?.systemRecordedInflow) > 0 ||
    numeric(closeoutSummary?.paidOperatingExpenses) > 0 ||
    numeric(closeoutSummary?.coveredRevenue) > 0
  );
  if (!closeoutHasActivity) {
    expectedCloseoutStatus = "no_activity";
  } else if (
    numeric(closeoutSummary?.paymentMismatch) > 0.005 ||
    numeric(closeoutSummary?.untrackedExpected) > 0.005 ||
    numeric(closeoutSummary?.trackedReconciliationGap) > 0.005
  ) {
    expectedCloseoutStatus = "attention";
  } else if (numeric(closeoutSummary?.pendingReceivables) > 0.005) {
    expectedCloseoutStatus = "open_receivables";
  }

  const closeoutChecks = closeoutData ? [
    {
      label: "queryMode",
      pass: closeoutData?.queryMode === CLOSEOUT_QUERY_MODE,
      left: CLOSEOUT_QUERY_MODE,
      right: String(closeoutData?.queryMode || ""),
    },
    {
      label: "closeoutVersion",
      pass: closeoutData?.closeoutVersion === CLOSEOUT_VERSION,
      left: CLOSEOUT_VERSION,
      right: String(closeoutData?.closeoutVersion || ""),
    },
    {
      label: "semanticsVersion",
      pass: closeoutData?.closeoutSemanticsVersion === CLOSEOUT_SEMANTICS_VERSION,
      left: CLOSEOUT_SEMANTICS_VERSION,
      right: String(closeoutData?.closeoutSemanticsVersion || ""),
    },
    {
      label: "current-range from parity",
      pass: String(closeoutDiagnostics?.boundedQuery?.from || "") ===
        String(remote?.currentRangeFrom || ""),
      left: String(remote?.currentRangeFrom || ""),
      right: String(closeoutDiagnostics?.boundedQuery?.from || ""),
    },
    {
      label: "current-range to parity",
      pass: String(closeoutDiagnostics?.boundedQuery?.toExclusive || "") ===
        String(remote?.currentRangeTo || ""),
      left: String(remote?.currentRangeTo || ""),
      right: String(closeoutDiagnostics?.boundedQuery?.toExclusive || ""),
    },
    {
      label: "scan bounded within Range envelope",
      pass: numeric(closeoutDiagnostics?.scanned?.bookings) <=
        numeric(remote?.rangeDiagnostics?.scanned?.bookings),
      left: `<=${numeric(remote?.rangeDiagnostics?.scanned?.bookings)}`,
      right: String(numeric(closeoutDiagnostics?.scanned?.bookings)),
    },
    {
      label: "recognized revenue parity",
      pass: moneyEqual(closeoutSummary?.recognizedRevenue, remote?.current?.revenue),
      left: numeric(remote?.current?.revenue).toFixed(2),
      right: numeric(closeoutSummary?.recognizedRevenue).toFixed(2),
    },
    {
      label: "tracked sales base parity",
      pass: moneyEqual(closeoutSummary?.expectedSalesDue, remote?.current?.trackedRevenueBase),
      left: numeric(remote?.current?.trackedRevenueBase).toFixed(2),
      right: numeric(closeoutSummary?.expectedSalesDue).toFixed(2),
    },
    {
      label: "collected sales parity",
      pass: moneyEqual(closeoutSummary?.collectedSales, numeric(remote?.current?.collectedRevenue) + numeric(closeoutSummary?.sessionCashAdjustment)),
      left: numeric(remote?.current?.collectedRevenue).toFixed(2),
      right: numeric(closeoutSummary?.collectedSales).toFixed(2),
    },
    {
      label: "tips parity",
      pass: moneyEqual(closeoutSummary?.tipsReceived, remote?.current?.totalTips),
      left: numeric(remote?.current?.totalTips).toFixed(2),
      right: numeric(closeoutSummary?.tipsReceived).toFixed(2),
    },
    {
      label: "pending receivables parity",
      pass: moneyEqual(closeoutSummary?.pendingReceivables, remote?.current?.pendingRevenue),
      left: numeric(remote?.current?.pendingRevenue).toFixed(2),
      right: numeric(closeoutSummary?.pendingReceivables).toFixed(2),
    },
    {
      label: "payment mismatch parity",
      pass: moneyEqual(closeoutSummary?.paymentMismatch, remote?.current?.paymentMismatchRevenue),
      left: numeric(remote?.current?.paymentMismatchRevenue).toFixed(2),
      right: numeric(closeoutSummary?.paymentMismatch).toFixed(2),
    },
    {
      label: "untracked parity",
      pass: moneyEqual(closeoutSummary?.untrackedExpected, remote?.current?.untrackedExpectedRevenue),
      left: numeric(remote?.current?.untrackedExpectedRevenue).toFixed(2),
      right: numeric(closeoutSummary?.untrackedExpected).toFixed(2),
    },
    {
      label: "reconciliation gap parity",
      pass: moneyEqual(closeoutSummary?.trackedReconciliationGap, remote?.current?.trackedReconciliationGap),
      left: numeric(remote?.current?.trackedReconciliationGap).toFixed(2),
      right: numeric(closeoutSummary?.trackedReconciliationGap).toFixed(2),
    },
    {
      label: "paid OPEX parity",
      pass: moneyEqual(closeoutSummary?.paidOperatingExpenses, remote?.current?.paidOperatingExpenses),
      left: numeric(remote?.current?.paidOperatingExpenses).toFixed(2),
      right: numeric(closeoutSummary?.paidOperatingExpenses).toFixed(2),
    },
    {
      label: "system methods reconciliation",
      pass: moneyEqual(closeoutSystemMethodSum, closeoutSummary?.systemRecordedInflow),
      left: closeoutSystemMethodSum.toFixed(2),
      right: numeric(closeoutSummary?.systemRecordedInflow).toFixed(2),
    },
    {
      label: "manual actual absent in certification",
      pass: !closeoutHasActuals,
      left: "null",
      right: closeoutHasActuals ? "present" : "null",
    },
    {
      label: "daily row count",
      pass: closeoutDays.length === numeric(closeoutData?.period?.days),
      left: String(numeric(closeoutData?.period?.days)),
      right: String(closeoutDays.length),
    },
    {
      label: "daily windows contiguous",
      pass: closeoutDaysContiguous,
      left: "contiguous",
      right: closeoutDaysContiguous ? "contiguous" : "gap/overlap",
    },
    {
      label: "daily revenue reconciliation",
      pass: moneyEqual(closeoutDaysRevenue, closeoutSummary?.recognizedRevenue),
      left: closeoutDaysRevenue.toFixed(2),
      right: numeric(closeoutSummary?.recognizedRevenue).toFixed(2),
    },
    {
      label: "daily inflow reconciliation",
      pass: moneyEqual(closeoutDaysInflow, closeoutSummary?.systemRecordedInflow),
      left: closeoutDaysInflow.toFixed(2),
      right: numeric(closeoutSummary?.systemRecordedInflow).toFixed(2),
    },
    {
      label: "daily paid OPEX reconciliation",
      pass: moneyEqual(closeoutDaysPaidOpex, closeoutSummary?.paidOperatingExpenses),
      left: closeoutDaysPaidOpex.toFixed(2),
      right: numeric(closeoutSummary?.paidOperatingExpenses).toFixed(2),
    },
    {
      label: "daily pending reconciliation",
      pass: moneyEqual(closeoutDaysPending, closeoutSummary?.pendingReceivables),
      left: closeoutDaysPending.toFixed(2),
      right: numeric(closeoutSummary?.pendingReceivables).toFixed(2),
    },
    {
      label: "close checklist contract",
      pass: closeoutChecklistComplete,
      left: "5 required keys",
      right: closeoutChecklist.map((row) => row?.key || "").join(","),
    },
    {
      label: "status formula",
      pass: String(closeoutData?.status || "") === expectedCloseoutStatus,
      left: expectedCloseoutStatus,
      right: String(closeoutData?.status || ""),
    },
    {
      label: "no bank reconciliation claim",
      pass: closeoutData?.policy?.bankReconciliation ===
        "not_claimed_without_external_bank_or_processor_settlement_data",
      left: "not_claimed_without_external_bank_or_processor_settlement_data",
      right: String(closeoutData?.policy?.bankReconciliation || ""),
    },
  ] : [];

  const closeoutFailures = closeoutChecks.filter((row) => !row.pass);
  const closeoutResult = closeoutProbe.status === "ready"
    ? closeoutFailures.length
      ? `FAIL · ${closeoutFailures.length}`
      : `PASS · ${closeoutChecks.length}/${closeoutChecks.length}`
    : closeoutProbe.status === "error"
      ? "ERROR"
      : closeoutProbe.status === "running"
        ? "RUNNING"
        : "READY TO RUN";

  const closeoutReport = [
    "LOOP A4.7.2 CASH + DAILY CLOSEOUT REAL-DATA CERTIFICATION",
    `Period: ${periodLabel || "n/a"}`,
    `Worker: ${String(workerId || "all")}`,
    `Probe status: ${closeoutProbe.status}`,
    `Query mode: ${closeoutData?.queryMode || ""}`,
    `Version: ${closeoutData?.closeoutVersion || ""}`,
    `Semantics: ${closeoutData?.closeoutSemanticsVersion || ""}`,
    `From: ${closeoutData?.period?.from || ""}`,
    `ToExclusive: ${closeoutData?.period?.toExclusive || ""}`,
    `Daily rows: ${numeric(closeoutData?.period?.days)}`,
    `Status: ${closeoutData?.status || ""}`,
    `Recognized revenue: ${numeric(closeoutSummary?.recognizedRevenue).toFixed(2)}`,
    `Expected sales due: ${numeric(closeoutSummary?.expectedSalesDue).toFixed(2)}`,
    `Collected sales: ${numeric(closeoutSummary?.collectedSales).toFixed(2)}`,
    `Tips received: ${numeric(closeoutSummary?.tipsReceived).toFixed(2)}`,
    `System inflow: ${numeric(closeoutSummary?.systemRecordedInflow).toFixed(2)}`,
    `Declared actual: ${closeoutSummary?.declaredActualInflow == null ? "null" : numeric(closeoutSummary?.declaredActualInflow).toFixed(2)}`,
    `Variance: ${closeoutSummary?.declaredVariance == null ? "null" : numeric(closeoutSummary?.declaredVariance).toFixed(2)}`,
    `Pending receivables: ${numeric(closeoutSummary?.pendingReceivables).toFixed(2)}`,
    `Paid OPEX: ${numeric(closeoutSummary?.paidOperatingExpenses).toFixed(2)}`,
    `Net known cash: ${numeric(closeoutSummary?.netKnownCash).toFixed(2)}`,
    `Scanned bookings: ${closeoutDiagnostics?.scanned?.bookings ?? ""}`,
    `Range envelope scanned bookings: ${remote?.rangeDiagnostics?.scanned?.bookings ?? ""}`,
    `Current range from: ${remote?.currentRangeFrom || ""}`,
    `Current range toExclusive: ${remote?.currentRangeTo || ""}`,
    `Bounded from: ${closeoutDiagnostics?.boundedQuery?.from || ""}`,
    `Bounded toExclusive: ${closeoutDiagnostics?.boundedQuery?.toExclusive || ""}`,
    "",
    ...closeoutMethods.map((row) =>
      `METHOD | ${row?.label || row?.method || ""} | system=${numeric(row?.systemAmount).toFixed(2)} | actual=${row?.actualAmount == null ? "null" : numeric(row.actualAmount).toFixed(2)} | variance=${row?.variance == null ? "null" : numeric(row.variance).toFixed(2)}`
    ),
    "",
    ...closeoutDays.map((row) =>
      `DAY | ${row?.dayKey || ""} | status=${row?.status || ""} | revenue=${numeric(row?.summary?.recognizedRevenue).toFixed(2)} | inflow=${numeric(row?.summary?.systemRecordedInflow).toFixed(2)} | pending=${numeric(row?.summary?.pendingReceivables).toFixed(2)} | paidOpex=${numeric(row?.summary?.paidOperatingExpenses).toFixed(2)}`
    ),
    "",
    ...closeoutChecks.map((row) =>
      `${row.pass ? "PASS" : "FAIL"} | ${row.label} | local=${row.left} | remote=${row.right}`
    ),
    "",
    `Result: ${closeoutProbe.status === "ready" && closeoutFailures.length === 0 ? "PASS" : closeoutProbe.status === "ready" ? "FAIL" : closeoutProbe.status.toUpperCase()}`,
  ].join("\n");

  const copyCloseout = async () => {
    try {
      await navigator.clipboard.writeText(closeoutReport);
    } catch (_) {
      window.prompt("Copia el reporte A4.7.2:", closeoutReport);
    }
  };

  const report = [
    "LOOP A4.S15 FINANCE FAST-PATH CUTOVER",
    `Period: ${periodLabel || "n/a"}`,
    `Worker: ${String(workerId || "all")}`,
    `Range status: ${remote?.rangeStatus || "n/a"}`,
    `Range ready: ${Boolean(remote?.rangeReady)}`,
    `Range queryMode: ${remote?.rangeResult?.queryMode || ""}`,
    `Range scanned bookings: ${remote?.rangeDiagnostics?.scanned?.bookings ?? ""}`,
    `Range from: ${remote?.rangeDiagnostics?.boundedQuery?.from || ""}`,
    `Range toExclusive: ${remote?.rangeDiagnostics?.boundedQuery?.toExclusive || ""}`,
    `UI rows version: ${remote?.uiRowsVersion || ""}`,
    `UI rows count: ${Array.isArray(remote?.currentUiRows) ? remote.currentUiRows.length : 0}`,
    `Finance fast path: ${Boolean(fastPath)}`,
    `Local bookings loaded: ${numeric(loadedBookings)}`,
    `Detail view: ${detailDiagnostic?.view || detailView || ""}`,
    `Detail status: ${detailStatusLabel}`,
    `Detail ready: ${Boolean(liveDetailDiagnostic || detailDiagnostic)}`,
    `Detail memory: ${detailDiagnostic && !liveDetailDiagnostic ? "retained after drawer close" : "live/none"}`,
    "",
    ...checks.map((row) =>
      `${row.pass ? "PASS" : "FAIL"} | ${row.label} | local=${row.left} | remote=${row.right}`
    ),
    "",
    `Result: ${ready && failures.length === 0 ? "PASS" : ready ? "FAIL" : "PENDING/FALLBACK"}`,
  ].join("\n");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(report);
    } catch (_) {
      window.prompt("Copia el reporte:", report);
    }
  };

  return (
    <details
      data-loop-finance-cutover="a4-s15"
      className="ba-finance-dev-diagnostics"
      style={{
        margin: "8px 0 0",
        border: "1px dashed #cbd5e1",
        borderRadius: 12,
        background: "#f8fafc",
      }}
    >
      <summary
        style={{
          minHeight: 38,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          padding: "0 11px",
          cursor: "pointer",
          listStyle: "none",
          color: "#64748b",
          fontSize: 10,
          fontWeight: 800,
        }}
      >
        <span>Diagnóstico Finance · desarrollo</span>
        <strong style={{ color: !ready ? "#92400e" : failures.length ? "#b91c1c" : "#047857", fontSize: 10 }}>
          {resultLabel}
        </strong>
      </summary>
      <div style={{ padding: "10px 12px 12px", borderTop: "1px solid #e2e8f0" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div>
          <strong style={{ display: "block", fontSize: 13 }}>A4.S15 · Finance bounded fast path</strong>
          <span style={{ display: "block", marginTop: 3, fontSize: 12, color: "#64748b" }}>
            Range: {remote?.rangeStatus || "idle"} · UI rows: {remote?.currentUiRows?.length || 0} · bookings locales: {numeric(loadedBookings)} · fallback automático
          </span>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <strong style={{ color: !ready ? "#92400e" : failures.length ? "#b91c1c" : "#047857" }}>
            {resultLabel}
          </strong>
          <button type="button" onClick={copy} disabled={!checks.length} style={{ minHeight: 32, borderRadius: 9, padding: "0 10px" }}>
            Copiar reporte
          </button>
        </div>
      </div>
      {remote?.rangeError ? (
        <pre style={{ whiteSpace: "pre-wrap", margin: "10px 0 0", fontSize: 11, color: "#b91c1c" }}>{remote.rangeError}</pre>
      ) : null}
      {remote?.detailError ? (
        <pre style={{ whiteSpace: "pre-wrap", margin: "10px 0 0", fontSize: 11, color: "#b91c1c" }}>{remote.detailError}</pre>
      ) : null}

      {!fastPath ? (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <div>
            <strong style={{ display: "block", fontSize: 12 }}>A4.S14 · Bounded UI rows parity</strong>
            <span style={{ display: "block", marginTop: 3, fontSize: 11, color: "#64748b" }}>
              Disponible solo fuera del fast path; A4.S14 ya fue certificado antes de apagar el full booking load.
            </span>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #e2e8f0", fontSize: 11, color: "#64748b" }}>
          A4.S14 ya certificado · probe local-vs-full-load deshabilitado porque A4.S15 mantiene bookings locales en 0 por diseño.
        </div>
      )}

      {fastPath ? (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #e2e8f0" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <div>
              <strong style={{ display: "block", fontSize: 12 }}>A4.4.2 · Profitability real-data parity</strong>
              <span style={{ display: "block", marginTop: 3, fontSize: 11, color: "#64748b" }}>
                Una llamada manual · compara rentabilidad bounded contra las 22 UI rows ya certificadas.
              </span>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <strong style={{ color: profitabilityFailures.length ? "#b91c1c" : profitabilityProbe.status === "ready" ? "#047857" : profitabilityProbe.status === "error" ? "#b91c1c" : "#64748b" }}>
                {profitabilityResult}
              </strong>
              <button
                type="button"
                onClick={runProfitabilityParity}
                disabled={profitabilityProbe.attempted || profitabilityProbe.status === "running" || !remote?.rangeReady}
                style={{ minHeight: 32, borderRadius: 9, padding: "0 10px" }}
              >
                {profitabilityProbe.status === "running" ? "Ejecutando..." : "Ejecutar 1 profitability call"}
              </button>
              <button
                type="button"
                onClick={copyProfitability}
                disabled={profitabilityProbe.status !== "ready"}
                style={{ minHeight: 32, borderRadius: 9, padding: "0 10px" }}
              >
                Copiar A4.4.2
              </button>
            </div>
          </div>
          {profitabilityProbe.error ? (
            <pre style={{ whiteSpace: "pre-wrap", margin: "10px 0 0", fontSize: 11, color: "#b91c1c" }}>{profitabilityProbe.error}</pre>
          ) : null}
        </div>
      ) : null}

      {fastPath ? (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #e2e8f0" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <div>
              <strong style={{ display: "block", fontSize: 12 }}>A4.5.2 · A/R Aging real-data certification</strong>
              <span style={{ display: "block", marginTop: 3, fontSize: 11, color: "#64748b" }}>
                Una llamada manual bounded de 730 días. No escribe datos ni inventa snapshots históricos.
              </span>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <strong style={{ color: agingProbe.status === "ready" && !agingFailures.length ? "#047857" : agingProbe.status === "error" || agingFailures.length ? "#b91c1c" : "#92400e" }}>
                {agingResult}
              </strong>
              <button
                type="button"
                onClick={runAgingParity}
                disabled={agingProbe.attempted || agingProbe.status === "running" || !remote?.rangeReady}
                style={{ minHeight: 32, borderRadius: 9, padding: "0 10px" }}
              >
                {agingProbe.status === "running" ? "Ejecutando..." : agingProbe.attempted ? "Aging call ejecutado" : "Ejecutar 1 Aging call"}
              </button>
              <button
                type="button"
                onClick={copyAging}
                disabled={agingProbe.status !== "ready"}
                style={{ minHeight: 32, borderRadius: 9, padding: "0 10px" }}
              >
                Copiar A4.5.2
              </button>
            </div>
          </div>
          {agingProbe.status === "ready" ? (
            <div style={{ marginTop: 8, fontSize: 11, color: "#475569" }}>
              Total A/R {numeric(agingSummary?.totalOutstanding).toFixed(2)} · período {numeric(agingSummary?.currentPeriodOutstanding).toFixed(2)} · arrastre {numeric(agingSummary?.carryoverAmount).toFixed(2)} · 31+ {numeric(agingSummary?.overdueAmount).toFixed(2)} · 60+ {numeric(agingSummary?.criticalAmount).toFixed(2)}
            </div>
          ) : null}
          {agingProbe.status === "error" ? (
            <pre style={{ whiteSpace: "pre-wrap", margin: "10px 0 0", fontSize: 11, color: "#b91c1c" }}>{agingProbe.error}</pre>
          ) : null}
        </div>
      ) : null}

      {fastPath ? (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #e2e8f0" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <div>
              <strong style={{ display: "block", fontSize: 12 }}>A4.6.2 · Targets + Forecast real-data certification</strong>
              <span style={{ display: "block", marginTop: 3, fontSize: 11, color: "#64748b" }}>
                Una llamada manual con meta de certificación USD 1,000. No persiste metas ni escribe datos.
              </span>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <strong style={{ color: forecastProbe.status === "ready" && !forecastFailures.length ? "#047857" : forecastProbe.status === "error" || forecastFailures.length ? "#b91c1c" : "#92400e" }}>
                {forecastResult}
              </strong>
              <button
                type="button"
                onClick={runForecastParity}
                disabled={forecastProbe.attempted || forecastProbe.status === "running" || !remote?.rangeReady}
                style={{ minHeight: 32, borderRadius: 9, padding: "0 10px" }}
              >
                {forecastProbe.status === "running" ? "Ejecutando..." : forecastProbe.attempted ? "Forecast call ejecutado" : "Ejecutar 1 Forecast call"}
              </button>
              <button
                type="button"
                onClick={copyForecast}
                disabled={forecastProbe.status !== "ready"}
                style={{ minHeight: 32, borderRadius: 9, padding: "0 10px" }}
              >
                Copiar A4.6.2
              </button>
            </div>
          </div>
          {forecastProbe.status === "ready" ? (
            <div style={{ marginTop: 8, fontSize: 11, color: "#475569" }}>
              Actual {forecastActual.toFixed(2)} · futuro reservado {forecastFuture.toFixed(2)} · forecast {forecastClose.toFixed(2)} · estado {forecastPerformance?.status || ""}
            </div>
          ) : null}
          {forecastProbe.status === "error" ? (
            <pre style={{ whiteSpace: "pre-wrap", margin: "10px 0 0", fontSize: 11, color: "#b91c1c" }}>{forecastProbe.error}</pre>
          ) : null}
        </div>
      ) : null}

      {fastPath ? (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #e2e8f0" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <div>
              <strong style={{ display: "block", fontSize: 12 }}>A4.7.2 · Cash + Daily Closeout real-data certification</strong>
              <span style={{ display: "block", marginTop: 3, fontSize: 11, color: "#64748b" }}>
                Una llamada manual read-only. Certifica caja registrada, OPEX pagado, días y checklist sin persistir un cierre.
              </span>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <strong style={{ color: closeoutProbe.status === "ready" && !closeoutFailures.length ? "#047857" : closeoutProbe.status === "error" || closeoutFailures.length ? "#b91c1c" : "#92400e" }}>
                {closeoutResult}
              </strong>
              <button
                type="button"
                onClick={runCloseoutParity}
                disabled={closeoutProbe.attempted || closeoutProbe.status === "running" || !remote?.rangeReady}
                style={{ minHeight: 32, borderRadius: 9, padding: "0 10px" }}
              >
                {closeoutProbe.status === "running" ? "Ejecutando..." : closeoutProbe.attempted ? "Closeout call ejecutado" : "Ejecutar 1 Closeout call"}
              </button>
              <button
                type="button"
                onClick={copyCloseout}
                disabled={closeoutProbe.status !== "ready"}
                style={{ minHeight: 32, borderRadius: 9, padding: "0 10px" }}
              >
                Copiar A4.7.2
              </button>
            </div>
          </div>
          {closeoutProbe.status === "ready" ? (
            <div style={{ marginTop: 8, fontSize: 11, color: "#475569" }}>
              Inflow {numeric(closeoutSummary?.systemRecordedInflow).toFixed(2)} · por cobrar {numeric(closeoutSummary?.pendingReceivables).toFixed(2)} · OPEX pagado {numeric(closeoutSummary?.paidOperatingExpenses).toFixed(2)} · neto conocido {numeric(closeoutSummary?.netKnownCash).toFixed(2)} · {closeoutData?.status || ""}
            </div>
          ) : null}
          {closeoutProbe.status === "error" ? (
            <pre style={{ whiteSpace: "pre-wrap", margin: "10px 0 0", fontSize: 11, color: "#b91c1c" }}>{closeoutProbe.error}</pre>
          ) : null}
        </div>
      ) : null}
      </div>
    </details>
  );
}

